import React, { useState, useEffect } from 'react';
import { PageSpeedResult, ServiceData } from '../../types';
import { analyzePageSpeed, analyzeHtml, analyzeComplete, disconnectWebSocket, connectWebSocket, BACKEND_URL } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import LlmAnalysisSectionV2Enhanced from './dashboard/LlmAnalysisSectionV2Enhanced';
import HallOfFameModalAppV2 from './dashboard/HallOfFameModalAppV2';
import SavedAnalysesModalV2 from './dashboard/SavedAnalysesModalV2';
import MultiUrlComparisonTable from './dashboard/MultiUrlComparisonTable';
import { calculateOverallRating, calculateMobileRating, calculateDesktopRating, getRatingGrade, getRatingColor } from '../../utils/overallRating';
import { getImportantOpportunities } from '../../utils/dataTransformers';
import { translateOpportunityTitle, translateDescription, generateHtmlReport } from '../../utils/reportGenerators';
import { getSharedAnalysisById, saveAnalysis, SavedAnalysis } from '../../utils/analysisStorage';
import SiteUrlsManager from '../core/SiteUrlsManager';
import SitePagesManager from '../core/SitePagesManager';
import { getApiUrl } from '../../config/environment';

// style.md 기반 완전 새로운 디자인 시스템
const designSystem = {
  colors: {
    primary: '#2467E7',
    primaryDark: '#1B4DB8',
    primaryLight: '#E8F2FF',
    secondary: '#6C5CE7',
    accent: '#00D4FF',
    success: '#00B894',
    warning: '#FDCB6E',
    danger: '#E17055',
    bgPrimary: '#FAFBFC',
    bgSecondary: '#FFFFFF',
    textPrimary: '#1A202C',
    textSecondary: '#718096',
    textMuted: '#A0AEC0',
    border: '#E2E8F0'
  },
  shadows: {
    sm: '0 1px 3px rgba(0, 0, 0, 0.12)',
    md: '0 4px 6px rgba(0, 0, 0, 0.1)',
    lg: '0 10px 15px rgba(0, 0, 0, 0.15)',
    xl: '0 20px 25px rgba(0, 0, 0, 0.2)',
    '2xl': '0 25px 50px rgba(0, 0, 0, 0.25)'
  },
  typography: {
    h1: { fontSize: '3rem', fontWeight: '900', lineHeight: '1.1' },
    h2: { fontSize: '2.25rem', fontWeight: '700', lineHeight: '1.2' },
    h3: { fontSize: '1.875rem', fontWeight: '600', lineHeight: '1.3' },
    body: { fontSize: '1rem', fontWeight: '400', lineHeight: '1.6' },
    bodyLarge: { fontSize: '1.125rem', fontWeight: '400', lineHeight: '1.7' },
    caption: { fontSize: '0.875rem', fontWeight: '500', lineHeight: '1.4' }
  }
} as const;

interface AppV2Props {
  // 기존 App.tsx에서 전달받을 props들
}

interface AnalysisResult {
  url: string;
  batchId?: string;
  urlIndex?: number;
  failed?: boolean;
  error?: string;
  pageSpeedData?: PageSpeedResult;
  llmAnalysis?: string;
  timestamp?: string;
  services?: ServiceData[];
  importantInfo?: any;
  resourceSummary?: any;
  customRatings?: {
    mobile: number;
    desktop: number;
    overall: number;
  };
}

const AppV2: React.FC<AppV2Props> = () => {
  const { user, isAdmin, isLoading: authLoading, isCheckingAdmin, login, logout } = useAuth();

  // 보고서 섹션 참조 (자동 스크롤용)
  const reportSectionRef = React.useRef<HTMLElement>(null);

  // 다중 URL 입력 관리
  const [urls, setUrls] = useState<string[]>(['http://']);
  const [isAnalyzing, setIsAnalyzing] = useState(false);

  // 단일 분석 결과
  const [analysisResult, setAnalysisResult] = useState<{
    pageSpeedData: PageSpeedResult | null;
    llmAnalysis: string | null;
  }>({ pageSpeedData: null, llmAnalysis: null });
  const [displayUrl, setDisplayUrl] = useState<string>('');

  // 다중 분석 상태
  const [isBatchMode, setIsBatchMode] = useState(false);
  const [batchResults, setBatchResults] = useState<AnalysisResult[]>([]);
  const [selectedUrlIndex, setSelectedUrlIndex] = useState<number | undefined>(undefined);
  const [multiProgress, setMultiProgress] = useState({
    completed: 0,
    total: 0,
    currentUrl: undefined as string | undefined,
    currentIndex: undefined as number | undefined,
    urls: [] as string[],
    urlProgress: [] as Array<{
      url: string;
      status: 'pending' | 'waiting' | 'analyzing' | 'completed' | 'error';
      message?: string;
      error?: string;
    }>
  });

  // 커스텀 성능 점수 상태
  const [customScores, setCustomScores] = useState<{
    overall: number;
    mobile: number;
    desktop: number;
    grade: string;
  } | null>(null);

  // Hall of Fame 벤치마크 데이터 (탭 전환 시 일관된 점수 계산용)
  const [hallOfFameBenchmarks, setHallOfFameBenchmarks] = useState<any[]>([]);

  // 성능 점수 상태 (PerformanceScoresV2에서 계산된 값 - 명예의전당 모달용)
  const [performanceScores, setPerformanceScores] = useState<{ desktop: number; mobile: number }>({ desktop: 0, mobile: 0 });

  // testCase 상태 (기본값: 2, URL 파라미터로 오버라이드 가능: ?testCase=1, 3, 4, 5)
  const [testCase, setTestCase] = useState<1 | 2 | 3 | 4 | 5>(2);


  // Case 1 Accordion 상태
  const [isAccordionOpen, setIsAccordionOpen] = useState(false);

  // URL 파라미터에서 설정 읽기
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);

    // testCase 파라미터
    const caseParam = urlParams.get('testCase');
    if (caseParam === '1' || caseParam === '3' || caseParam === '4' || caseParam === '5') {
      setTestCase(parseInt(caseParam) as 1 | 3 | 4 | 5);
    }
  }, []);

  // 히어로 섹션 미리보기 자동 롤링 (4초 간격)
  useEffect(() => {
    const timer = setInterval(() => {
      setPreviewSlideIndex(prev => (prev + 1) % 3);
    }, 4000);
    return () => clearInterval(timer);
  }, []);

  // 앱 시작 시 Hall of Fame 벤치마크 데이터 미리 로드
  useEffect(() => {
    const loadBenchmarkData = async () => {
      try {
        const apiUrl = process.env.NODE_ENV === 'production'
          ? ''
          : (process.env.REACT_APP_API_URL || 'http://localhost:3300');
        const response = await fetch(`${apiUrl}/api/hall-of-fame?showAll=true&limit=200`);

        if (response.ok) {
          const result = await response.json();
          if (result.success && result.data) {
            console.log(`🏆 벤치마크 데이터 사전 로드: ${result.data.length}개 사이트`);
            setHallOfFameBenchmarks(result.data);
          }
        }
      } catch (error) {
        console.error('벤치마크 데이터 로드 실패:', error);
      }
    };
    loadBenchmarkData();
  }, []);

  // 명예의전당 모달 상태 관리
  const [showHallOfFame, setShowHallOfFame] = useState(false);
  const [modalOrigin, setModalOrigin] = useState({ x: 50, y: 50 });

  // 사이드바 상태 관리
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);

  // 히어로 섹션 미리보기 롤링 상태
  const [previewSlideIndex, setPreviewSlideIndex] = useState(0);
  const [isPerformanceMenuOpen, setIsPerformanceMenuOpen] = useState(true);

  // 개선 권장사항 탭 상태 관리
  const [opportunitiesTab, setOpportunitiesTab] = useState<'desktop' | 'mobile'>('desktop');

  // 리소스 상세 정보 펼침/접힘 상태 관리
  const [expandedItems, setExpandedItems] = useState<{ [key: string]: { [key: number]: boolean } }>({});

  // 개선 기회 카드 아코디언 상태 (어떤 기회가 펼쳐져 있는지)
  const [expandedOpportunities, setExpandedOpportunities] = useState<{ [key: string]: boolean }>({});

  // 관리자 모드 자동 활성화 (관리자 인증 시 항상 true)
  const adminMode = isAdmin; // 관리자는 자동으로 관리자 모드

  // 사이트 그룹 드롭다운 상태
  const [showSiteGroupDropdown, setShowSiteGroupDropdown] = useState(false);
  const [siteGroupSearch, setSiteGroupSearch] = useState('');
  const [selectedGroup, setSelectedGroup] = useState<any>(null);
  const [selectedUrls, setSelectedUrls] = useState<Array<{ url: string; label?: string }>>([]);

  // 실제 사이트 그룹 데이터 (DB에서 로드)
  const [siteGroups, setSiteGroups] = useState<any[]>([]);
  const [loadingSiteGroups, setLoadingSiteGroups] = useState(false);

  // 사이트 그룹 관리 모달 상태
  const [showGroupManageModal, setShowGroupManageModal] = useState(false);
  const [editingGroup, setEditingGroup] = useState<any>(null);
  const [groupFormData, setGroupFormData] = useState({ site_name: '', description: '' });

  // URL 관리 모달 상태
  const [showUrlManageModal, setShowUrlManageModal] = useState(false);
  const [managingGroupId, setManagingGroupId] = useState<number | null>(null);
  const [groupUrls, setGroupUrls] = useState<any[]>([]);
  const [editingUrl, setEditingUrl] = useState<any>(null);
  const [urlFormData, setUrlFormData] = useState({ url: '', label: '', display_order: 0 });

  // Site Pages 관리 모달 상태
  const [showSitePagesModal, setShowSitePagesModal] = useState(false);
  const [managingSiteName, setManagingSiteName] = useState<string>('');
  const [sitePagesData, setSitePagesData] = useState<any>(null);
  const [sitePagesForm, setSitePagesForm] = useState({
    site_name: '',
    main_page_url: '',
    category_page_url: '',
    product_detail_url: '',
    login_signup_url: '',
    cart_url: '',
    other_page_1_url: '',
    other_page_2_url: '',
    other_page_3_url: '',
    other_page_4_url: '',
    other_page_5_url: ''
  });

  // 공유 모드 플래그
  const [isSharedView, setIsSharedView] = useState(false);

  // 저장소에서 불러온 모드 플래그
  const [isLoadedFromStorage, setIsLoadedFromStorage] = useState(false);

  // 저장소에서 불러온 분석 ID (공유 링크 생성용)
  const [loadedAnalysisId, setLoadedAnalysisId] = useState<string | null>(null);

  // 저장된 분석 모달
  const [showSavedAnalysesModal, setShowSavedAnalysesModal] = useState(false);

  // 불러온 분석의 URL 라벨 (저장 당시 고정된 라벨)
  const [loadedUrlLabels, setLoadedUrlLabels] = useState<{[key: string]: string} | null>(null);

  // 사이드바 페이지 관리
  const [currentSidebarPage, setCurrentSidebarPage] = useState<string>('dashboard');

  // URL 파라미터에서 공유된 분석 ID 및 관리자 페이지 확인
  useEffect(() => {
    const loadSharedAnalysis = async () => {
      const urlParams = new URLSearchParams(window.location.search);
      const shareId = urlParams.get('share');
      const adminPage = urlParams.get('admin');

      // === 1단계: URL에서 admin 파라미터 감지 시 sessionStorage에 저장 ===
      if (adminPage) {
        const storedAdminPage = sessionStorage.getItem('requestedAdminPage');
        if (!storedAdminPage) {
          console.log('🔐 [1단계] 관리자 페이지 요청 감지 및 저장:', adminPage);
          sessionStorage.setItem('requestedAdminPage', adminPage);
        }
      }

      // === 2단계: 로그인 후 sessionStorage에서 admin 요청 확인 및 처리 ===
      const requestedAdminPage = sessionStorage.getItem('requestedAdminPage');

      if (requestedAdminPage) {
        console.log('🔐 [2단계] sessionStorage에서 관리자 페이지 요청 발견:', requestedAdminPage);

        // 인증 정보 로딩 중이면 대기
        if (authLoading) {
          console.log('⏳ [2-1] 인증 정보 로딩 중... 대기');
          return; // 로딩 완료 후 재실행됨
        }

        // 로그인하지 않은 상태면 대기 (IntroPage 표시)
        if (!user) {
          console.log('⏳ [2-2] 로그인 대기 중... (IntroPage 표시 예정)');
          return; // 로그인 완료 후 재실행됨
        }

        // 관리자 권한 확인 중이면 대기 (중요!)
        if (isCheckingAdmin) {
          console.log('⏳ [2-3] 관리자 권한 확인 중... 대기');
          return; // checkAdminStatus() 완료 후 재실행됨
        }

        // 로그인 완료 + 권한 확인 완료 → 권한에 따라 처리
        console.log('🔐 [2-4] 로그인 및 권한 확인 완료 → 최종 처리');

        if (isAdmin) {
          // 관리자 권한 있음 - 페이지 진입
          const validAdminPages = ['site-pages'];
          if (validAdminPages.includes(requestedAdminPage)) {
            console.log('✅ [2-5] 관리자 권한 확인 → 페이지 진입:', requestedAdminPage);
            setCurrentSidebarPage(requestedAdminPage);
            setIsSidebarOpen(false); // 사이드바 닫기

            // URL 파라미터 유지 (새로고침 시에도 페이지 유지)
            const url = new URL(window.location.href);
            if (!url.searchParams.has('admin')) {
              url.searchParams.set('admin', requestedAdminPage);
              window.history.replaceState({}, '', url.toString());
            }
          } else {
            console.warn('⚠️ [2-5] 유효하지 않은 관리자 페이지:', requestedAdminPage);
          }

          // sessionStorage 정리
          sessionStorage.removeItem('requestedAdminPage');
          console.log('🧹 [2-6] sessionStorage 정리 완료');
        } else {
          // 관리자 권한 없음 - 거부
          console.warn('❌ [2-5] 관리자 권한 없음 - 접근 거부');
          alert('관리자 권한이 필요합니다.');

          // URL 파라미터 제거 및 sessionStorage 정리
          const url = new URL(window.location.href);
          url.searchParams.delete('admin');
          window.history.replaceState({}, '', url.toString());
          sessionStorage.removeItem('requestedAdminPage');

          console.log('🧹 [2-6] URL 파라미터 및 sessionStorage 정리 완료');
        }

        return; // admin 처리 후 종료
      }

      if (shareId) {
        console.log('🔗 공유 분석 로드 시도:', shareId);
        setIsSharedView(true); // 공유 모드 활성화
        const sharedAnalysis = await getSharedAnalysisById(shareId);

        if (sharedAnalysis) {
          console.log('✅ 공유 분석 로드 성공:', sharedAnalysis);
          console.log('🔍 batchResults 존재 여부:', !!sharedAnalysis.batchResults);
          console.log('🔍 batchResults 타입:', typeof sharedAnalysis.batchResults);
          console.log('🔍 batchResults 값:', sharedAnalysis.batchResults);

          // 다중 분석 결과 확인 (새로운 구조: {results, urlLabels} 또는 구버전 배열)
          let batchResultsArray: any = null;
          let urlLabelsObj: any = {};

          if (sharedAnalysis.batchResults) {
            if (Array.isArray(sharedAnalysis.batchResults)) {
              // 구버전: 배열만 있는 경우
              batchResultsArray = sharedAnalysis.batchResults;
              urlLabelsObj = sharedAnalysis.urlLabels || {};
            } else if ((sharedAnalysis.batchResults as any).results) {
              // 신버전: {results: [...], urlLabels: {...}}
              batchResultsArray = (sharedAnalysis.batchResults as any).results;
              urlLabelsObj = (sharedAnalysis.batchResults as any).urlLabels || {};
            }
          }

          if (batchResultsArray && batchResultsArray.length > 0) {
            console.log(`📊 다중 분석 복원: ${batchResultsArray.length}개 사이트`);

            // urlLabels 정보 복원 (저장 당시 라벨을 state에 저장, sessionStorage 건드리지 않음!)
            setLoadedUrlLabels(urlLabelsObj);
            console.log('✅ URL 라벨 state 복원 완료:', urlLabelsObj);

            // 다중 분석 모드로 복원 (배열만 저장)
            setIsBatchMode(true);
            setBatchResults(batchResultsArray);
            setSelectedUrlIndex(undefined); // 종합 총평 탭이 기본 선택

            // 첫 번째 결과를 현재 표시용으로 설정 (페이지별 탭 전환 시 필요)
            const firstResult = batchResultsArray[0];
            setDisplayUrl(firstResult.url);
            setAnalysisResult({
              pageSpeedData: firstResult.pageSpeedData,
              llmAnalysis: firstResult.llmAnalysis
            });

            // 커스텀 성능 점수 계산 (첫 번째 결과)
            if (firstResult.customRatings) {
              console.log('✅ 다중 분석 - 백엔드 점수 사용:', firstResult.customRatings);
              setCustomScores({
                overall: firstResult.customRatings.overall,
                mobile: firstResult.customRatings.mobile,
                desktop: firstResult.customRatings.desktop,
                grade: getRatingGrade(firstResult.customRatings.overall)
              });
              setPerformanceScores({
                desktop: firstResult.customRatings.desktop,
                mobile: firstResult.customRatings.mobile
              });
            } else if (firstResult.pageSpeedData) {
              // customRatings 없으면 계산
              const currentSiteData = {
                fcp_mobile: firstResult.pageSpeedData.mobile?.firstContentfulPaint,
                lcp_mobile: firstResult.pageSpeedData.mobile?.largestContentfulPaint,
                tbt_mobile: firstResult.pageSpeedData.mobile?.totalBlockingTime,
                total_size_mobile: firstResult.pageSpeedData.mobile?.pageSize?.totalSize,
                fcp_desktop: firstResult.pageSpeedData.desktop?.firstContentfulPaint,
                lcp_desktop: firstResult.pageSpeedData.desktop?.largestContentfulPaint,
                tbt_desktop: firstResult.pageSpeedData.desktop?.totalBlockingTime,
                total_size_desktop: firstResult.pageSpeedData.desktop?.pageSize?.totalSize
              };

              // Hall of Fame 데이터로 점수 계산
              (async () => {
                try {
                  const apiUrl = process.env.NODE_ENV === 'production'
                    ? ''
                    : (process.env.REACT_APP_API_URL || 'http://localhost:3300');
                  const response = await fetch(`${apiUrl}/api/hall-of-fame?showAll=true&limit=200`);

                  if (response.ok) {
                    const result = await response.json();
                    if (result.success && result.data) {
                      setHallOfFameBenchmarks(result.data);

                      const mobileScore = calculateMobileRating(currentSiteData, result.data);
                      const desktopScore = calculateDesktopRating(currentSiteData, result.data);
                      const overallScore = Math.round((mobileScore + desktopScore) / 2);

                      const scores = {
                        overall: overallScore,
                        mobile: mobileScore,
                        desktop: desktopScore,
                        grade: getRatingGrade(overallScore)
                      };

                      console.log('✅ 다중 분석 공유링크 점수 계산:', scores);
                      setCustomScores(scores);
                      setPerformanceScores({
                        desktop: desktopScore,
                        mobile: mobileScore
                      });
                    }
                  }
                } catch (error) {
                  console.error('❌ 다중 분석 점수 계산 실패:', error);
                }
              })();
            }
          } else {
            // 단일 분석 결과
            console.log('📊 단일 분석 복원');
            setDisplayUrl(sharedAnalysis.url);
            setAnalysisResult({
              pageSpeedData: sharedAnalysis.pageSpeedData,
              llmAnalysis: sharedAnalysis.llmAnalysis
            });

          // 커스텀 성능 점수 계산 (실제 Hall of Fame 데이터 사용)
          if (sharedAnalysis.pageSpeedData) {
            console.log('📊 공유링크 PageSpeed 데이터:', sharedAnalysis.pageSpeedData);

            const currentSiteData = {
              fcp_mobile: sharedAnalysis.pageSpeedData.mobile?.firstContentfulPaint,
              lcp_mobile: sharedAnalysis.pageSpeedData.mobile?.largestContentfulPaint,
              tbt_mobile: sharedAnalysis.pageSpeedData.mobile?.totalBlockingTime,
              total_size_mobile: sharedAnalysis.pageSpeedData.mobile?.pageSize?.totalSize,
              fcp_desktop: sharedAnalysis.pageSpeedData.desktop?.firstContentfulPaint,
              lcp_desktop: sharedAnalysis.pageSpeedData.desktop?.largestContentfulPaint,
              tbt_desktop: sharedAnalysis.pageSpeedData.desktop?.totalBlockingTime,
              total_size_desktop: sharedAnalysis.pageSpeedData.desktop?.pageSize?.totalSize
            };

            console.log('📱 공유링크 현재 사이트 메트릭:', currentSiteData);

            // 실제 Hall of Fame 데이터 가져와서 점수 계산 (비동기로 실행)
            (async () => {
              try {
                const apiUrl = process.env.NODE_ENV === 'production'
                  ? ''
                  : (process.env.REACT_APP_API_URL || 'http://localhost:3300');
                const response = await fetch(`${apiUrl}/api/hall-of-fame?showAll=true&limit=200`);

                if (response.ok) {
                  const result = await response.json();
                  if (result.success && result.data) {
                    console.log(`📊 공유링크 벤치마크 데이터: ${result.data.length}개 사이트`);

                    // Hall of Fame 데이터를 state에 저장 (탭 전환 시 재사용)
                    setHallOfFameBenchmarks(result.data);

                    const mobileScore = calculateMobileRating(currentSiteData, result.data);
                    const desktopScore = calculateDesktopRating(currentSiteData, result.data);
                    const overallScore = Math.round((mobileScore + desktopScore) / 2);
                    const grade = getRatingGrade(overallScore);

                    const scores = {
                      overall: overallScore,
                      mobile: mobileScore,
                      desktop: desktopScore,
                      grade: grade
                    };

                    console.log('✅ 공유링크 최종 점수:', scores);
                    setCustomScores(scores);
                    setPerformanceScores({
                      desktop: desktopScore,
                      mobile: mobileScore
                    });
                  } else {
                    console.warn('⚠️ Hall of Fame 데이터 로드 실패');
                  }
                } else {
                  console.error('❌ Hall of Fame API 응답 오류:', response.status);
                }
              } catch (error) {
                console.error('❌ 공유링크 점수 계산 실패:', error);
              }
            })();
          } else {
            console.warn('⚠️ PageSpeed 데이터가 없습니다.');
          }

          // 보고서 섹션으로 자동 스크롤
          setTimeout(() => {
            reportSectionRef.current?.scrollIntoView({ behavior: 'smooth' });
          }, 100);
          }
        } else {
          console.error('❌ 공유 분석을 찾을 수 없습니다.');
          alert('공유된 분석을 찾을 수 없습니다.');
        }
      }
    };

    loadSharedAnalysis();
  }, [user, isAdmin, authLoading, isCheckingAdmin]); // admin 페이지 접근 시 로그인 및 권한 확인 필요

  // 사이트 그룹 로드
  useEffect(() => {
    if (isAdmin && adminMode && showSiteGroupDropdown) {
      fetchSiteGroups();
    }
  }, [isAdmin, adminMode, showSiteGroupDropdown]);

  const fetchSiteGroups = async () => {
    setLoadingSiteGroups(true);
    try {
      // site_pages 테이블에서 직접 조회
      const response = await fetch(getApiUrl('/api/admin/site-pages?limit=100'), {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('google_auth_token')}`
        }
      });

      if (!response.ok) {
        throw new Error('사이트 페이지 로드 실패');
      }

      const result = await response.json();
      console.log('✅ site_pages 로드 완료:', result.data.length, '개');

      // site_pages 데이터를 group 형식으로 변환
      const groupsWithUrls = result.data.map((sitePage: any) => {
        const urls: Array<{ url: string; label: string }> = [];

        if (sitePage.main_page_url) {
          urls.push({ url: sitePage.main_page_url, label: '메인 페이지' });
        }
        if (sitePage.category_page_url) {
          urls.push({ url: sitePage.category_page_url, label: '상품 카테고리' });
        }
        if (sitePage.product_detail_url) {
          urls.push({ url: sitePage.product_detail_url, label: '상품 상세' });
        }
        if (sitePage.login_signup_url) {
          urls.push({ url: sitePage.login_signup_url, label: '로그인/회원가입' });
        }
        if (sitePage.cart_url) {
          urls.push({ url: sitePage.cart_url, label: '장바구니' });
        }
        if (sitePage.other_page_1_url) {
          urls.push({ url: sitePage.other_page_1_url, label: '기타 페이지 1' });
        }
        if (sitePage.other_page_2_url) {
          urls.push({ url: sitePage.other_page_2_url, label: '기타 페이지 2' });
        }
        if (sitePage.other_page_3_url) {
          urls.push({ url: sitePage.other_page_3_url, label: '기타 페이지 3' });
        }
        if (sitePage.other_page_4_url) {
          urls.push({ url: sitePage.other_page_4_url, label: '기타 페이지 4' });
        }
        if (sitePage.other_page_5_url) {
          urls.push({ url: sitePage.other_page_5_url, label: '기타 페이지 5' });
        }

        console.log(`✅ ${sitePage.site_name}: ${urls.length}개 URL`);

        // group 형식으로 변환
        return {
          id: sitePage.id,
          site_name: sitePage.site_name,
          description: `${urls.length}개 페이지`,
          urls
        };
      });

      setSiteGroups(groupsWithUrls);
    } catch (error) {
      console.error('❌ 사이트 페이지 로드 실패:', error);
      setSiteGroups([]);
    } finally {
      setLoadingSiteGroups(false);
    }
  };

  // 필터링된 사이트 그룹
  const filteredSiteGroups = siteGroups.filter(group =>
    group.site_name.toLowerCase().includes(siteGroupSearch.toLowerCase()) ||
    group.id.toString().includes(siteGroupSearch)
  );

  // ========== 사이트 그룹 CRUD 함수 ==========

  // 사이트 그룹 생성/수정 모달 열기
  const openGroupModal = (group: any = null) => {
    if (group) {
      setEditingGroup(group);
      setGroupFormData({ site_name: group.site_name, description: group.description || '' });
    } else {
      setEditingGroup(null);
      setGroupFormData({ site_name: '', description: '' });
    }
    setShowGroupManageModal(true);
  };

  // 사이트 그룹 생성
  const createSiteGroup = async () => {
    try {
      const response = await fetch(getApiUrl('/api/admin/site-groups'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('google_auth_token')}`
        },
        body: JSON.stringify(groupFormData)
      });

      const result = await response.json();

      if (!response.ok) {
        alert(result.error || '사이트 그룹 생성 실패');
        return;
      }

      console.log('✅ 사이트 그룹 생성 완료:', result.data);
      alert('사이트 그룹이 생성되었습니다.');
      setShowGroupManageModal(false);
      fetchSiteGroups(); // 목록 새로고침
    } catch (error) {
      console.error('❌ 사이트 그룹 생성 실패:', error);
      alert('사이트 그룹 생성 중 오류가 발생했습니다.');
    }
  };

  // 사이트 그룹 수정
  const updateSiteGroup = async () => {
    if (!editingGroup) return;

    try {
      const response = await fetch(getApiUrl(`/api/admin/site-groups/${editingGroup.id}`), {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('google_auth_token')}`
        },
        body: JSON.stringify(groupFormData)
      });

      const result = await response.json();

      if (!response.ok) {
        alert(result.error || '사이트 그룹 수정 실패');
        return;
      }

      console.log('✅ 사이트 그룹 수정 완료:', result.data);
      alert('사이트 그룹이 수정되었습니다.');
      setShowGroupManageModal(false);
      fetchSiteGroups(); // 목록 새로고침
    } catch (error) {
      console.error('❌ 사이트 그룹 수정 실패:', error);
      alert('사이트 그룹 수정 중 오류가 발생했습니다.');
    }
  };

  // 사이트 그룹 삭제
  const deleteSiteGroup = async (groupId: number) => {
    if (!window.confirm('정말로 이 사이트 그룹을 삭제하시겠습니까?')) {
      return;
    }

    try {
      const response = await fetch(getApiUrl(`/api/admin/site-groups/${groupId}`), {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('google_auth_token')}`
        }
      });

      const result = await response.json();

      if (!response.ok) {
        alert(result.error || '사이트 그룹 삭제 실패');
        return;
      }

      console.log('✅ 사이트 그룹 삭제 완료:', result.data);
      alert('사이트 그룹이 삭제되었습니다.');
      fetchSiteGroups(); // 목록 새로고침
    } catch (error) {
      console.error('❌ 사이트 그룹 삭제 실패:', error);
      alert('사이트 그룹 삭제 중 오류가 발생했습니다.');
    }
  };

  // ========== URL 관리 함수 (기존 site_groups 시스템) ==========

  // URL 관리 모달 열기
  const openUrlManageModal = async (groupId: number) => {
    setManagingGroupId(groupId);

    try {
      const response = await fetch(getApiUrl(`/api/admin/site-groups/${groupId}`), {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('google_auth_token')}`
        }
      });

      if (response.ok) {
        const result = await response.json();
        setGroupUrls(result.data.urls || []);
      }
    } catch (error) {
      console.error('❌ URL 목록 로드 실패:', error);
      setGroupUrls([]);
    }

    setShowUrlManageModal(true);
  };

  // ========== Site Pages 관리 함수 (새로운 페이지별 URL 시스템) ==========

  // Site Pages 모달 열기
  const openSitePagesModal = async (siteName: string, existingSitePageId?: number) => {
    try {
      setManagingSiteName(siteName);

      // site_pages에서 해당 사이트 조회 (ID로 직접 조회 가능하면 사용)
      if (existingSitePageId) {
        const response = await fetch(
          getApiUrl(`/api/admin/site-pages/${existingSitePageId}`),
          {
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('google_auth_token')}`
            }
          }
        );

        if (response.ok) {
          const result = await response.json();
          const existingData = result.data;

          // 기존 데이터 있음 - 편집 모드
          setSitePagesData(existingData);
          setSitePagesForm({
            site_name: existingData.site_name,
            main_page_url: existingData.main_page_url || '',
            category_page_url: existingData.category_page_url || '',
            product_detail_url: existingData.product_detail_url || '',
            login_signup_url: existingData.login_signup_url || '',
            cart_url: existingData.cart_url || '',
            other_page_1_url: existingData.other_page_1_url || '',
            other_page_2_url: existingData.other_page_2_url || '',
            other_page_3_url: existingData.other_page_3_url || '',
            other_page_4_url: existingData.other_page_4_url || '',
            other_page_5_url: existingData.other_page_5_url || ''
          });
        }
      } else {
        // 데이터 없음 - 신규 생성 모드
        setSitePagesData(null);
        setSitePagesForm({
          site_name: siteName,
          main_page_url: '',
          category_page_url: '',
          product_detail_url: '',
          login_signup_url: '',
          cart_url: '',
          other_page_1_url: '',
          other_page_2_url: '',
          other_page_3_url: '',
          other_page_4_url: '',
          other_page_5_url: ''
        });
      }

      setShowSitePagesModal(true);
    } catch (error) {
      console.error('❌ Site Pages 모달 열기 실패:', error);
      alert('페이지 정보를 불러오는데 실패했습니다.');
    }
  };

  // Site Pages 저장
  const saveSitePages = async () => {
    try {
      const method = sitePagesData ? 'PUT' : 'POST';
      const url = sitePagesData
        ? getApiUrl(`/api/admin/site-pages/${sitePagesData.id}`)
        : getApiUrl('/api/admin/site-pages');

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('google_auth_token')}`
        },
        body: JSON.stringify(sitePagesForm)
      });

      const result = await response.json();

      if (!response.ok) {
        alert(result.error || '저장 실패');
        return;
      }

      alert(sitePagesData ? '수정되었습니다.' : '추가되었습니다.');
      setShowSitePagesModal(false);

      // 목록 새로고침 (드롭다운은 유지)
      await fetchSiteGroups();
    } catch (error) {
      console.error('❌ Site Pages 저장 실패:', error);
      alert('저장 중 오류가 발생했습니다.');
    }
  };

  // URL 추가/수정 모달 열기
  const openUrlFormModal = (url: any = null) => {
    if (url) {
      setEditingUrl(url);
      setUrlFormData({ url: url.url, label: url.label || '', display_order: url.display_order || 0 });
    } else {
      setEditingUrl(null);
      setUrlFormData({ url: '', label: '', display_order: 0 });
    }
  };

  // URL 추가
  const addUrl = async () => {
    if (!managingGroupId) return;

    try {
      const response = await fetch(getApiUrl(`/api/admin/site-groups/${managingGroupId}/urls`), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('google_auth_token')}`
        },
        body: JSON.stringify(urlFormData)
      });

      const result = await response.json();

      if (!response.ok) {
        alert(result.error || 'URL 추가 실패');
        return;
      }

      console.log('✅ URL 추가 완료:', result.data);
      alert('URL이 추가되었습니다.');
      setUrlFormData({ url: '', label: '', display_order: 0 });
      openUrlManageModal(managingGroupId); // 목록 새로고침
      fetchSiteGroups(); // 전체 목록도 새로고침
    } catch (error) {
      console.error('❌ URL 추가 실패:', error);
      alert('URL 추가 중 오류가 발생했습니다.');
    }
  };

  // URL 수정
  const updateUrl = async () => {
    if (!managingGroupId || !editingUrl) return;

    try {
      const response = await fetch(getApiUrl(`/api/admin/site-groups/${managingGroupId}/urls/${editingUrl.id}`), {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('google_auth_token')}`
        },
        body: JSON.stringify(urlFormData)
      });

      const result = await response.json();

      if (!response.ok) {
        alert(result.error || 'URL 수정 실패');
        return;
      }

      console.log('✅ URL 수정 완료:', result.data);
      alert('URL이 수정되었습니다.');
      setEditingUrl(null);
      setUrlFormData({ url: '', label: '', display_order: 0 });
      openUrlManageModal(managingGroupId); // 목록 새로고침
      fetchSiteGroups(); // 전체 목록도 새로고침
    } catch (error) {
      console.error('❌ URL 수정 실패:', error);
      alert('URL 수정 중 오류가 발생했습니다.');
    }
  };

  // URL 삭제
  const deleteUrl = async (urlId: number) => {
    if (!managingGroupId) return;
    if (!window.confirm('정말로 이 URL을 삭제하시겠습니까?')) {
      return;
    }

    try {
      const response = await fetch(getApiUrl(`/api/admin/site-groups/${managingGroupId}/urls/${urlId}`), {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('google_auth_token')}`
        }
      });

      const result = await response.json();

      if (!response.ok) {
        alert(result.error || 'URL 삭제 실패');
        return;
      }

      console.log('✅ URL 삭제 완료:', result.data);
      alert('URL이 삭제되었습니다.');
      openUrlManageModal(managingGroupId); // 목록 새로고침
      fetchSiteGroups(); // 전체 목록도 새로고침
    } catch (error) {
      console.error('❌ URL 삭제 실패:', error);
      alert('URL 삭제 중 오류가 발생했습니다.');
    }
  };

  // 사이트 그룹 선택 핸들러
  const handleSelectSiteGroup = (group: any) => {
    console.log('📋 선택된 사이트 그룹:', group);

    // 이미 fetchSiteGroups에서 site_pages 데이터가 로드되어 있음
    setSelectedGroup(group);
    // 선택 초기화 제거 - 여러 사이트에서 누적 선택 가능
    setSiteGroupSearch(''); // 검색어 초기화
  };

  // URL 체크박스 토글 (URL + label로 unique 비교)
  const handleToggleUrl = (urlItem: { url: string; label?: string }) => {
    setSelectedUrls(prev => {
      // URL과 label이 모두 같은 항목만 찾기
      const exists = prev.some(u => u.url === urlItem.url && u.label === urlItem.label);

      if (exists) {
        // 같은 URL + label 항목만 제거
        return prev.filter(u => !(u.url === urlItem.url && u.label === urlItem.label));
      } else {
        // 최대 5개까지만 선택 가능
        if (prev.length >= 5) {
          alert('최대 5개의 URL만 선택할 수 있습니다.');
          return prev;
        }
        return [...prev, urlItem];
      }
    });
  };

  // URL 전체선택 (최대 5개) - 현재 사이트의 URL만 추가 선택
  const handleSelectAll = () => {
    if (!selectedGroup || !selectedGroup.urls) return;

    // 현재 사이트의 상위 5개 URL 선택
    const urlsToAdd = selectedGroup.urls.slice(0, 5).map((urlItem: any) => ({
      url: urlItem.url,
      label: urlItem.label
    }));

    // 기존 selectedUrls와 중복 제거하여 병합
    setSelectedUrls(prev => {
      const newUrls = [...prev];
      urlsToAdd.forEach((newItem: { url: string; label?: string }) => {
        // 이미 선택된 항목인지 확인 (URL + label)
        const exists = newUrls.some(u => u.url === newItem.url && u.label === newItem.label);
        if (!exists && newUrls.length < 5) {
          newUrls.push(newItem);
        }
      });
      console.log('✅ 전체 선택 완료:', newUrls.length, '개');
      return newUrls;
    });
  };

  // URL 선택 초기화
  const handleClearSelection = () => {
    setSelectedUrls([]);
    console.log('🔄 선택 초기화 완료');
  };

  // 선택된 URL을 입력 필드에 적용
  const handleApplySelectedUrls = () => {
    if (selectedUrls.length === 0) {
      alert('최소 1개 이상의 URL을 선택해주세요.');
      return;
    }

    // URL만 추출하여 입력 필드에 적용
    const urlStrings = selectedUrls.map(item => item.url);
    setUrls(urlStrings);

    // 라벨 정보는 sessionStorage에 임시 저장 (분석 진행 시 사용)
    const urlLabels: { [key: string]: string } = {};
    selectedUrls.forEach(item => {
      if (item.label) {
        urlLabels[item.url] = item.label;
      }
    });
    sessionStorage.setItem('urlLabels', JSON.stringify(urlLabels));

    // 상태 초기화 및 드롭다운 닫기
    setSelectedGroup(null);
    setSelectedUrls([]);
    setShowSiteGroupDropdown(false);

    console.log('✅ URL 적용 완료:', selectedUrls);
  };

  // (자동 적용 로직은 handleSelectAll에서 처리됨)

  // 공유 링크 생성 및 복사
  const handleShareAnalysis = async () => {
    // 게스트 모드 체크
    if (!user) {
      alert('공유 링크 생성은 로그인이 필요합니다.\n\n로그인하시면:\n• 분석 결과를 저장하고 공유할 수 있습니다\n• 최대 200개까지 분석 결과를 저장할 수 있습니다\n• 저장된 분석을 언제든지 불러올 수 있습니다');
      return;
    }

    // 저장소에서 불러온 경우: 저장 없이 공유 링크만 생성
    if (isLoadedFromStorage && loadedAnalysisId) {
      const shareUrl = `${window.location.origin}${window.location.pathname}?v2=true&share=${loadedAnalysisId}`;

      // prompt()로 링크 표시
      prompt('공유 링크:', shareUrl);
      return;
    }

    // 다중 분석 모드 체크
    if (isBatchMode && batchResults.length > 0) {
      // 사용자에게 제목 입력받기
      const defaultTitle = `다중 분석 결과 (${batchResults.length}개 사이트)`;
      const userTitle = prompt('저장할 이름을 입력하세요:', defaultTitle);

      if (userTitle === null) {
        // 취소 버튼 클릭
        return;
      }

      const finalTitle = userTitle.trim() || defaultTitle;

      // 다중 분석 결과 저장
      try {
        // sessionStorage에서 urlLabels 가져오기
        const urlLabelsStr = sessionStorage.getItem('urlLabels');
        const urlLabels = urlLabelsStr ? JSON.parse(urlLabelsStr) : {};

        console.log('💾 다중 분석 저장 시작:', {
          isBatchMode,
          batchResultsLength: batchResults.length,
          firstUrl: batchResults[0].url,
          hasBatchResults: !!batchResults,
          title: finalTitle,
          urlLabels
        });

        const analysisId = await saveAnalysis(
          batchResults[0].url, // 첫 번째 URL을 대표로 사용
          null, // pageSpeedData는 batchResults에 포함
          null, // llmAnalysis는 batchResults에 포함
          finalTitle, // 사용자가 입력한 제목
          batchResults, // 다중 분석 결과 전체 전달
          urlLabels // URL 라벨 전달
        );

        console.log('✅ 다중 분석 저장 완료, ID:', analysisId);

        const shareUrl = `${window.location.origin}${window.location.pathname}?v2=true&share=${analysisId}`;

        // prompt로 URL 표시 (클립보드 권한 불필요)
        const userAction = prompt(
          `✅ 저장 완료!\n\n${batchResults.length}개 사이트 분석 결과가 저장되었습니다.\n\n아래 링크를 복사하여 공유하세요:`,
          shareUrl
        );

        console.log('✅ 다중 분석 저장 및 공유 링크 표시 완료:', shareUrl);
      } catch (error) {
        console.error('❌ 다중 분석 공유 링크 생성 실패:', error);
        alert('공유 링크 생성에 실패했습니다. 로그인이 필요할 수 있습니다.');
      }
    } else {
      // 단일 분석 결과 저장
      if (!displayUrl || !analysisResult.pageSpeedData) {
        alert('분석 결과가 없습니다. 먼저 분석을 진행해주세요.');
        return;
      }

      // 사용자에게 제목 입력받기
      const defaultTitle = `${displayUrl} 분석 결과`;
      const userTitle = prompt('저장할 이름을 입력하세요:', defaultTitle);

      if (userTitle === null) {
        // 취소 버튼 클릭
        return;
      }

      const finalTitle = userTitle.trim() || defaultTitle;

      try {
        const analysisId = await saveAnalysis(
          displayUrl,
          analysisResult.pageSpeedData,
          analysisResult.llmAnalysis,
          finalTitle // 사용자가 입력한 제목
        );

        const shareUrl = `${window.location.origin}${window.location.pathname}?v2=true&share=${analysisId}`;

        // prompt로 URL 표시 (클립보드 권한 불필요)
        const userAction = prompt(
          '✅ 저장 완료!\n\n분석 결과가 저장되었습니다.\n\n아래 링크를 복사하여 공유하세요:',
          shareUrl
        );

        console.log('✅ 저장 및 공유 링크 표시 완료:', shareUrl);
      } catch (error) {
        console.error('❌ 공유 링크 생성 실패:', error);
        alert('공유 링크 생성에 실패했습니다. 로그인이 필요할 수 있습니다.');
      }
    }
  };

  // 뒤로가기 (그룹 목록으로)
  const handleBackToGroups = () => {
    setSelectedGroup(null);
    // selectedUrls는 유지 - 다른 사이트에서 계속 선택 가능
  };

  // 드롭다운 외부 클릭 감지
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;

      // 편집/수정 모달이 열려있으면 외부 클릭 감지 비활성화
      if (showGroupManageModal || showUrlManageModal || showSitePagesModal) {
        return;
      }

      if (showSiteGroupDropdown && !target.closest('.site-group-dropdown-container')) {
        setShowSiteGroupDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showSiteGroupDropdown, showGroupManageModal, showUrlManageModal, showSitePagesModal]);

  // URL 입력 필드 관리 함수
  const handleAddUrlField = () => {
    if (urls.length < 5) {
      setUrls([...urls, 'http://']);
    }
  };

  const handleRemoveUrlField = (index: number) => {
    if (urls.length > 1) {
      const newUrls = urls.filter((_, i) => i !== index);
      setUrls(newUrls);
    }
  };

  const handleUrlChange = (index: number, value: string) => {
    const newUrls = [...urls];
    // http:// 자동 프리픽스 처리
    if (!value) {
      newUrls[index] = 'http://';
    } else if (!value.startsWith('http://') && !value.startsWith('https://')) {
      newUrls[index] = 'http://' + value.replace(/^http:\/\//, '');
    } else {
      newUrls[index] = value;
    }
    setUrls(newUrls);
  };

  // URL 패널 초기화 함수
  const handleResetPanel = () => {
    if (window.confirm('모든 입력 내용을 초기화하시겠습니까?')) {
      setUrls(['http://']);
      setAnalysisResult({ pageSpeedData: null, llmAnalysis: null });
      setBatchResults([]);
      setSelectedGroup(null);
      setSelectedUrls([]);
      setShowSiteGroupDropdown(false);
      console.log('🔄 URL 패널 초기화 완료');
    }
  };

  // 단일/다중 분석 시작 함수
  const handleStartAnalysis = async () => {
    const validUrls = urls.filter(url => url && url !== 'http://' && url !== 'https://');

    if (validUrls.length === 0) {
      alert('최소 1개 이상의 유효한 URL을 입력하세요.');
      return;
    }

    if (validUrls.length === 1) {
      // 단일 분석
      await handleSingleAnalyze(validUrls[0]);
    } else {
      // 다중 분석
      await handleMultiAnalyze(validUrls);
    }
  };

  // 단일 URL 분석 함수 - 기존 Dashboard와 동일한 방식 사용
  const handleSingleAnalyze = async (url: string) => {
    console.log('🚀 V2 단일 URL 분석 시작:', url);

    // 이전 분석 결과 모두 초기화 (로딩 화면만 표시하기 위해)
    setAnalysisResult({ pageSpeedData: null, llmAnalysis: null });
    setCustomScores(null);
    setIsBatchMode(false);
    setBatchResults([]);
    setSelectedUrlIndex(undefined);
    setLoadedUrlLabels(null); // 불러온 라벨 초기화
    setIsLoadedFromStorage(false); // 저장소 불러온 모드 초기화
    setDisplayUrl(url);
    setIsAnalyzing(true);

    try {
      // 백엔드 완전한 분석 API 호출 (PageSpeed + LLM + customRatings 모두 포함)
      // api.ts의 analyzeComplete 함수 사용 (일관성 유지)
      const result = await analyzeComplete(url);

      if (result) {
        const { pageSpeedData, llmAnalysis, customRatings } = result;

        // PageSpeed 데이터 설정
        setAnalysisResult({
          pageSpeedData: pageSpeedData || null,
          llmAnalysis: llmAnalysis || null
        });

        // 백엔드에서 계산된 customRatings 사용 (기존 Dashboard와 동일)
        if (customRatings) {
          console.log('✅ V2: 백엔드 customRatings 사용:', customRatings);
          setCustomScores({
            overall: customRatings.overall,
            mobile: customRatings.mobile,
            desktop: customRatings.desktop,
            grade: getRatingGrade(customRatings.desktop)
          });
          // performanceScores도 동일하게 업데이트 (명예의전당 모달용)
          setPerformanceScores({
            desktop: customRatings.desktop,
            mobile: customRatings.mobile
          });
        }
      }
    } catch (error) {
      console.error('V2 단일 분석 실패:', error);
      alert('분석 중 오류가 발생했습니다. 다시 시도해주세요.');
    } finally {
      setIsAnalyzing(false);
    }
  };

  // 다중 URL 분석 함수
  const handleMultiAnalyze = async (validUrls: string[]) => {
    console.log('🚀 V2 다중 URL 분석 시작:', validUrls);

    const batchId = `batch-${Date.now()}`;
    const sessionId = `batch-analysis-${Date.now()}`;

    // 이전 분석 결과 모두 초기화 (로딩 화면만 표시하기 위해)
    setAnalysisResult({ pageSpeedData: null, llmAnalysis: null });
    setCustomScores(null);
    setDisplayUrl('');
    setIsAnalyzing(true);
    setIsBatchMode(true);
    setBatchResults([]);
    setSelectedUrlIndex(undefined);
    setLoadedUrlLabels(null); // 불러온 라벨 초기화 (새 분석은 sessionStorage 사용)
    setIsLoadedFromStorage(false); // 저장소 불러온 모드 초기화
    setMultiProgress({
      completed: 0,
      total: validUrls.length,
      currentUrl: validUrls[0],
      currentIndex: 0,
      urls: validUrls,
      urlProgress: validUrls.map((url, index) => ({
        url,
        status: index === 0 ? 'analyzing' : 'pending',
        message: index === 0 ? '분석 시작...' : '대기 중'
      }))
    });

    try {
      // WebSocket 연결 및 다중 URL 이벤트 리스너 설정 (sessionId 전달)
      const socket = await connectWebSocket(sessionId);

      // 기존 리스너 제거 (재분석 시 중복 방지)
      socket.off('multiStarted');
      socket.off('multiProgress');
      socket.off('multiComplete');
      socket.off('multiError');

      socket.on('multiStarted', (data: any) => {
        console.log('📨 V2 다중 URL 분석 시작 알림:', data);
      });

      socket.on('multiProgress', (data: any) => {
        console.log('📊 V2 다중 URL 진행 상황:', data);
        setMultiProgress(prev => {
          const newUrlProgress = prev.urlProgress?.slice() || [];

          if (data.currentUrl && data.currentIndex !== undefined) {
            const urlIndex = data.currentIndex;
            if (newUrlProgress[urlIndex]) {
              newUrlProgress[urlIndex] = {
                ...newUrlProgress[urlIndex],
                status: data.status || 'analyzing',
                message: data.message || '분석 중...'
              };
            }
          }

          if (data.error && data.currentIndex !== undefined) {
            const urlIndex = data.currentIndex;
            if (newUrlProgress[urlIndex]) {
              newUrlProgress[urlIndex] = {
                ...newUrlProgress[urlIndex],
                status: 'error',
                error: data.error
              };
            }
          }

          return {
            ...prev,
            completed: data.completed,
            total: data.total,
            currentUrl: data.currentUrl,
            currentIndex: data.currentIndex,
            urlProgress: newUrlProgress
          };
        });
      });

      socket.on('multiComplete', (data: any) => {
        console.log('✅ V2 다중 URL 분석 완료:', data);
        const results = data.results || [];

        setBatchResults(results);
        setSelectedUrlIndex(undefined); // 종합 총평 탭이 기본 선택
        setIsAnalyzing(false);
        setMultiProgress(prev => ({
          ...prev,
          completed: data.total || results.length,
          total: data.total || results.length,
          currentUrl: undefined,
          currentIndex: undefined
        }));

        // 첫 번째 성공한 결과의 데이터로 analysisResult 설정 → Dashboard 렌더링 트리거
        const firstSuccess = results.find((r: any) => !r.failed && r.pageSpeedData);
        if (firstSuccess) {
          setDisplayUrl(firstSuccess.url);
          setAnalysisResult({
            pageSpeedData: firstSuccess.pageSpeedData,
            llmAnalysis: firstSuccess.llmAnalysis
          });

          // 종합 총평 섹션으로 자동 스크롤
          setTimeout(() => {
            reportSectionRef.current?.scrollIntoView({ behavior: 'smooth' });
          }, 100);
        }
      });

      socket.on('multiError', (data: any) => {
        console.error('❌ V2 다중 URL 분석 오류:', data);
        setIsAnalyzing(false);
        alert(data.error || '페이지 분석 중 오류가 발생했습니다.');
      });

      // 백엔드에 다중 URL 분석 요청
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
      };

      if (user) {
        headers['Authorization'] = `Bearer ${localStorage.getItem('google_auth_token')}`;
      }

      // BACKEND_URL은 api.ts에서 import (일관성 유지)
      const response = await fetch(`${BACKEND_URL}/api/batch-analyze`, {
        method: 'POST',
        headers: headers,
        body: JSON.stringify({
          urls: validUrls,
          sessionId: sessionId,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || '페이지 분석 요청 실패');
      }

      console.log('📤 V2 다중 URL 분석 요청 전송 완료');

    } catch (error) {
      console.error('V2 다중 URL 분석 중 오류 발생:', error);
      setIsAnalyzing(false);
      alert(error instanceof Error ? error.message : '페이지 분석 중 오류가 발생했습니다.');
      setMultiProgress({
        completed: 0,
        total: 0,
        currentUrl: undefined,
        currentIndex: undefined,
        urls: [],
        urlProgress: []
      });
    }
  };

  // 도메인 추출 함수
  const extractDomain = (url: string): string => {
    try {
      let domain = url;
      // http(s):// 제거
      domain = domain.replace(/^https?:\/\//, '');
      // www. 제거
      domain = domain.replace(/^www\./, '');
      // 첫 번째 슬래시 이후 제거 (경로 제거)
      domain = domain.split('/')[0];
      // 포트 번호 제거
      domain = domain.split(':')[0];
      return domain;
    } catch (error) {
      return 'unknown-domain';
    }
  };

  // HTML 콘텐츠 생성 함수 (새 창과 다운로드에서 공통 사용)
  const generateResourcesHTML = (filteredItems: any[], opportunityTitle: string, opportunityDescription: string, analysisUrl: string, includeDownloadButton = false) => {
    const domain = extractDomain(analysisUrl);
    const title = translateOpportunityTitle(opportunityTitle).replace(/[^a-zA-Z0-9\s\-\_]/g, '').replace(/\s+/g, '_');
    const date = `${new Date().getFullYear()}${String(new Date().getMonth() + 1).padStart(2, '0')}${String(new Date().getDate()).padStart(2, '0')}`;
    const fileName = `${domain}_${title}_${date}.html`;

    return `
<!DOCTYPE html>
<html lang="ko">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>${translateOpportunityTitle(opportunityTitle)} - 전체 리소스 목록</title>
  <style>
    body {
      font-family: 'Pretendard', 'Noto Sans KR', -apple-system, BlinkMacSystemFont, sans-serif;
      margin: 0;
      padding: 20px;
      background: linear-gradient(135deg, ${designSystem.colors.bgPrimary} 0%, ${designSystem.colors.border} 100%);
      min-height: 100vh;
    }
    .container {
      max-width: 1200px;
      margin: 0 auto;
      background: white;
      border-radius: 1rem;
      box-shadow: ${designSystem.shadows.xl};
      overflow: hidden;
    }
    .header {
      background: linear-gradient(135deg, ${designSystem.colors.primary} 0%, ${designSystem.colors.primaryDark} 100%);
      color: white;
      padding: 2rem;
      text-align: center;
      position: relative;
    }
    .header h1 {
      margin: 0 0 0.5rem 0;
      font-size: 1.75rem;
      font-weight: 700;
    }
    .header p {
      margin: 0;
      font-size: 1rem;
      opacity: 0.9;
    }
    .download-info {
      position: absolute;
      top: 15px;
      right: 20px;
      background: rgba(255, 255, 255, 0.2);
      padding: 0.5rem 0.75rem;
      border-radius: 0.5rem;
      font-size: 0.75rem;
      opacity: 0.8;
    }
    .download-button {
      position: absolute;
      top: 15px;
      left: 20px;
      background: ${designSystem.colors.success};
      color: white;
      border: none;
      padding: 0.625rem 1rem;
      border-radius: 0.625rem;
      cursor: pointer;
      font-weight: 600;
      font-size: 0.8125rem;
      transition: all 0.2s ease;
      box-shadow: ${designSystem.shadows.md};
    }
    .download-button:hover {
      background: #00966B;
      transform: translateY(-1px);
      box-shadow: ${designSystem.shadows.lg};
    }
    .content {
      padding: 2rem;
    }
    .resource-item {
      background: ${designSystem.colors.bgPrimary};
      border: 1px solid ${designSystem.colors.border};
      border-radius: 0.75rem;
      margin-bottom: 1rem;
      padding: 1.25rem;
      transition: all 0.2s ease;
    }
    .resource-item:hover {
      transform: translateY(-2px);
      box-shadow: ${designSystem.shadows.md};
    }
    .resource-number {
      background: ${designSystem.colors.primary};
      color: white;
      padding: 0.25rem 0.75rem;
      border-radius: 1.25rem;
      font-size: 0.75rem;
      font-weight: 600;
      display: inline-block;
      margin-bottom: 0.625rem;
    }
    .resource-url {
      font-family: 'Consolas', 'Monaco', 'Courier New', monospace;
      background: white;
      border: 1px solid ${designSystem.colors.border};
      border-radius: 0.5rem;
      padding: 0.9375rem;
      word-break: break-all;
      line-height: 1.5;
      color: ${designSystem.colors.primary};
      text-decoration: none;
      display: block;
      transition: all 0.2s ease;
    }
    .resource-url:hover {
      background: ${designSystem.colors.primaryLight};
      border-color: ${designSystem.colors.primary};
      color: ${designSystem.colors.primaryDark};
    }
    .stats {
      background: linear-gradient(135deg, ${designSystem.colors.primaryLight} 0%, rgba(36, 103, 231, 0.1) 100%);
      padding: 1.25rem;
      text-align: center;
      color: ${designSystem.colors.primaryDark};
      font-weight: 600;
      font-size: 1rem;
    }
  </style>
</head>
<body>
  <div class="container">
    <div class="header">
      ${includeDownloadButton ? `
      <button class="download-button" onclick="downloadHTML()">
        HTML 다운로드
      </button>
      ` : ''}
      <div class="download-info">
        생성일: ${new Date().toLocaleDateString('ko-KR')}
      </div>
      <h1>${translateOpportunityTitle(opportunityTitle)}</h1>
      <p>전체 리소스 목록</p>
    </div>

    <div class="stats">
      총 <strong>${filteredItems.length}개</strong>의 관련 리소스가 발견되었습니다
    </div>

    ${opportunityDescription ? `
    <div style="background: linear-gradient(135deg, ${designSystem.colors.primaryLight} 0%, rgba(36, 103, 231, 0.05) 100%); padding: 1.25rem; margin: 0; border-bottom: 2px solid ${designSystem.colors.primary}; color: ${designSystem.colors.primaryDark}; line-height: 1.6; font-size: 0.9375rem;">
      <strong>개선 방법:</strong><br>
      ${translateDescription(opportunityDescription)}
    </div>
    ` : ''}

    <div class="content">
      ${filteredItems.map((item: any, index: number) => {
        const fullUrl = item.url ? item.url.replace(/^https?:\/\//, '') : item.source || item.node?.snippet || '알 수 없음';
        const linkUrl = item.url || `https://${fullUrl}`;

        return `
          <div class="resource-item">
            <div class="resource-number">#${index + 1}</div>
            <a href="${linkUrl}" target="_blank" class="resource-url" title="클릭하여 ${fullUrl} 방문">
              ${fullUrl}
            </a>
          </div>
        `;
      }).join('')}

      ${filteredItems.length === 0 ? '<p style="text-align: center; color: #6b7280; font-size: 1rem; padding: 2.5rem;">표시할 리소스가 없습니다.</p>' : ''}
    </div>
  </div>

  ${includeDownloadButton ? `
  <script>
    function downloadHTML() {
      // 다운로드 버튼을 숨기고 HTML 콘텐츠 생성
      const button = document.querySelector('.download-button');
      if (button) button.style.display = 'none';

      const htmlContent = document.documentElement.outerHTML;
      const blob = new Blob([htmlContent], { type: 'text/html;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = '${fileName}';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      // 버튼 다시 보이기
      if (button) button.style.display = 'block';
    }
  </script>
  ` : ''}
</body>
</html>
    `;
  };

  // 새 창에서 모든 리소스 보여주기
  const openResourcesInNewWindow = (details: any, opportunityId: string, opportunityTitle: string, opportunityDescription: string) => {
    if (!details || !details.items || details.items.length === 0) {
      return;
    }

    // optimizer.php 제외
    const excludePatterns = ['optimizer.php'];
    const filteredItems = details.items.filter((item: any) => {
      const itemUrl = item.url || item.source || item.node?.snippet || '';
      return !excludePatterns.some(pattern => itemUrl.includes(pattern));
    });

    const htmlContent = generateResourcesHTML(filteredItems, opportunityTitle, opportunityDescription, displayUrl, true);

    const newWindow = window.open('', '_blank', 'width=1000,height=700,scrollbars=yes,resizable=yes');
    if (newWindow) {
      newWindow.document.write(htmlContent);
      newWindow.document.close();
      newWindow.focus();
    }
  };

  // 리소스 정보 렌더링 함수 (V2 스타일)
  const renderResourceDetails = (details: any, opportunityId: string, opportunityTitle?: string, opportunityDescription?: string) => {
    if (!details || !details.items || details.items.length === 0) {
      return null;
    }

    // optimizer.php 제외
    const excludePatterns = ['optimizer.php'];
    const filteredItems = details.items.filter((item: any) => {
      const url = item.url || item.source || item.node?.snippet || '';
      return !excludePatterns.some(pattern => url.includes(pattern));
    });

    if (filteredItems.length === 0) {
      return null;
    }

    const toggleExpanded = (index: number) => {
      setExpandedItems(prev => ({
        ...prev,
        [opportunityId]: {
          ...prev[opportunityId],
          [index]: !prev[opportunityId]?.[index]
        }
      }));
    };

    const truncateUrl = (url: string, maxLength: number = 80) => {
      if (url.length <= maxLength) return url;
      return url.substring(0, maxLength) + '...';
    };

    return (
      <div style={{ marginTop: '1rem' }}>
        <div style={{
          ...designSystem.typography.caption,
          fontWeight: '600',
          color: designSystem.colors.textPrimary,
          padding: '0.75rem',
          backgroundColor: designSystem.colors.bgPrimary,
          borderRadius: '0.5rem 0.5rem 0 0',
          borderBottom: `2px solid ${designSystem.colors.border}`
        }}>
          소스 URL
        </div>
        {filteredItems.slice(0, 5).map((item: any, index: number) => {
          const fullUrl = item.url ? item.url.replace(/^https?:\/\//, '') : item.source || item.node?.snippet || '알 수 없음';
          const isExpanded = expandedItems[opportunityId]?.[index] || false;

          return (
            <div key={index} style={{
              padding: '0.5rem 0',
              borderBottom: `1px solid ${designSystem.colors.border}`
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                minWidth: 0
              }}>
                <a
                  href={item.url || `https://${fullUrl}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    wordBreak: isExpanded ? 'break-all' : 'normal',
                    whiteSpace: isExpanded ? 'normal' : 'nowrap',
                    lineHeight: '1.4',
                    fontSize: '0.875rem',
                    maxWidth: isExpanded ? '100%' : '80vw',
                    overflow: isExpanded ? 'visible' : 'hidden',
                    textOverflow: isExpanded ? 'clip' : 'ellipsis',
                    flex: 1,
                    fontFamily: 'Consolas, Monaco, "Courier New", monospace',
                    backgroundColor: designSystem.colors.bgPrimary,
                    padding: '0.5rem 0.75rem',
                    borderRadius: '0.5rem',
                    border: `1px solid ${designSystem.colors.border}`,
                    color: designSystem.colors.primary,
                    fontWeight: '600',
                    textDecoration: 'none',
                    display: 'block',
                    transition: 'all 0.2s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = designSystem.colors.primaryLight;
                    e.currentTarget.style.borderColor = designSystem.colors.primary;
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = designSystem.colors.bgPrimary;
                    e.currentTarget.style.borderColor = designSystem.colors.border;
                  }}
                >
                  {isExpanded ? fullUrl : truncateUrl(fullUrl, 120)}
                </a>
                {fullUrl.length > 120 && (
                  <button
                    onClick={() => toggleExpanded(index)}
                    style={{
                      fontSize: '0.75rem',
                      padding: '0.25rem 0.5rem',
                      backgroundColor: isExpanded ? designSystem.colors.border : designSystem.colors.primary,
                      color: isExpanded ? designSystem.colors.textPrimary : 'white',
                      border: 'none',
                      borderRadius: '0.25rem',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      whiteSpace: 'nowrap',
                      flexShrink: 0,
                      fontWeight: '600'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.opacity = '0.8';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.opacity = '1';
                    }}
                  >
                    {isExpanded ? '접기' : '자세히'}
                  </button>
                )}
              </div>
            </div>
          );
        })}
        {filteredItems.length > 5 && (
          <div style={{
            textAlign: 'center',
            padding: '0.75rem'
          }}>
            <button
              onClick={() => openResourcesInNewWindow(details, opportunityId, opportunityTitle || '', opportunityDescription || '')}
              style={{
                backgroundColor: designSystem.colors.primary,
                color: 'white',
                border: 'none',
                padding: '0.5rem 1rem',
                borderRadius: '0.5rem',
                fontSize: '0.875rem',
                fontWeight: '600',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                boxShadow: designSystem.shadows.sm
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = designSystem.shadows.md;
                e.currentTarget.style.backgroundColor = designSystem.colors.primaryDark;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = designSystem.shadows.sm;
                e.currentTarget.style.backgroundColor = designSystem.colors.primary;
              }}
            >
              ... 외 {filteredItems.length - 5}개 항목 모두 보기
            </button>
          </div>
        )}
      </div>
    );
  };

  // 다중 분석 결과에서 URL 선택 핸들러
  const handleUrlClick = (url: string, index: number) => {
    console.log(`📊 V2 URL 보고서 선택: ${url} (인덱스: ${index})`);
    const result = batchResults[index];
    if (result && !result.failed) {
      setSelectedUrlIndex(index);
      setDisplayUrl(url);
      setAnalysisResult({
        pageSpeedData: result.pageSpeedData || null,
        llmAnalysis: result.llmAnalysis || null
      });

      // 백엔드에서 이미 계산된 customRatings 사용 (우선순위)
      if (result.customRatings) {
        console.log('✅ 백엔드에서 계산된 커스텀 점수 사용:', result.customRatings);
        setCustomScores({
          overall: result.customRatings.overall,
          mobile: result.customRatings.mobile,
          desktop: result.customRatings.desktop,
          grade: getRatingGrade(result.customRatings.desktop)
        });
        // performanceScores도 동일하게 업데이트 (명예의전당 모달용)
        setPerformanceScores({
          desktop: result.customRatings.desktop,
          mobile: result.customRatings.mobile
        });
      } else if (result.pageSpeedData) {
        // customRatings가 없는 경우에만 다시 계산 (폴백)
        console.log('⚠️ customRatings 없음 - 프론트엔드에서 재계산');
        const currentSiteData = {
          fcp_mobile: result.pageSpeedData.mobile?.firstContentfulPaint,
          lcp_mobile: result.pageSpeedData.mobile?.largestContentfulPaint,
          tbt_mobile: result.pageSpeedData.mobile?.totalBlockingTime,
          total_size_mobile: result.pageSpeedData.mobile?.pageSize?.totalSize,
          fcp_desktop: result.pageSpeedData.desktop?.firstContentfulPaint,
          lcp_desktop: result.pageSpeedData.desktop?.largestContentfulPaint,
          tbt_desktop: result.pageSpeedData.desktop?.totalBlockingTime,
          total_size_desktop: result.pageSpeedData.desktop?.pageSize?.totalSize
        };

        // Hall of Fame 데이터 가져와서 점수 계산 (비동기로 실행)
        (async () => {
          try {
            const apiUrl = process.env.NODE_ENV === 'production'
              ? ''
              : (process.env.REACT_APP_API_URL || 'http://localhost:3300');
            const response = await fetch(`${apiUrl}/api/hall-of-fame?showAll=true&limit=200`);

            if (response.ok) {
              const result = await response.json();
              if (result.success && result.data) {
                // Hall of Fame 데이터를 state에 저장 (탭 전환 시 재사용)
                setHallOfFameBenchmarks(result.data);

                const mobileScore = calculateMobileRating(currentSiteData, result.data);
                const desktopScore = calculateDesktopRating(currentSiteData, result.data);
                const overallScore = Math.round((mobileScore + desktopScore) / 2);
                const grade = getRatingGrade(overallScore);

                setCustomScores({
                  overall: overallScore,
                  mobile: mobileScore,
                  desktop: desktopScore,
                  grade: grade
                });
              }
            }
          } catch (error) {
            console.log('Custom score calculation failed:', error);
          }
        })();
      }

      // 보고서 섹션으로 부드럽게 스크롤
      setTimeout(() => {
        reportSectionRef.current?.scrollIntoView({
          behavior: 'smooth',
          block: 'start'
        });
      }, 100);
    }
  };

  // 저장된 분석을 불러오는 함수
  const handleLoadAnalysis = (analysis: SavedAnalysis) => {
    console.log('📂 V2 저장된 분석 불러오기:', analysis);

    // batchResults 파싱 (신버전: {results, urlLabels} vs 구버전: 배열)
    let batchResultsArray: any = null;
    let urlLabelsObj: any = {};

    if (analysis.batchResults) {
      if (Array.isArray(analysis.batchResults)) {
        // 구버전: 배열만 있는 경우
        batchResultsArray = analysis.batchResults;
        urlLabelsObj = analysis.urlLabels || {};
      } else if ((analysis.batchResults as any).results) {
        // 신버전: {results: [...], urlLabels: {...}}
        batchResultsArray = (analysis.batchResults as any).results;
        urlLabelsObj = (analysis.batchResults as any).urlLabels || {};
      }
    }

    // batchResults가 있으면 다중 분석 모드
    if (batchResultsArray && batchResultsArray.length > 0) {
      console.log('📊 다중 분석 복원:', batchResultsArray.length, '개');
      setBatchResults(batchResultsArray);
      setIsBatchMode(true);
      setLoadedUrlLabels(urlLabelsObj); // 저장된 라벨 복원

      // 종합 총평 탭이 기본 선택되도록 설정
      setSelectedUrlIndex(undefined);

      // 첫 번째 성공한 결과를 표시용 데이터로 설정 (페이지별 탭 클릭 시 필요)
      const firstSuccess = batchResultsArray.find((r: any) => !r.failed);
      if (firstSuccess) {
        setDisplayUrl(firstSuccess.url);
        setAnalysisResult({
          pageSpeedData: firstSuccess.pageSpeedData || null,
          llmAnalysis: firstSuccess.llmAnalysis || null
        });

        // customRatings 복원
        if (firstSuccess.customRatings) {
          setCustomScores({
            overall: firstSuccess.customRatings.overall,
            mobile: firstSuccess.customRatings.mobile,
            desktop: firstSuccess.customRatings.desktop,
            grade: getRatingGrade(firstSuccess.customRatings.desktop)
          });
          setPerformanceScores({
            desktop: firstSuccess.customRatings.desktop,
            mobile: firstSuccess.customRatings.mobile
          });
        }
      }
    } else {
      // 단일 분석 모드
      console.log('📄 단일 분석 복원');
      setIsBatchMode(false);
      setBatchResults([]);
      setSelectedUrlIndex(undefined);
      setDisplayUrl(analysis.url);
      setAnalysisResult({
        pageSpeedData: analysis.pageSpeedData,
        llmAnalysis: analysis.llmAnalysis
      });

      // PageSpeed 데이터로 점수 재계산 (Hall of Fame 필요)
      if (analysis.pageSpeedData && hallOfFameBenchmarks.length > 0) {
        const currentSiteData = {
          fcp_mobile: analysis.pageSpeedData.mobile?.firstContentfulPaint,
          lcp_mobile: analysis.pageSpeedData.mobile?.largestContentfulPaint,
          tbt_mobile: analysis.pageSpeedData.mobile?.totalBlockingTime,
          total_size_mobile: analysis.pageSpeedData.mobile?.pageSize?.totalSize,
          fcp_desktop: analysis.pageSpeedData.desktop?.firstContentfulPaint,
          lcp_desktop: analysis.pageSpeedData.desktop?.largestContentfulPaint,
          tbt_desktop: analysis.pageSpeedData.desktop?.totalBlockingTime,
          total_size_desktop: analysis.pageSpeedData.desktop?.pageSize?.totalSize
        };

        const mobileScore = calculateMobileRating(currentSiteData, hallOfFameBenchmarks);
        const desktopScore = calculateDesktopRating(currentSiteData, hallOfFameBenchmarks);
        const overallScore = Math.round((mobileScore + desktopScore) / 2);

        setCustomScores({
          overall: overallScore,
          mobile: mobileScore,
          desktop: desktopScore,
          grade: getRatingGrade(overallScore)
        });
        setPerformanceScores({
          desktop: desktopScore,
          mobile: mobileScore
        });
      }
    }

    // 저장소에서 불러온 모드 활성화
    setIsLoadedFromStorage(true);
    setLoadedAnalysisId(analysis.id);

    // 모달 닫기
    setShowSavedAnalysesModal(false);

    // 보고서 섹션으로 스크롤
    setTimeout(() => {
      reportSectionRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      });
    }, 100);
  };

  // 컴포넌트 언마운트 시 WebSocket 정리
  useEffect(() => {
    return () => {
      disconnectWebSocket();
    };
  }, []);

  return (
    <div style={{
      minHeight: '100vh',
      background: `linear-gradient(135deg, ${designSystem.colors.bgPrimary} 0%, ${designSystem.colors.primaryLight} 100%)`,
      fontFamily: "'Inter', 'Noto Sans KR', -apple-system, BlinkMacSystemFont, sans-serif",
      display: 'flex'
    }}>
      {/* 사이드바 오버레이 (모바일) */}
      {isSidebarOpen && (
        <div
          onClick={() => setIsSidebarOpen(false)}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.5)',
            zIndex: 999,
            display: 'none'
          }}
          className="sidebar-overlay"
        />
      )}

      {/* 사이드바 */}
      <aside style={{
        position: 'fixed',
        top: 0,
        left: 0,
        height: '100vh',
        width: isSidebarOpen ? '280px' : '0',
        background: '#1F2937',
        boxShadow: designSystem.shadows.xl,
        transition: 'width 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        overflow: 'hidden',
        zIndex: 1000,
        display: 'flex',
        flexDirection: 'column'
      }}>
        {/* 사이드바 헤더 */}
        <div style={{
          padding: '2rem 1.5rem',
          borderBottom: '1px solid rgba(255, 255, 255, 0.1)'
        }}>
          <h1 style={{
            ...designSystem.typography.h3,
            color: 'white',
            margin: 0,
            fontSize: '1.5rem',
            whiteSpace: 'nowrap'
          }}>
            사이트 분석
          </h1>
        </div>

        {/* 메뉴 항목들 */}
        <nav style={{
          flex: 1,
          padding: '0.5rem 0',
          overflowY: 'auto'
        }}>
          {/* 성능 분석 (현재 활성) */}
          <div>
            <div
              onClick={() => setIsPerformanceMenuOpen(!isPerformanceMenuOpen)}
              style={{
                padding: '0.875rem 1.5rem',
                color: 'white',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                display: 'flex',
                alignItems: 'center',
                gap: '0.75rem',
                whiteSpace: 'nowrap',
                fontSize: '0.95rem',
                fontWeight: '500',
                background: '#2563EB'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = '#1D4ED8';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = '#2563EB';
              }}
            >
              <span style={{ flex: 1 }}>성능 분석</span>
              <span style={{
                transform: isPerformanceMenuOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                transition: 'transform 0.2s ease'
              }}>
                ▼
              </span>
            </div>

            {/* 서브메뉴 */}
            {isPerformanceMenuOpen && (
              <div style={{
                background: 'rgba(0, 0, 0, 0.2)',
                overflow: 'hidden'
              }}>
                {/* 명예의 전당 */}
                <div
                  onClick={() => {
                    setShowHallOfFame(true);
                    setIsSidebarOpen(false); // 모바일에서 사이드바 닫기
                  }}
                  style={{
                    padding: '0.75rem 1.5rem 0.75rem 3rem',
                    color: 'rgba(255, 255, 255, 0.9)',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    fontSize: '0.875rem',
                    fontWeight: '400'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)';
                    e.currentTarget.style.color = 'white';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = 'transparent';
                    e.currentTarget.style.color = 'rgba(255, 255, 255, 0.9)';
                  }}
                >
                  <span>명예의 전당</span>
                </div>

                {/* 저장된 분석 */}
                {user && (
                  <div
                    onClick={() => {
                      setShowSavedAnalysesModal(true);
                      setIsSidebarOpen(false); // 모바일에서 사이드바 닫기
                    }}
                    style={{
                      padding: '0.75rem 1.5rem 0.75rem 3rem',
                      color: 'rgba(255, 255, 255, 0.9)',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem',
                      fontSize: '0.875rem',
                      fontWeight: '400'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)';
                      e.currentTarget.style.color = 'white';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = 'transparent';
                      e.currentTarget.style.color = 'rgba(255, 255, 255, 0.9)';
                    }}
                  >
                    <span>저장된 분석</span>
                  </div>
                )}

                {/* 사이트 페이지 관리 (관리자 전용) */}
                {isAdmin && (
                  <div
                    onClick={() => {
                      setCurrentSidebarPage('site-pages');
                      setIsSidebarOpen(false); // 모바일에서 사이드바 닫기

                      // URL 파라미터 업데이트 (새로고침 시 페이지 유지)
                      const url = new URL(window.location.href);
                      url.searchParams.set('admin', 'site-pages');
                      window.history.pushState({}, '', url.toString());
                    }}
                    style={{
                      padding: '0.75rem 1.5rem 0.75rem 3rem',
                      color: currentSidebarPage === 'site-pages' ? '#FFD700' : 'rgba(255, 255, 255, 0.9)',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem',
                      fontSize: '0.875rem',
                      fontWeight: currentSidebarPage === 'site-pages' ? '600' : '400',
                      background: currentSidebarPage === 'site-pages' ? 'rgba(255, 215, 0, 0.1)' : 'transparent'
                    }}
                    onMouseEnter={(e) => {
                      if (currentSidebarPage !== 'site-pages') {
                        e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)';
                        e.currentTarget.style.color = 'white';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (currentSidebarPage !== 'site-pages') {
                        e.currentTarget.style.background = 'transparent';
                        e.currentTarget.style.color = 'rgba(255, 255, 255, 0.9)';
                      }
                    }}
                  >
                    <span>📋 사이트 페이지 관리</span>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* SEO 분석 (향후 확장용, 비활성) */}
          <div style={{
            padding: '0.875rem 1.5rem',
            color: 'rgba(255, 255, 255, 0.5)',
            cursor: 'not-allowed',
            display: 'flex',
            alignItems: 'center',
            gap: '0.75rem',
            whiteSpace: 'nowrap',
            fontSize: '0.95rem',
            fontWeight: '500'
          }}>
            <span style={{ flex: 1 }}>SEO 분석</span>
            <span style={{
              fontSize: '0.625rem',
              padding: '0.125rem 0.5rem',
              background: 'rgba(255, 255, 255, 0.1)',
              borderRadius: '0.25rem'
            }}>
              곧 출시
            </span>
          </div>
        </nav>

        {/* 사이드바 하단 */}
        <div style={{
          padding: '1.5rem',
          borderTop: `1px solid rgba(255, 255, 255, 0.1)`,
          display: 'flex',
          flexDirection: 'column',
          gap: '1rem'
        }}>
          {user ? (
            <>
              <div style={{
                padding: '0.75rem',
                borderRadius: '0.75rem',
                background: isAdmin
                  ? 'linear-gradient(135deg, rgba(36, 103, 231, 0.2) 0%, rgba(108, 92, 231, 0.2) 100%)'
                  : 'rgba(255, 255, 255, 0.1)',
                whiteSpace: 'nowrap',
                border: isAdmin ? '1px solid rgba(36, 103, 231, 0.3)' : 'none'
              }}>
                <div style={{
                  ...designSystem.typography.caption,
                  color: 'rgba(255, 255, 255, 0.6)',
                  marginBottom: '0.25rem',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}>
                  로그인 사용자
                  {isAdmin && (
                    <span style={{
                      fontSize: '0.625rem',
                      padding: '0.125rem 0.5rem',
                      background: 'linear-gradient(135deg, #2467E7 0%, #6C5CE7 100%)',
                      borderRadius: '0.25rem',
                      color: 'white',
                      fontWeight: '700',
                      letterSpacing: '0.5px'
                    }}>
                      ADMIN
                    </span>
                  )}
                </div>
                <div style={{
                  ...designSystem.typography.body,
                  color: 'white',
                  fontWeight: '600'
                }}>
                  {user.name}
                </div>
              </div>
              <button
                onClick={logout}
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  borderRadius: '0.75rem',
                  background: 'rgba(255, 255, 255, 0.05)',
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                  color: 'rgba(255, 255, 255, 0.9)',
                  fontSize: '0.875rem',
                  fontWeight: '500',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  whiteSpace: 'nowrap'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = 'rgba(239, 68, 68, 0.15)';
                  e.currentTarget.style.borderColor = 'rgba(239, 68, 68, 0.3)';
                  e.currentTarget.style.color = '#FCA5A5';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'rgba(255, 255, 255, 0.05)';
                  e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.1)';
                  e.currentTarget.style.color = 'rgba(255, 255, 255, 0.9)';
                }}
              >
                로그아웃
              </button>
            </>
          ) : (
            <>
              <div style={{
                padding: '0.75rem',
                borderRadius: '0.75rem',
                background: 'rgba(255, 255, 255, 0.05)',
                border: '1px solid rgba(255, 255, 255, 0.1)',
                whiteSpace: 'nowrap'
              }}>
                <div style={{
                  ...designSystem.typography.caption,
                  color: 'rgba(255, 255, 255, 0.6)',
                  marginBottom: '0.25rem'
                }}>
                  게스트 모드
                </div>
                <div style={{
                  ...designSystem.typography.body,
                  color: 'rgba(255, 255, 255, 0.9)',
                  fontSize: '0.875rem'
                }}>
                  로그인하여 더 많은 기능을 이용하세요
                </div>
              </div>
              <button
                onClick={login}
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  borderRadius: '0.75rem',
                  background: 'linear-gradient(135deg, #2467E7 0%, #6C5CE7 100%)',
                  border: 'none',
                  color: 'white',
                  fontSize: '0.875rem',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  whiteSpace: 'nowrap',
                  boxShadow: '0 4px 6px rgba(36, 103, 231, 0.3)'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.boxShadow = '0 6px 12px rgba(36, 103, 231, 0.4)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 4px 6px rgba(36, 103, 231, 0.3)';
                }}
              >
                {process.env.NODE_ENV === 'development' ? '개발 모드로 시작' : 'Google로 로그인'}
              </button>
            </>
          )}
        </div>
      </aside>

      {/* 사이드바 토글 버튼 */}
      <button
        onClick={() => setIsSidebarOpen(!isSidebarOpen)}
        style={{
          position: 'fixed',
          top: '1.5rem',
          left: isSidebarOpen ? '296px' : '1rem',
          zIndex: 1001,
          width: '48px',
          height: '48px',
          borderRadius: '50%',
          background: 'white',
          border: 'none',
          boxShadow: designSystem.shadows.xl,
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          fontSize: '1.5rem'
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.transform = 'scale(1.1)';
          e.currentTarget.style.boxShadow = designSystem.shadows['2xl'];
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.transform = 'scale(1)';
          e.currentTarget.style.boxShadow = designSystem.shadows.xl;
        }}
      >
        {isSidebarOpen ? '✕' : '☰'}
      </button>

      {/* 메인 컨텐츠 영역 */}
      <div style={{
        flex: 1,
        marginLeft: isSidebarOpen ? '280px' : '0',
        transition: 'margin-left 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        minHeight: '100vh'
      }}>

      {/* 사이트 그룹 관리 페이지 */}
      {currentSidebarPage === 'saved-sites' ? (
        <div style={{
          padding: '2rem 0',
          minHeight: '100vh',
          background: designSystem.colors.bgPrimary
        }}>
          <div style={{
            maxWidth: '1800px',
            margin: '0 auto',
            paddingRight: '2rem'
          }}>
            {/* 뒤로가기 버튼 */}
            <button
              onClick={() => {
                setCurrentSidebarPage('dashboard');

                // URL 파라미터 제거 (대시보드로 복귀)
                const url = new URL(window.location.href);
                url.searchParams.delete('admin');
                window.history.pushState({}, '', url.toString());
              }}
              style={{
                marginBottom: '1rem',
                padding: '0.5rem 1rem',
                background: designSystem.colors.primary,
                color: 'white',
                border: 'none',
                borderRadius: '0.5rem',
                cursor: 'pointer',
                fontSize: '0.875rem',
                fontWeight: '600',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                transition: 'background 0.2s ease'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = designSystem.colors.primaryDark;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = designSystem.colors.primary;
              }}
            >
              ← 분석 페이지로 돌아가기
            </button>
            <SiteUrlsManager />
          </div>
        </div>
      ) : currentSidebarPage === 'site-pages' ? (
        <div style={{
          padding: '2rem 0',
          minHeight: '100vh',
          background: designSystem.colors.bgPrimary
        }}>
          <div style={{
            maxWidth: '1800px',
            margin: '0 auto',
            paddingRight: '2rem'
          }}>
            {/* 뒤로가기 버튼 */}
            <button
              onClick={() => {
                setCurrentSidebarPage('dashboard');

                // URL 파라미터 제거 (대시보드로 복귀)
                const url = new URL(window.location.href);
                url.searchParams.delete('admin');
                window.history.pushState({}, '', url.toString());
              }}
              style={{
                marginBottom: '1rem',
                padding: '0.5rem 1rem',
                background: designSystem.colors.primary,
                color: 'white',
                border: 'none',
                borderRadius: '0.5rem',
                cursor: 'pointer',
                fontSize: '0.875rem',
                fontWeight: '600',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                transition: 'background 0.2s ease'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = designSystem.colors.primaryDark;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = designSystem.colors.primary;
              }}
            >
              ← 분석 페이지로 돌아가기
            </button>
            <SitePagesManager />
          </div>
        </div>
      ) : (
        <>
      {/* 메인 히어로 섹션 + URL 입력 - 공유 모드에서는 숨김 */}
      {!isSharedView && (
      <section style={{
        minHeight: 'calc(100vh - 80px)',
        padding: '2rem',
        textAlign: 'center',
        position: 'relative',
        overflow: 'visible',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        {/* 배경 장식 요소들 */}
        <div style={{
          position: 'absolute',
          top: '-100px',
          left: '-100px',
          width: '300px',
          height: '300px',
          background: `radial-gradient(circle, ${designSystem.colors.accent}15 0%, transparent 70%)`,
          borderRadius: '50%'
        }} />
        <div style={{
          position: 'absolute',
          bottom: '-150px',
          right: '-150px',
          width: '400px',
          height: '400px',
          background: `radial-gradient(circle, ${designSystem.colors.secondary}10 0%, transparent 70%)`,
          borderRadius: '50%'
        }} />

        {/* 2열 레이아웃 컨테이너 */}
        <div style={{
          maxWidth: '1400px',
          width: '100%',
          margin: '0 auto',
          position: 'relative',
          zIndex: 1,
          display: 'grid',
          gridTemplateColumns: '1.2fr 1fr',
          gap: '2.5rem',
          overflow: 'visible',
          alignItems: 'stretch',
          transform: 'scale(1.2)',
          transformOrigin: 'top center'
        }}>
          {/* 좌측 열: 타이틀 + 검색 + 주요분석기능 */}
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '2rem'
          }}>
            {/* 타이틀 영역 */}
            <div>
              <h1 style={{
                ...designSystem.typography.h1,
                color: designSystem.colors.primary,
                marginBottom: '1rem',
                textAlign: 'left'
              }}>
                사이트 성능 분석
              </h1>
              <p style={{
                ...designSystem.typography.bodyLarge,
                color: designSystem.colors.textSecondary,
                marginBottom: '0',
                textAlign: 'left'
              }}>
                웹사이트의 성능을 측정하고 개선사항을 제안합니다.
              </p>
            </div>

            {/* URL 입력 섹션 */}
            <div style={{ overflow: 'visible' }}>
          {/* 다중 URL 입력 섹션 */}
          <div style={{
            background: designSystem.colors.bgSecondary,
            borderRadius: '2rem',
            padding: '3rem',
            boxShadow: designSystem.shadows.xl,
            border: `2px solid ${designSystem.colors.primary}20`,
            maxWidth: '800px',
            margin: '0 auto',
            position: 'relative',
            overflow: 'visible'
          }}>
            {/* 관리자: 저장된 목록 불러오기 링크 */}
            {isAdmin && adminMode && (
              <div style={{
                display: 'flex',
                justifyContent: 'flex-end',
                marginBottom: '0.75rem'
              }}>
                <button
                  onClick={() => setShowSiteGroupDropdown(!showSiteGroupDropdown)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.4rem',
                    padding: '0.4rem 0.75rem',
                    background: showSiteGroupDropdown ? designSystem.colors.primaryLight : 'transparent',
                    border: `1px solid ${showSiteGroupDropdown ? designSystem.colors.primary : designSystem.colors.border}`,
                    borderRadius: '0.5rem',
                    fontSize: '0.8rem',
                    color: designSystem.colors.primary,
                    cursor: 'pointer',
                    transition: 'all 0.2s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = designSystem.colors.primaryLight;
                    e.currentTarget.style.borderColor = designSystem.colors.primary;
                  }}
                  onMouseLeave={(e) => {
                    if (!showSiteGroupDropdown) {
                      e.currentTarget.style.background = 'transparent';
                      e.currentTarget.style.borderColor = designSystem.colors.border;
                    }
                  }}
                >
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                    <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                  </svg>
                  <span style={{ fontWeight: '500' }}>관리자</span>
                  <span style={{ color: designSystem.colors.textSecondary }}>저장된 목록</span>
                  <span style={{ fontSize: '0.7rem' }}>{showSiteGroupDropdown ? '▲' : '▼'}</span>
                </button>
              </div>
            )}

            {/* 관리자 드롭다운 오버레이 (새 위치) */}
            {isAdmin && adminMode && showSiteGroupDropdown && (
              <div
                onClick={() => setShowSiteGroupDropdown(false)}
                style={{
                  position: 'fixed',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  background: 'rgba(0, 0, 0, 0.5)',
                  zIndex: 9999
                }}
              />
            )}

            {/* URL 입력 필드들 */}
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '1rem',
              marginBottom: '1.5rem'
            }}>
              {urls.map((url, index) => (
                <div key={index} style={{
                  display: 'flex',
                  gap: '0.75rem',
                  alignItems: 'center'
                }}>
                  <span style={{
                    ...designSystem.typography.body,
                    color: designSystem.colors.textSecondary,
                    minWidth: '30px',
                    fontWeight: '600'
                  }}>
                    {index + 1}.
                  </span>
                  <input
                    type="url"
                    value={url}
                    onChange={(e) => handleUrlChange(index, e.target.value)}
                    onFocus={(e) => {
                      // http:// 커서 자동 포지셔닝
                      if (e.target.value === 'http://') {
                        e.target.setSelectionRange(7, 7);
                      }
                      // 포커스 스타일 적용
                      e.currentTarget.style.borderColor = designSystem.colors.primary;
                      e.currentTarget.style.boxShadow = `0 0 0 3px ${designSystem.colors.primary}15`;
                    }}
                    onBlur={(e) => {
                      e.currentTarget.style.borderColor = designSystem.colors.border;
                      e.currentTarget.style.boxShadow = 'none';
                    }}
                    placeholder="URL을 입력하세요... (예: google.com)"
                    style={{
                      flex: 1,
                      padding: '0.875rem 1.25rem',
                      borderRadius: '1rem',
                      border: `2px solid ${designSystem.colors.border}`,
                      fontSize: '0.95rem',
                      outline: 'none',
                      transition: 'all 0.2s ease',
                      background: designSystem.colors.bgPrimary
                    }}
                  />
                  {urls.length > 1 && (
                    <button
                      onClick={() => handleRemoveUrlField(index)}
                      style={{
                        padding: '0.75rem',
                        background: designSystem.colors.danger,
                        color: 'white',
                        border: 'none',
                        borderRadius: '0.75rem',
                        cursor: 'pointer',
                        fontSize: '1.25rem',
                        lineHeight: '1',
                        width: '40px',
                        height: '40px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        transition: 'all 0.2s ease'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.opacity = '0.8';
                        e.currentTarget.style.transform = 'scale(1.05)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.opacity = '1';
                        e.currentTarget.style.transform = 'scale(1)';
                      }}
                    >
                      ×
                    </button>
                  )}
                </div>
              ))}
            </div>

            {/* 관리자 드롭다운 메뉴 (fixed 모달) */}
            {isAdmin && adminMode && showSiteGroupDropdown && (
                      <div
                        className="site-group-dropdown-container"
                        onClick={(e) => e.stopPropagation()}
                        style={{
                          position: 'fixed',
                          top: '50%',
                          left: '50%',
                          transform: 'translate(-50%, -50%)',
                          width: '90%',
                          maxWidth: '600px',
                          background: designSystem.colors.bgSecondary,
                          borderRadius: '1.5rem',
                          boxShadow: designSystem.shadows['2xl'],
                          border: `2px solid ${designSystem.colors.primary}`,
                          height: 'calc(100vh - 200px)',
                          maxHeight: '700px',
                          overflow: 'hidden',
                          display: 'flex',
                          flexDirection: 'column',
                          zIndex: 10000
                        }}
                      >
                      {/* 조건부 렌더링: 그룹 목록 또는 URL 선택 */}
                      {!selectedGroup ? (
                        <>
                          {/* 검색 입력 */}
                          <div style={{
                            padding: '1rem',
                            borderBottom: `2px solid ${designSystem.colors.border}`
                          }}>
                            <input
                              type="text"
                              placeholder="사이트명 또는 ID 검색..."
                              value={siteGroupSearch}
                              onChange={(e) => setSiteGroupSearch(e.target.value)}
                              autoFocus
                              style={{
                                width: '100%',
                                padding: '0.75rem 1rem',
                                borderRadius: '0.75rem',
                                border: `2px solid ${designSystem.colors.border}`,
                                fontSize: '0.9rem',
                                outline: 'none',
                                transition: 'all 0.2s ease'
                              }}
                              onFocus={(e) => {
                                e.currentTarget.style.borderColor = designSystem.colors.primary;
                                e.currentTarget.style.boxShadow = `0 0 0 3px ${designSystem.colors.primary}15`;
                              }}
                              onBlur={(e) => {
                                e.currentTarget.style.borderColor = designSystem.colors.border;
                                e.currentTarget.style.boxShadow = 'none';
                              }}
                            />
                          </div>

                          {/* 버튼 영역 (새 그룹 추가 + 적용하기) */}
                          <div style={{
                            padding: '1rem',
                            borderBottom: `2px solid ${designSystem.colors.border}`,
                            display: 'flex',
                            gap: '0.5rem'
                          }}>
                            {/* 새 그룹 추가 버튼 */}
                            <button
                              onClick={() => openGroupModal()}
                              style={{
                                flex: selectedUrls.length > 0 ? '0 0 auto' : 1,
                                padding: selectedUrls.length > 0 ? '0.5rem 0.75rem' : '0.75rem',
                                background: 'white',
                                color: designSystem.colors.primary,
                                border: `2px solid ${designSystem.colors.primary}`,
                                borderRadius: '0.75rem',
                                fontSize: selectedUrls.length > 0 ? '0.85rem' : '0.9rem',
                                fontWeight: '600',
                                cursor: 'pointer',
                                transition: 'all 0.2s ease',
                                whiteSpace: 'nowrap'
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.background = designSystem.colors.primaryLight;
                                e.currentTarget.style.transform = 'translateY(-1px)';
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.background = 'white';
                                e.currentTarget.style.transform = 'translateY(0)';
                              }}
                            >
                              + 새 사이트
                            </button>

                            {/* 적용하기 버튼 (선택된 URL이 있을 때만) */}
                            {selectedUrls.length > 0 && (
                              <button
                                onClick={handleApplySelectedUrls}
                                style={{
                                  flex: 1,
                                  padding: '0.75rem',
                                  background: `linear-gradient(135deg, ${designSystem.colors.primary} 0%, ${designSystem.colors.secondary} 100%)`,
                                  color: 'white',
                                  border: 'none',
                                  borderRadius: '0.75rem',
                                  fontSize: '0.9rem',
                                  fontWeight: '700',
                                  cursor: 'pointer',
                                  transition: 'all 0.2s ease',
                                  boxShadow: designSystem.shadows.md
                                }}
                                onMouseEnter={(e) => {
                                  e.currentTarget.style.transform = 'translateY(-2px)';
                                  e.currentTarget.style.boxShadow = designSystem.shadows.lg;
                                }}
                                onMouseLeave={(e) => {
                                  e.currentTarget.style.transform = 'translateY(0)';
                                  e.currentTarget.style.boxShadow = designSystem.shadows.md;
                                }}
                              >
                                선택한 URL 적용하기 ({selectedUrls.length}개)
                              </button>
                            )}
                          </div>

                          {/* 검색 결과 리스트 */}
                          <div style={{
                            flex: 1,
                            overflowY: 'auto'
                          }}>
                            {loadingSiteGroups ? (
                              <div style={{
                                padding: '2rem',
                                textAlign: 'center',
                                color: designSystem.colors.textSecondary
                              }}>
                                사이트 목록을 불러오는 중...
                              </div>
                            ) : filteredSiteGroups.length === 0 ? (
                              <div style={{
                                padding: '2rem',
                                textAlign: 'center',
                                color: designSystem.colors.textMuted
                              }}>
                                {siteGroups.length === 0 ? '저장된 사이트가 없습니다.' : '검색 결과가 없습니다.'}
                              </div>
                            ) : (
                              filteredSiteGroups.map((group) => (
                                <div
                                  key={group.id}
                                  style={{
                                    width: '100%',
                                    padding: '1rem 1.25rem',
                                    background: 'transparent',
                                    borderBottom: `1px solid ${designSystem.colors.border}`,
                                    transition: 'all 0.2s ease'
                                  }}
                                  onMouseEnter={(e) => {
                                    e.currentTarget.style.background = designSystem.colors.primaryLight;
                                  }}
                                  onMouseLeave={(e) => {
                                    e.currentTarget.style.background = 'transparent';
                                  }}
                                >
                                  {/* 그룹 정보 (클릭 가능) */}
                                  <div
                                    onClick={() => handleSelectSiteGroup(group)}
                                    style={{
                                      cursor: 'pointer',
                                      marginBottom: '0.75rem'
                                    }}
                                  >
                                    <div style={{
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: '0.75rem',
                                      marginBottom: '0.5rem'
                                    }}>
                                      <span style={{
                                        fontSize: '0.75rem',
                                        padding: '0.25rem 0.5rem',
                                        background: designSystem.colors.primary,
                                        color: 'white',
                                        borderRadius: '0.375rem',
                                        fontWeight: '700'
                                      }}>
                                        #{group.id}
                                      </span>
                                      <span style={{
                                        fontSize: '0.95rem',
                                        fontWeight: '700',
                                        color: designSystem.colors.textPrimary
                                      }}>
                                        {group.site_name}
                                      </span>
                                      {(() => {
                                        // URL + label로 선택 개수 계산 (중복 URL 대응)
                                        const selectedCount = group.urls.filter((urlItem: any) =>
                                          selectedUrls.some(u => u.url === urlItem.url && u.label === urlItem.label)
                                        ).length;
                                        return selectedCount > 0 && (
                                          <span style={{
                                            fontSize: '0.7rem',
                                            padding: '0.2rem 0.5rem',
                                            background: designSystem.colors.success,
                                            color: 'white',
                                            borderRadius: '0.375rem',
                                            fontWeight: '700'
                                          }}>
                                            {selectedCount}개 선택됨
                                          </span>
                                        );
                                      })()}
                                    </div>
                                    <div style={{
                                      fontSize: '0.8rem',
                                      color: designSystem.colors.textSecondary
                                    }}>
                                      {group.description}
                                    </div>
                                  </div>

                                  {/* 관리 버튼들 */}
                                  <div style={{
                                    display: 'flex',
                                    gap: '0.5rem',
                                    alignItems: 'center'
                                  }}>
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        openSitePagesModal(group.site_name, group.id);
                                      }}
                                      style={{
                                        flex: 1,
                                        padding: '0.5rem',
                                        background: 'transparent',
                                        color: designSystem.colors.primary,
                                        border: `2px solid ${designSystem.colors.primary}`,
                                        borderRadius: '0.5rem',
                                        fontSize: '0.75rem',
                                        fontWeight: '600',
                                        cursor: 'pointer',
                                        transition: 'all 0.2s ease'
                                      }}
                                      onMouseEnter={(e) => {
                                        e.currentTarget.style.background = designSystem.colors.primary;
                                        e.currentTarget.style.color = 'white';
                                        e.currentTarget.style.transform = 'translateY(-1px)';
                                      }}
                                      onMouseLeave={(e) => {
                                        e.currentTarget.style.background = 'transparent';
                                        e.currentTarget.style.color = designSystem.colors.primary;
                                        e.currentTarget.style.transform = 'translateY(0)';
                                      }}
                                    >
                                      페이지 관리
                                    </button>
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        openGroupModal(group);
                                      }}
                                      style={{
                                        padding: '0.5rem 0.75rem',
                                        background: 'transparent',
                                        color: '#6B7280',
                                        border: '2px solid #6B7280',
                                        borderRadius: '0.5rem',
                                        fontSize: '0.75rem',
                                        fontWeight: '600',
                                        cursor: 'pointer',
                                        transition: 'all 0.2s ease'
                                      }}
                                      onMouseEnter={(e) => {
                                        e.currentTarget.style.background = '#6B7280';
                                        e.currentTarget.style.color = 'white';
                                        e.currentTarget.style.transform = 'translateY(-1px)';
                                      }}
                                      onMouseLeave={(e) => {
                                        e.currentTarget.style.background = 'transparent';
                                        e.currentTarget.style.color = '#6B7280';
                                        e.currentTarget.style.transform = 'translateY(0)';
                                      }}
                                    >
                                      편집
                                    </button>
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        deleteSiteGroup(group.id);
                                      }}
                                      style={{
                                        padding: '0.5rem 0.75rem',
                                        background: 'transparent',
                                        color: '#DC2626',
                                        border: '2px solid #DC2626',
                                        borderRadius: '0.5rem',
                                        fontSize: '0.75rem',
                                        fontWeight: '600',
                                        cursor: 'pointer',
                                        transition: 'all 0.2s ease'
                                      }}
                                      onMouseEnter={(e) => {
                                        e.currentTarget.style.background = '#DC2626';
                                        e.currentTarget.style.color = 'white';
                                        e.currentTarget.style.transform = 'translateY(-1px)';
                                      }}
                                      onMouseLeave={(e) => {
                                        e.currentTarget.style.background = 'transparent';
                                        e.currentTarget.style.color = '#DC2626';
                                        e.currentTarget.style.transform = 'translateY(0)';
                                      }}
                                    >
                                      삭제
                                    </button>
                                  </div>
                                </div>
                              ))
                            )}
                          </div>
                        </>
                      ) : (
                        <>
                          {/* URL 선택 모드 */}
                          <div style={{
                            padding: '1rem',
                            borderBottom: `2px solid ${designSystem.colors.border}`,
                            display: 'flex',
                            alignItems: 'center',
                            gap: '1rem'
                          }}>
                            <button
                              onClick={handleBackToGroups}
                              style={{
                                padding: '0.5rem 1rem',
                                background: 'transparent',
                                border: `2px solid ${designSystem.colors.border}`,
                                borderRadius: '0.5rem',
                                cursor: 'pointer',
                                fontSize: '0.9rem',
                                color: designSystem.colors.textPrimary,
                                transition: 'all 0.2s ease'
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.borderColor = designSystem.colors.primary;
                                e.currentTarget.style.color = designSystem.colors.primary;
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.borderColor = designSystem.colors.border;
                                e.currentTarget.style.color = designSystem.colors.textPrimary;
                              }}
                            >
                              ← 뒤로
                            </button>
                            <div style={{ flex: 1 }}>
                              <div style={{
                                fontSize: '1.1rem',
                                fontWeight: '700',
                                color: designSystem.colors.primary
                              }}>
                                {selectedGroup.site_name}
                              </div>
                              <div style={{
                                fontSize: '0.85rem',
                                color: selectedUrls.length >= 5 ? designSystem.colors.danger : designSystem.colors.textSecondary,
                                fontWeight: selectedUrls.length >= 5 ? '700' : '400'
                              }}>
                                전체 {selectedUrls.length}/5개 선택됨
                                {selectedUrls.length >= 5 && ' (최대)'}
                              </div>
                            </div>
                          </div>

                          {/* 전체선택/초기화 버튼 */}
                          <div style={{
                            padding: '0.75rem 1.25rem',
                            borderBottom: `2px solid ${designSystem.colors.border}`,
                            display: 'flex',
                            gap: '0.5rem',
                            background: designSystem.colors.bgPrimary
                          }}>
                            <button
                              onClick={handleSelectAll}
                              style={{
                                flex: 1,
                                padding: '0.625rem 1rem',
                                background: designSystem.colors.primary,
                                color: 'white',
                                border: 'none',
                                borderRadius: '0.5rem',
                                fontSize: '0.875rem',
                                fontWeight: '600',
                                cursor: 'pointer',
                                transition: 'all 0.2s ease'
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.background = designSystem.colors.primaryDark;
                                e.currentTarget.style.transform = 'translateY(-1px)';
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.background = designSystem.colors.primary;
                                e.currentTarget.style.transform = 'translateY(0)';
                              }}
                            >
                              전체선택 {selectedGroup.urls.length > 5 && `(상위 5개)`}
                            </button>
                            <button
                              onClick={handleClearSelection}
                              disabled={selectedUrls.length === 0}
                              style={{
                                flex: 1,
                                padding: '0.625rem 1rem',
                                background: selectedUrls.length === 0 ? '#E5E7EB' : 'white',
                                color: selectedUrls.length === 0 ? '#9CA3AF' : designSystem.colors.textPrimary,
                                border: `2px solid ${selectedUrls.length === 0 ? '#E5E7EB' : designSystem.colors.border}`,
                                borderRadius: '0.5rem',
                                fontSize: '0.875rem',
                                fontWeight: '600',
                                cursor: selectedUrls.length === 0 ? 'not-allowed' : 'pointer',
                                transition: 'all 0.2s ease'
                              }}
                              onMouseEnter={(e) => {
                                if (selectedUrls.length > 0) {
                                  e.currentTarget.style.borderColor = designSystem.colors.danger;
                                  e.currentTarget.style.color = designSystem.colors.danger;
                                  e.currentTarget.style.transform = 'translateY(-1px)';
                                }
                              }}
                              onMouseLeave={(e) => {
                                if (selectedUrls.length > 0) {
                                  e.currentTarget.style.borderColor = designSystem.colors.border;
                                  e.currentTarget.style.color = designSystem.colors.textPrimary;
                                  e.currentTarget.style.transform = 'translateY(0)';
                                }
                              }}
                            >
                              선택초기화
                            </button>
                          </div>

                          {/* URL 체크박스 리스트 */}
                          <div style={{
                            flex: 1,
                            overflowY: 'auto'
                          }}>
                            {selectedGroup.urls.map((urlItem: any, index: number) => {
                              // URL + label로 선택 여부 확인 (중복 URL 대응)
                              const isSelected = selectedUrls.some(u => u.url === urlItem.url && u.label === urlItem.label);

                              return (
                                <label
                                  key={index}
                                  style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    padding: '1rem 1.25rem',
                                    borderBottom: `1px solid ${designSystem.colors.border}`,
                                    cursor: 'pointer',
                                    transition: 'all 0.2s ease',
                                    background: isSelected ? designSystem.colors.primaryLight : 'transparent'
                                  }}
                                  onMouseEnter={(e) => {
                                    if (!isSelected) {
                                      e.currentTarget.style.background = `${designSystem.colors.primary}10`;
                                    }
                                  }}
                                  onMouseLeave={(e) => {
                                    if (!isSelected) {
                                      e.currentTarget.style.background = 'transparent';
                                    }
                                  }}
                                >
                                  <input
                                    type="checkbox"
                                    checked={isSelected}
                                    onChange={() => handleToggleUrl(urlItem)}
                                    style={{
                                      width: '1.25rem',
                                      height: '1.25rem',
                                      marginRight: '1rem',
                                      cursor: 'pointer'
                                    }}
                                  />
                                <div style={{ flex: 1 }}>
                                  <div style={{
                                    fontSize: '0.9rem',
                                    fontWeight: '600',
                                    color: designSystem.colors.textPrimary,
                                    marginBottom: '0.25rem'
                                  }}>
                                    {urlItem.label || `URL ${index + 1}`}
                                  </div>
                                  <div style={{
                                    fontSize: '0.8rem',
                                    color: designSystem.colors.textSecondary,
                                    wordBreak: 'break-all'
                                  }}>
                                    {urlItem.url}
                                  </div>
                                </div>
                              </label>
                            );
                            })}
                          </div>

                          {/* 적용 버튼 */}
                          <div style={{
                            padding: '1rem',
                            borderTop: `2px solid ${designSystem.colors.border}`,
                            display: 'flex',
                            gap: '1rem'
                          }}>
                            <button
                              onClick={handleApplySelectedUrls}
                              disabled={selectedUrls.length === 0}
                              style={{
                                flex: 1,
                                padding: '0.875rem 1.5rem',
                                background: selectedUrls.length > 0
                                  ? `linear-gradient(135deg, ${designSystem.colors.primary} 0%, ${designSystem.colors.secondary} 100%)`
                                  : designSystem.colors.border,
                                color: 'white',
                                border: 'none',
                                borderRadius: '1rem',
                                fontSize: '0.95rem',
                                fontWeight: '700',
                                cursor: selectedUrls.length > 0 ? 'pointer' : 'not-allowed',
                                opacity: selectedUrls.length > 0 ? 1 : 0.5,
                                transition: 'all 0.2s ease'
                              }}
                              onMouseEnter={(e) => {
                                if (selectedUrls.length > 0) {
                                  e.currentTarget.style.transform = 'translateY(-2px)';
                                  e.currentTarget.style.boxShadow = designSystem.shadows.lg;
                                }
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.transform = 'translateY(0)';
                                e.currentTarget.style.boxShadow = 'none';
                              }}
                            >
                              선택한 URL 적용하기 ({selectedUrls.length}개)
                            </button>
                          </div>
                        </>
                      )}
                    </div>
            )}

            {/* 추가/분석 버튼 */}
            <div style={{
              display: 'flex',
              gap: '1rem',
              justifyContent: 'center'
            }}>
              {/* 초기화 버튼 */}
              <button
                onClick={handleResetPanel}
                disabled={isAnalyzing}
                title="URL 패널 초기화"
                style={{
                  padding: '0',
                  background: 'transparent',
                  color: designSystem.colors.danger,
                  border: `2px solid ${designSystem.colors.danger}`,
                  borderRadius: '0.75rem',
                  fontSize: '1.1rem',
                  cursor: isAnalyzing ? 'not-allowed' : 'pointer',
                  opacity: isAnalyzing ? 0.5 : 1,
                  transition: 'all 0.2s ease',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '42px',
                  height: '42px',
                  flexShrink: 0,
                  alignSelf: 'center'
                }}
                onMouseEnter={(e) => {
                  if (!isAnalyzing) {
                    e.currentTarget.style.background = `${designSystem.colors.danger}15`;
                  }
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'transparent';
                }}
              >
                ↺
              </button>

              {urls.length < 5 && (
                <button
                  onClick={handleAddUrlField}
                  disabled={isAnalyzing}
                  style={{
                    padding: '0.875rem 1.5rem',
                    background: 'transparent',
                    color: designSystem.colors.primary,
                    border: `2px solid ${designSystem.colors.primary}`,
                    borderRadius: '1rem',
                    fontSize: '0.95rem',
                    fontWeight: '600',
                    cursor: isAnalyzing ? 'not-allowed' : 'pointer',
                    opacity: isAnalyzing ? 0.5 : 1,
                    transition: 'all 0.2s ease',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem'
                  }}
                  onMouseEnter={(e) => {
                    if (!isAnalyzing) {
                      e.currentTarget.style.background = designSystem.colors.primaryLight;
                    }
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = 'transparent';
                  }}
                >
                  + URL 추가 ({urls.length}/5)
                </button>
              )}

              <button
                onClick={handleStartAnalysis}
                disabled={isAnalyzing}
                style={{
                  padding: '0.875rem 2rem',
                  background: `linear-gradient(135deg, ${designSystem.colors.primary} 0%, ${designSystem.colors.secondary} 100%)`,
                  color: 'white',
                  border: 'none',
                  borderRadius: '1rem',
                  fontSize: '1rem',
                  fontWeight: '600',
                  cursor: isAnalyzing ? 'not-allowed' : 'pointer',
                  boxShadow: designSystem.shadows.md,
                  transition: 'all 0.2s ease',
                  opacity: isAnalyzing ? 0.6 : 1,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}
                onMouseEnter={(e) => {
                  if (!isAnalyzing) {
                    e.currentTarget.style.transform = 'translateY(-2px)';
                    e.currentTarget.style.boxShadow = designSystem.shadows.lg;
                  }
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = designSystem.shadows.md;
                }}
              >
                {isAnalyzing ? (
                  <>
                    <div style={{
                      width: '18px',
                      height: '18px',
                      border: '2px solid rgba(255, 255, 255, 0.3)',
                      borderTop: '2px solid white',
                      borderRadius: '50%',
                      animation: 'spin 1s linear infinite'
                    }} />
                    분석 중...
                  </>
                ) : (
                  <>
                    분석 시작 {urls.length > 1 && `(${urls.length}개)`}
                  </>
                )}
              </button>
            </div>

          </div>
          </div>
          {/* URL 입력 섹션 끝 */}

            {/* 주요 분석 기능 섹션 (좌측 열 안) */}
            <div style={{
              background: `linear-gradient(135deg, ${designSystem.colors.primaryLight} 0%, rgba(232, 242, 255, 0.6) 100%)`,
              borderRadius: '1rem',
              padding: '1.25rem',
              border: `1px solid ${designSystem.colors.primary}25`
            }}>
              <div style={{
                fontSize: '0.85rem',
                fontWeight: '600',
                color: designSystem.colors.textSecondary,
                marginBottom: '1.25rem'
              }}>
                주요 분석 기능
              </div>
              <div style={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: '1.5rem'
              }}>
                {/* 핵심 지표 (CWV) */}
                <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'flex-start' }}>
                  <div style={{
                    width: '36px',
                    height: '36px',
                    borderRadius: '10px',
                    background: 'white',
                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0
                  }}>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={designSystem.colors.primary} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M3 3v18h18" />
                      <path d="M18 17V9" />
                      <path d="M13 17V5" />
                      <path d="M8 17v-3" />
                    </svg>
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: '0.85rem', fontWeight: '600', color: designSystem.colors.textPrimary, marginBottom: '0.2rem' }}>
                      핵심 지표 (CWV)
                    </div>
                    <div style={{ fontSize: '0.75rem', color: designSystem.colors.textSecondary, lineHeight: '1.45', wordBreak: 'keep-all' }}>
                      LCP, FCP, TBT 등 사용자 경험에 영향을 미치는 핵심 지표를 정밀 측정합니다.
                    </div>
                  </div>
                </div>

                {/* 리소스 규모 분석 */}
                <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'flex-start' }}>
                  <div style={{
                    width: '36px',
                    height: '36px',
                    borderRadius: '10px',
                    background: 'white',
                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0
                  }}>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={designSystem.colors.primary} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M12 20V10" />
                      <path d="M18 20V4" />
                      <path d="M6 20v-4" />
                    </svg>
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: '0.85rem', fontWeight: '600', color: designSystem.colors.textPrimary, marginBottom: '0.2rem' }}>
                      리소스 규모 분석
                    </div>
                    <div style={{ fontSize: '0.75rem', color: designSystem.colors.textSecondary, lineHeight: '1.45', wordBreak: 'keep-all' }}>
                      이미지, JS, CSS 등 각 리소스별 용량을 확인하고 최적화 기회를 찾습니다.
                    </div>
                  </div>
                </div>

                {/* AI 인사이트 */}
                <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'flex-start' }}>
                  <div style={{
                    width: '36px',
                    height: '36px',
                    borderRadius: '10px',
                    background: 'white',
                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0
                  }}>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={designSystem.colors.primary} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <circle cx="12" cy="12" r="10" />
                      <path d="M12 16v-4" />
                      <path d="M12 8h.01" />
                    </svg>
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: '0.85rem', fontWeight: '600', color: designSystem.colors.textPrimary, marginBottom: '0.2rem' }}>
                      AI 인사이트
                    </div>
                    <div style={{ fontSize: '0.75rem', color: designSystem.colors.textSecondary, lineHeight: '1.45', wordBreak: 'keep-all' }}>
                      LLM 모델을 통해 성능 저하 원인을 분석하고 즉각적인 수정 코드를 제안합니다.
                    </div>
                  </div>
                </div>

                {/* 벤치마킹 */}
                <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'flex-start' }}>
                  <div style={{
                    width: '36px',
                    height: '36px',
                    borderRadius: '10px',
                    background: 'white',
                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0
                  }}>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={designSystem.colors.primary} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M6 9H4.5a2.5 2.5 0 0 1 0-5H6" />
                      <path d="M18 9h1.5a2.5 2.5 0 0 0 0-5H18" />
                      <path d="M4 22h16" />
                      <path d="M10 14.66V17c0 .55-.47.98-.97 1.21C7.85 18.75 7 20.24 7 22" />
                      <path d="M14 14.66V17c0 .55.47.98.97 1.21C16.15 18.75 17 20.24 17 22" />
                      <path d="M18 2H6v7a6 6 0 0 0 12 0V2Z" />
                    </svg>
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: '0.85rem', fontWeight: '600', color: designSystem.colors.textPrimary, marginBottom: '0.2rem' }}>
                      벤치마킹
                    </div>
                    <div style={{ fontSize: '0.75rem', color: designSystem.colors.textSecondary, lineHeight: '1.45', wordBreak: 'keep-all' }}>
                      동종 카테고리 및 카페24 주요 사이트 대비 현재 사이트의 성능 순위를 비교합니다.
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          {/* 좌측 열 끝 */}

          {/* 우측 열: 대시보드 미리보기 */}
          <div style={{
            background: designSystem.colors.bgSecondary,
            borderRadius: '1.5rem',
            padding: '1.5rem',
            boxShadow: designSystem.shadows.lg,
            border: `1px solid ${designSystem.colors.border}`,
            position: 'relative',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            alignSelf: 'center',
            height: 'fit-content'
          }}>
            {/* 슬라이드 컨테이너 */}
            <div style={{
              display: 'flex',
              transition: 'transform 0.5s ease-in-out',
              transform: `translateX(-${previewSlideIndex * 100}%)`,
              flex: 1
            }}>
              {/* 슬라이드 1: 종합 총평 */}
              <div style={{ minWidth: '100%', padding: '0 0.25rem', display: 'flex', flexDirection: 'column' }}>
                <div style={{
                  background: `linear-gradient(135deg, ${designSystem.colors.primaryLight} 0%, white 100%)`,
                  borderRadius: '1rem',
                  padding: '1.5rem',
                  flex: 1,
                  display: 'flex',
                  flexDirection: 'column'
                }}>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    marginBottom: '1.25rem'
                  }}>
                    <div style={{
                      width: '10px',
                      height: '10px',
                      borderRadius: '50%',
                      background: designSystem.colors.primary
                    }} />
                    <span style={{
                      fontSize: '0.9rem',
                      fontWeight: '600',
                      color: designSystem.colors.primary,
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px'
                    }}>종합 총평</span>
                  </div>

                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '1.5rem',
                    marginBottom: '1.5rem'
                  }}>
                    <div style={{
                      width: '90px',
                      height: '90px',
                      borderRadius: '50%',
                      background: `conic-gradient(${designSystem.colors.success} 0% 85%, ${designSystem.colors.border} 85% 100%)`,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      position: 'relative'
                    }}>
                      <div style={{
                        width: '72px',
                        height: '72px',
                        borderRadius: '50%',
                        background: 'white',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexDirection: 'column'
                      }}>
                        <span style={{ fontSize: '1.75rem', fontWeight: '700', color: designSystem.colors.success }}>85</span>
                        <span style={{ fontSize: '0.7rem', color: designSystem.colors.textMuted }}>점</span>
                      </div>
                    </div>
                    <div>
                      <div style={{ fontSize: '1rem', fontWeight: '600', color: designSystem.colors.textPrimary, marginBottom: '0.35rem' }}>
                        평균 성능 점수
                      </div>
                      <div style={{ fontSize: '0.85rem', color: designSystem.colors.textSecondary }}>
                        3개 사이트 분석 완료
                      </div>
                    </div>
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem', marginBottom: '1.25rem' }}>
                    <div style={{ background: 'white', borderRadius: '0.75rem', padding: '1rem', textAlign: 'center' }}>
                      <div style={{ fontSize: '1.25rem', fontWeight: '700', color: designSystem.colors.success }}>1.4s</div>
                      <div style={{ fontSize: '0.75rem', color: designSystem.colors.textMuted }}>평균 FCP</div>
                    </div>
                    <div style={{ background: 'white', borderRadius: '0.75rem', padding: '1rem', textAlign: 'center' }}>
                      <div style={{ fontSize: '1.25rem', fontWeight: '700', color: designSystem.colors.warning }}>2.8s</div>
                      <div style={{ fontSize: '0.75rem', color: designSystem.colors.textMuted }}>평균 LCP</div>
                    </div>
                  </div>

                  {/* 비즈니스 임팩트 섹션 */}
                  <div style={{
                    background: 'white',
                    borderRadius: '0.75rem',
                    padding: '1rem',
                    border: `1px solid ${designSystem.colors.border}`,
                    flex: 1
                  }}>
                    <div style={{ fontSize: '0.8rem', fontWeight: '600', color: designSystem.colors.textPrimary, marginBottom: '0.75rem' }}>
                      비즈니스 임팩트
                    </div>

                    {/* 가장 시급한 개선 필요 페이지 */}
                    <div style={{ fontSize: '0.75rem', fontWeight: '600', color: '#c0392b', marginBottom: '0.5rem' }}>
                      가장 시급한 개선 필요 페이지
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.75rem' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <div style={{
                          width: '26px',
                          height: '26px',
                          borderRadius: '50%',
                          background: designSystem.colors.danger,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: 'white',
                          fontSize: '0.65rem',
                          fontWeight: '700'
                        }}>62</div>
                        <div>
                          <div style={{ fontSize: '0.7rem', fontWeight: '500', color: designSystem.colors.textPrimary }}>shop.example.com</div>
                          <div style={{ fontSize: '0.6rem', color: designSystem.colors.textMuted }}>LCP 4.2s / TBT 890ms</div>
                        </div>
                      </div>
                      <div style={{
                        padding: '0.2rem 0.4rem',
                        background: 'rgba(225, 112, 85, 0.12)',
                        borderRadius: '4px',
                        fontSize: '0.55rem',
                        fontWeight: '600',
                        color: '#c0392b'
                      }}>우선 개선</div>
                    </div>

                    {/* 구분선 */}
                    <div style={{ height: '1px', background: designSystem.colors.border, marginBottom: '0.75rem' }} />

                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span style={{ fontSize: '0.75rem', color: designSystem.colors.textSecondary }}>예상 이탈률 감소</span>
                        <span style={{ fontSize: '0.85rem', fontWeight: '600', color: designSystem.colors.success }}>-12%</span>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span style={{ fontSize: '0.75rem', color: designSystem.colors.textSecondary }}>전환율 개선 예상</span>
                        <span style={{ fontSize: '0.85rem', fontWeight: '600', color: designSystem.colors.primary }}>+8%</span>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span style={{ fontSize: '0.75rem', color: designSystem.colors.textSecondary }}>SEO 점수 향상</span>
                        <span style={{ fontSize: '0.85rem', fontWeight: '600', color: designSystem.colors.success }}>+15점</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* 슬라이드 2: 개별 보고서 - google.com */}
              <div style={{ minWidth: '100%', padding: '0 0.25rem', display: 'flex', flexDirection: 'column' }}>
                <div style={{
                  background: `linear-gradient(135deg, ${designSystem.colors.primaryLight} 0%, white 100%)`,
                  borderRadius: '1rem',
                  padding: '1.5rem',
                  border: `1px solid ${designSystem.colors.primary}20`,
                  flex: 1,
                  display: 'flex',
                  flexDirection: 'column'
                }}>
                  {/* 리포트 헤더 */}
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    marginBottom: '1.25rem'
                  }}>
                    <div style={{
                      width: '28px',
                      height: '28px',
                      borderRadius: '8px',
                      background: designSystem.colors.primary,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: 'white',
                      fontSize: '0.8rem'
                    }}>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5L14.5 2z" />
                        <polyline points="14 2 14 8 20 8" />
                      </svg>
                    </div>
                    <div style={{ fontSize: '1.1rem', fontWeight: '700', color: '#212529' }}>
                      google.com 성능분석 리포트
                    </div>
                  </div>

                  {/* 핵심 지표 카드 - 가로 나열 */}
                  <div style={{
                    background: '#f8fafc',
                    borderRadius: '10px',
                    padding: '1rem',
                    border: '1px solid #e9ecef',
                    marginBottom: '1rem'
                  }}>
                    <div style={{ fontSize: '0.8rem', fontWeight: '600', color: '#212529', marginBottom: '0.75rem' }}>
                      핵심 지표 (Core Web Vitals)
                    </div>
                    <div style={{ display: 'flex', gap: '0.75rem' }}>
                      {/* FCP */}
                      <div style={{ flex: 1, padding: '0.75rem', background: 'white', borderRadius: '8px', border: '1px solid #e2e8f0', textAlign: 'center' }}>
                        <div style={{ fontSize: '0.65rem', color: '#64748b', marginBottom: '4px' }}>FCP</div>
                        <div style={{ fontSize: '1rem', fontWeight: '700', color: designSystem.colors.success }}>0.8s</div>
                        <div style={{ marginTop: '4px', padding: '3px 8px', borderRadius: '4px', fontSize: '0.55rem', fontWeight: '600', background: 'rgba(0, 184, 148, 0.15)', color: '#00B894', display: 'inline-block' }}>양호</div>
                      </div>
                      {/* LCP */}
                      <div style={{ flex: 1, padding: '0.75rem', background: 'white', borderRadius: '8px', border: '1px solid #e2e8f0', textAlign: 'center' }}>
                        <div style={{ fontSize: '0.65rem', color: '#64748b', marginBottom: '4px' }}>LCP</div>
                        <div style={{ fontSize: '1rem', fontWeight: '700', color: designSystem.colors.success }}>1.2s</div>
                        <div style={{ marginTop: '4px', padding: '3px 8px', borderRadius: '4px', fontSize: '0.55rem', fontWeight: '600', background: 'rgba(0, 184, 148, 0.15)', color: '#00B894', display: 'inline-block' }}>양호</div>
                      </div>
                      {/* TBT */}
                      <div style={{ flex: 1, padding: '0.75rem', background: 'white', borderRadius: '8px', border: '1px solid #e2e8f0', textAlign: 'center' }}>
                        <div style={{ fontSize: '0.65rem', color: '#64748b', marginBottom: '4px' }}>TBT</div>
                        <div style={{ fontSize: '1rem', fontWeight: '700', color: designSystem.colors.success }}>45ms</div>
                        <div style={{ marginTop: '4px', padding: '3px 8px', borderRadius: '4px', fontSize: '0.55rem', fontWeight: '600', background: 'rgba(0, 184, 148, 0.15)', color: '#00B894', display: 'inline-block' }}>양호</div>
                      </div>
                    </div>
                  </div>

                  {/* 종합 점수 */}
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '1rem',
                    padding: '1rem',
                    background: 'rgba(0, 184, 148, 0.08)',
                    borderRadius: '10px',
                    border: '1px solid rgba(0, 184, 148, 0.2)',
                    marginBottom: '1rem'
                  }}>
                    <div style={{
                      width: '48px',
                      height: '48px',
                      borderRadius: '50%',
                      background: designSystem.colors.success,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: 'white',
                      fontSize: '1.1rem',
                      fontWeight: '700'
                    }}>92</div>
                    <div>
                      <div style={{ fontSize: '0.9rem', fontWeight: '600', color: '#212529' }}>우수한 성능</div>
                      <div style={{ fontSize: '0.75rem', color: designSystem.colors.success }}>상위 5% 수준</div>
                    </div>
                  </div>

                  {/* 핵심 성능 개선 기회 - 새로운 디자인 */}
                  <div style={{
                    background: 'white',
                    borderRadius: '10px',
                    padding: '0.85rem',
                    border: `1px solid ${designSystem.colors.border}`,
                    flex: 1
                  }}>
                    {/* 헤더 */}
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <span style={{
                          padding: '0.15rem 0.4rem',
                          background: designSystem.colors.danger,
                          borderRadius: '4px',
                          fontSize: '0.55rem',
                          fontWeight: '600',
                          color: 'white'
                        }}>Action Required</span>
                        <span style={{ fontSize: '0.75rem', fontWeight: '600', color: designSystem.colors.textPrimary }}>핵심 성능 개선 기회</span>
                      </div>
                    </div>
                    <div style={{ fontSize: '0.65rem', color: designSystem.colors.textSecondary, marginBottom: '0.6rem' }}>
                      아래 항목을 최적화하면 <span style={{ color: designSystem.colors.primary, fontWeight: '600' }}>약 1.2초</span> 로딩 속도 개선이 가능합니다.
                    </div>

                    {/* 개선 항목 카드 */}
                    <div style={{
                      background: '#f8fafc',
                      borderRadius: '6px',
                      padding: '0.6rem',
                      border: '1px solid #e2e8f0'
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', marginBottom: '0.35rem' }}>
                        <span style={{
                          padding: '0.1rem 0.3rem',
                          background: 'rgba(225, 112, 85, 0.15)',
                          borderRadius: '3px',
                          fontSize: '0.5rem',
                          fontWeight: '600',
                          color: '#c0392b'
                        }}>Critical</span>
                        <span style={{ fontSize: '0.7rem', fontWeight: '500', color: designSystem.colors.textPrimary }}>미사용 JavaScript 줄이기</span>
                      </div>
                      <div style={{ fontSize: '0.6rem', color: designSystem.colors.textMuted }}>
                        잠재적 절약: <span style={{ color: designSystem.colors.success, fontWeight: '600' }}>2,063 KiB</span> (32개 리소스)
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* 슬라이드 3: 사용 가이드 */}
              <div style={{ minWidth: '100%', padding: '0 0.25rem', display: 'flex', flexDirection: 'column', flex: 1 }}>
                <div style={{
                  background: `linear-gradient(135deg, ${designSystem.colors.primaryLight} 0%, white 100%)`,
                  borderRadius: '12px',
                  padding: '1.5rem',
                  border: `1px solid ${designSystem.colors.primary}20`,
                  display: 'flex',
                  flexDirection: 'column',
                  flex: 1
                }}>
                  {/* 가이드 헤더 */}
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.75rem',
                    marginBottom: '1.25rem'
                  }}>
                    <div style={{
                      width: '32px',
                      height: '32px',
                      borderRadius: '8px',
                      background: designSystem.colors.primary,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: 'white',
                      fontSize: '1rem',
                      fontWeight: '700'
                    }}>?</div>
                    <div style={{ fontSize: '1.1rem', fontWeight: '700', color: '#212529' }}>
                      사용 방법
                    </div>
                  </div>

                  {/* 가이드 항목들 */}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', flex: 1 }}>
                    {/* 항목 1: URL 입력 */}
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: '1rem',
                      padding: '0.85rem 1rem',
                      background: 'white',
                      borderRadius: '12px',
                      border: '1px solid #e2e8f0',
                      flex: 1
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.85rem' }}>
                        <div style={{
                          width: '30px',
                          height: '30px',
                          borderRadius: '50%',
                          background: designSystem.colors.primary,
                          color: 'white',
                          fontSize: '0.85rem',
                          fontWeight: '700',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          flexShrink: 0
                        }}>1</div>
                        <div style={{ textAlign: 'left' }}>
                          <div style={{ fontSize: '0.95rem', fontWeight: '600', color: '#212529' }}>URL 입력</div>
                          <div style={{ fontSize: '0.75rem', color: '#64748b' }}>웹사이트 주소를 입력하세요</div>
                        </div>
                      </div>
                      {/* 미니 입력창 UI */}
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                        padding: '8px 14px',
                        background: '#f8fafc',
                        borderRadius: '8px',
                        border: '1px solid #e2e8f0',
                        fontSize: '0.7rem',
                        color: '#94a3b8'
                      }}>
                        <span style={{ color: designSystem.colors.primary, fontWeight: '500' }}>http://</span>
                        <span>example.com</span>
                      </div>
                    </div>

                    {/* 항목 2: 다중 URL 분석 */}
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: '1rem',
                      padding: '0.85rem 1rem',
                      background: 'white',
                      borderRadius: '12px',
                      border: '1px solid #e2e8f0',
                      flex: 1
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.85rem' }}>
                        <div style={{
                          width: '30px',
                          height: '30px',
                          borderRadius: '50%',
                          background: designSystem.colors.secondary,
                          color: 'white',
                          fontSize: '0.85rem',
                          fontWeight: '700',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          flexShrink: 0
                        }}>2</div>
                        <div style={{ textAlign: 'left' }}>
                          <div style={{ fontSize: '0.95rem', fontWeight: '600', color: '#212529' }}>동시 페이지 분석</div>
                          <div style={{ fontSize: '0.75rem', color: '#64748b' }}>최대 5개 동시 분석</div>
                        </div>
                      </div>
                      {/* 미니 다중 URL UI */}
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                          <div style={{ width: '16px', height: '16px', borderRadius: '50%', background: '#e2e8f0', fontSize: '0.55rem', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#64748b' }}>1</div>
                          <div style={{ width: '70px', height: '10px', background: '#e2e8f0', borderRadius: '3px' }} />
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                          <div style={{ width: '16px', height: '16px', borderRadius: '50%', background: '#e2e8f0', fontSize: '0.55rem', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#64748b' }}>2</div>
                          <div style={{ width: '70px', height: '10px', background: '#e2e8f0', borderRadius: '3px' }} />
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                          <div style={{ width: '16px', height: '16px', borderRadius: '50%', background: designSystem.colors.primary, fontSize: '0.55rem', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white' }}>+</div>
                          <div style={{ fontSize: '0.65rem', color: designSystem.colors.primary, fontWeight: '500' }}>URL 추가</div>
                        </div>
                      </div>
                    </div>

                    {/* 항목 3: 분석 시작 */}
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: '1rem',
                      padding: '0.85rem 1rem',
                      background: 'white',
                      borderRadius: '12px',
                      border: '1px solid #e2e8f0',
                      flex: 1
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.85rem' }}>
                        <div style={{
                          width: '30px',
                          height: '30px',
                          borderRadius: '50%',
                          background: designSystem.colors.success,
                          color: 'white',
                          fontSize: '0.85rem',
                          fontWeight: '700',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          flexShrink: 0
                        }}>3</div>
                        <div style={{ textAlign: 'left' }}>
                          <div style={{ fontSize: '0.95rem', fontWeight: '600', color: '#212529' }}>분석 시작</div>
                          <div style={{ fontSize: '0.75rem', color: '#64748b' }}>Core Web Vitals 리포트</div>
                        </div>
                      </div>
                      {/* 미니 버튼 + 결과 UI */}
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <div style={{
                          padding: '6px 12px',
                          background: `linear-gradient(135deg, ${designSystem.colors.primary} 0%, ${designSystem.colors.secondary} 100%)`,
                          borderRadius: '6px',
                          fontSize: '0.7rem',
                          color: 'white',
                          fontWeight: '600'
                        }}>분석</div>
                        <span style={{ fontSize: '0.75rem', color: '#94a3b8' }}>→</span>
                        <div style={{ width: '26px', height: '26px', borderRadius: '50%', background: designSystem.colors.success, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontSize: '0.65rem', fontWeight: '700' }}>92</div>
                      </div>
                    </div>

                    {/* 항목 4: 리포트 저장/공유 */}
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: '1rem',
                      padding: '0.85rem 1rem',
                      background: 'white',
                      borderRadius: '12px',
                      border: '1px solid #e2e8f0',
                      flex: 1
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.85rem' }}>
                        <div style={{
                          width: '30px',
                          height: '30px',
                          borderRadius: '50%',
                          background: '#f59e0b',
                          color: 'white',
                          fontSize: '0.85rem',
                          fontWeight: '700',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          flexShrink: 0
                        }}>4</div>
                        <div style={{ textAlign: 'left' }}>
                          <div style={{ fontSize: '0.95rem', fontWeight: '600', color: '#212529' }}>저장 및 공유</div>
                          <div style={{ fontSize: '0.75rem', color: '#64748b' }}>리포트 보관 및 링크 공유</div>
                        </div>
                      </div>
                      {/* 미니 저장/공유 UI */}
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <div style={{
                          padding: '6px 10px',
                          background: '#f8fafc',
                          borderRadius: '6px',
                          border: '1px solid #e2e8f0',
                          fontSize: '0.65rem',
                          color: '#64748b',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '4px'
                        }}>
                          <span style={{ fontSize: '0.7rem' }}>💾</span>
                          저장
                        </div>
                        <div style={{
                          padding: '6px 10px',
                          background: designSystem.colors.primaryLight,
                          borderRadius: '6px',
                          border: `1px solid ${designSystem.colors.primary}30`,
                          fontSize: '0.65rem',
                          color: designSystem.colors.primary,
                          display: 'flex',
                          alignItems: 'center',
                          gap: '4px'
                        }}>
                          <span style={{ fontSize: '0.7rem' }}>🔗</span>
                          공유
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* 슬라이드 인디케이터 */}
            <div style={{
              display: 'flex',
              justifyContent: 'center',
              gap: '0.5rem',
              marginTop: '0.75rem'
            }}>
              {[0, 1, 2].map(idx => (
                <button
                  key={idx}
                  onClick={() => setPreviewSlideIndex(idx)}
                  style={{
                    width: previewSlideIndex === idx ? '24px' : '8px',
                    height: '8px',
                    borderRadius: '4px',
                    border: 'none',
                    background: previewSlideIndex === idx ? designSystem.colors.primary : designSystem.colors.border,
                    cursor: 'pointer',
                    transition: 'all 0.3s ease'
                  }}
                />
              ))}
            </div>
          </div>

        </div>
        {/* 2열 레이아웃 끝 */}

      </section>
      )}

      {/* 단일 분석 진행 상황 표시 */}
      {isAnalyzing && !isBatchMode && (
        <section style={{
          padding: '3rem 2rem',
          background: designSystem.colors.bgSecondary
        }}>
          <div style={{
            maxWidth: '800px',
            margin: '0 auto',
            textAlign: 'center'
          }}>
            <div style={{
              background: 'white',
              borderRadius: '2rem',
              padding: '3rem',
              boxShadow: designSystem.shadows.xl
            }}>
              {/* 로딩 애니메이션 */}
              <div style={{
                width: '80px',
                height: '80px',
                border: `8px solid ${designSystem.colors.border}`,
                borderTop: `8px solid ${designSystem.colors.primary}`,
                borderRadius: '50%',
                animation: 'spin 1s linear infinite',
                margin: '0 auto 2rem'
              }} />

              <h2 style={{
                ...designSystem.typography.h2,
                color: designSystem.colors.primary,
                marginBottom: '1rem'
              }}>
                분석 진행 중...
              </h2>

              <p style={{
                ...designSystem.typography.bodyLarge,
                color: designSystem.colors.textSecondary,
                marginBottom: '2rem'
              }}>
                {urls[0]} 을(를) 분석하고 있습니다.
              </p>

              <div style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '0.75rem',
                alignItems: 'center'
              }}>
                <div style={{
                  ...designSystem.typography.body,
                  color: designSystem.colors.textMuted
                }}>
                  PageSpeed Insights 데이터 수집 중...
                </div>
                <div style={{
                  ...designSystem.typography.body,
                  color: designSystem.colors.textMuted
                }}>
                  AI 기반 성능 분석 준비 중...
                </div>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* 다중 분석 진행 상황 표시 */}
      {isAnalyzing && isBatchMode && multiProgress.urlProgress.length > 0 && (
        <section style={{
          padding: '3rem 2rem',
          background: designSystem.colors.bgSecondary
        }}>
          <div style={{
            maxWidth: '1200px',
            margin: '0 auto'
          }}>
            <h2 style={{
              ...designSystem.typography.h2,
              color: designSystem.colors.primary,
              textAlign: 'center',
              marginBottom: '2rem'
            }}>
              페이지 분석 진행 중...
            </h2>
            <div style={{
              background: 'white',
              borderRadius: '1.5rem',
              padding: '2rem',
              boxShadow: designSystem.shadows.lg
            }}>
              {/* 전체 진행률 */}
              <div style={{ marginBottom: '2rem' }}>
                {(() => {
                  // 실제 완료된 URL 개수 계산
                  const actualCompletedCount = multiProgress.urlProgress.filter(
                    up => up.status === 'completed'
                  ).length;
                  const total = multiProgress.total;
                  const percentage = total > 0 ? (actualCompletedCount / total) * 100 : 0;

                  return (
                    <>
                      <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        marginBottom: '0.5rem'
                      }}>
                        <span style={{
                          ...designSystem.typography.body,
                          fontWeight: '600',
                          color: designSystem.colors.textPrimary
                        }}>
                          전체 진행률
                        </span>
                        <span style={{
                          ...designSystem.typography.body,
                          fontWeight: '600',
                          color: designSystem.colors.primary
                        }}>
                          {actualCompletedCount} / {total}
                        </span>
                      </div>
                      <div style={{
                        width: '100%',
                        height: '12px',
                        background: designSystem.colors.border,
                        borderRadius: '6px',
                        overflow: 'hidden'
                      }}>
                        <div style={{
                          width: `${percentage}%`,
                          height: '100%',
                          background: `linear-gradient(90deg, ${designSystem.colors.primary}, ${designSystem.colors.secondary})`,
                          transition: 'width 0.3s ease'
                        }} />
                      </div>
                    </>
                  );
                })()}
              </div>

              {/* 개별 URL 진행 상태 */}
              <div style={{
                display: 'grid',
                gap: '1rem'
              }}>
                {multiProgress.urlProgress.map((urlProgress, index) => {
                  const statusColors = {
                    pending: designSystem.colors.textMuted,
                    waiting: designSystem.colors.warning,
                    analyzing: designSystem.colors.primary,
                    completed: designSystem.colors.success,
                    error: designSystem.colors.danger
                  };

                  const statusLabels = {
                    pending: '대기',
                    waiting: '대기중',
                    analyzing: '분석중',
                    completed: '완료',
                    error: '오류'
                  };

                  return (
                    <div key={index} style={{
                      padding: '1rem',
                      background: designSystem.colors.bgPrimary,
                      borderRadius: '0.75rem',
                      border: `2px solid ${statusColors[urlProgress.status]}30`,
                      display: 'flex',
                      alignItems: 'center',
                      gap: '1rem'
                    }}>
                      <span style={{
                        fontSize: '0.875rem',
                        fontWeight: '600',
                        color: statusColors[urlProgress.status],
                        minWidth: '50px',
                        textAlign: 'center'
                      }}>
                        {statusLabels[urlProgress.status]}
                      </span>
                      <div style={{ flex: 1 }}>
                        <div style={{
                          ...designSystem.typography.body,
                          fontWeight: '600',
                          color: designSystem.colors.textPrimary,
                          marginBottom: '0.25rem'
                        }}>
                          {index + 1}. {urlProgress.url}
                        </div>
                        <div style={{
                          ...designSystem.typography.caption,
                          color: statusColors[urlProgress.status]
                        }}>
                          {urlProgress.error || urlProgress.message || '대기 중'}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* 다중 분석 완료 결과 목록 - 공유 모드 및 Dashboard 표시 시 숨김 */}
      {!isSharedView && !isAnalyzing && isBatchMode && batchResults.length > 0 && !analysisResult.pageSpeedData && (
        <section style={{
          padding: '3rem 2rem',
          background: designSystem.colors.bgPrimary
        }}>
          <div style={{
            maxWidth: '1200px',
            margin: '0 auto'
          }}>
            <h2 style={{
              ...designSystem.typography.h2,
              color: designSystem.colors.success,
              textAlign: 'center',
              marginBottom: '2rem'
            }}>
              페이지 분석 완료 ({batchResults.length}개 사이트)
            </h2>
            <div style={{
              display: 'grid',
              gap: '1.5rem'
            }}>
              {batchResults.map((result, index) => {
                const isClickable = !result.failed;
                const isSelected = selectedUrlIndex === index;
                return (
                  <div
                    key={index}
                    onClick={isClickable ? () => handleUrlClick(result.url, index) : undefined}
                    style={{
                      background: 'white',
                      borderRadius: '1.5rem',
                      padding: '1.5rem',
                      border: `2px solid ${isSelected ? designSystem.colors.primary : (result.failed ? designSystem.colors.danger : designSystem.colors.border)}`,
                      cursor: isClickable ? 'pointer' : 'default',
                      transition: 'all 0.3s ease',
                      boxShadow: isSelected ? designSystem.shadows.xl : designSystem.shadows.md
                    }}
                    onMouseEnter={(e) => {
                      if (isClickable) {
                        e.currentTarget.style.transform = 'translateY(-4px)';
                        e.currentTarget.style.boxShadow = designSystem.shadows.xl;
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (isClickable) {
                        e.currentTarget.style.transform = 'translateY(0)';
                        e.currentTarget.style.boxShadow = isSelected ? designSystem.shadows.xl : designSystem.shadows.md;
                      }
                    }}
                  >
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between'
                    }}>
                      <div style={{ flex: 1 }}>
                        <div style={{
                          ...designSystem.typography.h3,
                          color: result.failed ? designSystem.colors.danger : designSystem.colors.textPrimary,
                          marginBottom: '0.5rem',
                          fontSize: '1.125rem'
                        }}>
                          {index + 1}. {result.url}
                        </div>
                        {result.failed ? (
                          <div style={{
                            ...designSystem.typography.body,
                            color: designSystem.colors.danger
                          }}>
                            오류: {result.error || '알 수 없는 오류가 발생했습니다.'}
                          </div>
                        ) : (
                          <div style={{
                            ...designSystem.typography.body,
                            color: designSystem.colors.textSecondary
                          }}>
                            {isSelected ? '현재 보고서 표시 중' : '클릭하여 보고서 확인'}
                          </div>
                        )}
                      </div>
                      {result.failed ? (
                        <div style={{
                          padding: '0.5rem 1rem',
                          borderRadius: '1rem',
                          background: designSystem.colors.danger,
                          color: 'white',
                          fontSize: '0.875rem',
                          fontWeight: '600'
                        }}>
                          실패
                        </div>
                      ) : result.customRatings ? (
                        <div style={{
                          display: 'flex',
                          flexDirection: 'column',
                          gap: '0.5rem',
                          minWidth: '120px'
                        }}>
                          <div style={{
                            padding: '0.5rem 0.75rem',
                            borderRadius: '0.75rem',
                            background: getRatingColor(result.customRatings.desktop),
                            color: 'white',
                            fontSize: '0.875rem',
                            fontWeight: '700',
                            textAlign: 'center',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '0.5rem'
                          }}>
                            <span>{result.customRatings.desktop}</span>
                            <span style={{ fontSize: '0.75rem', opacity: 0.9 }}>
                              {getRatingGrade(result.customRatings.desktop)}
                            </span>
                          </div>
                          <div style={{
                            display: 'flex',
                            gap: '0.25rem',
                            fontSize: '0.7rem',
                            color: designSystem.colors.textMuted,
                            justifyContent: 'center'
                          }}>
                            <span>모바일: {result.customRatings.mobile}</span>
                          </div>
                        </div>
                      ) : (
                        <div style={{
                          padding: '0.5rem 1rem',
                          borderRadius: '1rem',
                          background: designSystem.colors.success,
                          color: 'white',
                          fontSize: '0.875rem',
                          fontWeight: '600'
                        }}>
                          성공
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* 새로운 분석 버튼 */}
            <div style={{
              textAlign: 'center',
              marginTop: '2rem'
            }}>
              <button
                onClick={() => {
                  setIsBatchMode(false);
                  setBatchResults([]);
                  setSelectedUrlIndex(undefined);
                  setUrls(['http://']);
                  setAnalysisResult({ pageSpeedData: null, llmAnalysis: null });
                  setCustomScores(null);
                }}
                style={{
                  padding: '1rem 2rem',
                  background: designSystem.colors.textSecondary,
                  color: 'white',
                  border: 'none',
                  borderRadius: '1rem',
                  fontSize: '1rem',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  boxShadow: designSystem.shadows.md
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.boxShadow = designSystem.shadows.lg;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = designSystem.shadows.md;
                }}
              >
                새로운 분석 시작
              </button>
            </div>
          </div>
        </section>
      )}

      {/* 분석 결과 영역 - V2 전용 완전 새로운 디자인 */}
      {analysisResult.pageSpeedData && (
        <section
          ref={reportSectionRef}
          style={{
            padding: '0',
            background: `linear-gradient(135deg, ${designSystem.colors.bgPrimary} 0%, ${designSystem.colors.primaryLight}15 100%)`,
            position: showSiteGroupDropdown ? 'relative' : undefined,
            zIndex: showSiteGroupDropdown ? -1 : undefined
          }}>
          {/* 풀와이드 헤더 */}
          <div style={{
            background: `linear-gradient(135deg, ${designSystem.colors.primary} 0%, ${designSystem.colors.secondary} 100%)`,
            color: 'white',
            padding: '3rem 2rem',
            textAlign: 'center',
            position: 'relative',
            overflow: 'hidden'
          }}>
            {/* 배경 장식 */}
            <div style={{
              position: 'absolute',
              top: '-100px',
              right: '-100px',
              width: '200px',
              height: '200px',
              background: 'rgba(255, 255, 255, 0.1)',
              borderRadius: '50%'
            }} />
            <div style={{
              position: 'relative',
              zIndex: 1,
              maxWidth: '1200px',
              margin: '0 auto'
            }}>
              <h1 style={{
                ...designSystem.typography.h1,
                color: 'white',
                marginBottom: '1rem',
                fontSize: '2.5rem'
              }}>
                분석 완료
              </h1>
              <p style={{
                ...designSystem.typography.bodyLarge,
                color: 'rgba(255, 255, 255, 0.9)',
                margin: '0 0 1.5rem 0',
                fontSize: '1.125rem'
              }}>
                {displayUrl} 분석 결과입니다
              </p>
              {/* 공유 모드에 따라 다른 버튼 표시 */}
              {isSharedView ? (
                <button
                  onClick={() => window.location.href = 'https://speed.bc-hub.org/?v2=true'}
                  style={{
                    padding: '1rem 2.5rem',
                    background: `linear-gradient(135deg, #00D4FF 0%, #2467E7 50%, #6C5CE7 100%)`,
                    color: 'white',
                    border: '2px solid rgba(255, 255, 255, 0.5)',
                    borderRadius: '2rem',
                    fontSize: '1.125rem',
                    fontWeight: '700',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                    boxShadow: `${designSystem.shadows['2xl']}, 0 0 20px rgba(0, 212, 255, 0.4)`,
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    textShadow: '0 2px 4px rgba(0, 0, 0, 0.2)'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-4px) scale(1.02)';
                    e.currentTarget.style.boxShadow = `0 30px 60px rgba(0, 0, 0, 0.3), 0 0 30px rgba(0, 212, 255, 0.6)`;
                    e.currentTarget.style.border = '2px solid rgba(255, 255, 255, 0.8)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0) scale(1)';
                    e.currentTarget.style.boxShadow = `${designSystem.shadows['2xl']}, 0 0 20px rgba(0, 212, 255, 0.4)`;
                    e.currentTarget.style.border = '2px solid rgba(255, 255, 255, 0.5)';
                  }}
                >
                  새로운 사이트 분석하기
                </button>
              ) : isLoadedFromStorage ? (
                <button
                  onClick={handleShareAnalysis}
                  style={{
                    padding: '1rem 2.5rem',
                    background: `linear-gradient(135deg, #00D4FF 0%, #2467E7 50%, #6C5CE7 100%)`,
                    color: 'white',
                    border: '2px solid rgba(255, 255, 255, 0.5)',
                    borderRadius: '2rem',
                    fontSize: '1.125rem',
                    fontWeight: '700',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                    boxShadow: `${designSystem.shadows['2xl']}, 0 0 20px rgba(0, 212, 255, 0.4)`,
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    textShadow: '0 2px 4px rgba(0, 0, 0, 0.2)'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-4px) scale(1.02)';
                    e.currentTarget.style.boxShadow = `0 30px 60px rgba(0, 0, 0, 0.3), 0 0 30px rgba(0, 212, 255, 0.6)`;
                    e.currentTarget.style.border = '2px solid rgba(255, 255, 255, 0.8)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0) scale(1)';
                    e.currentTarget.style.boxShadow = `${designSystem.shadows['2xl']}, 0 0 20px rgba(0, 212, 255, 0.4)`;
                    e.currentTarget.style.border = '2px solid rgba(255, 255, 255, 0.5)';
                  }}
                >
                  공유하기
                </button>
              ) : (
                <button
                  onClick={handleShareAnalysis}
                  style={{
                    padding: '0.75rem 1.5rem',
                    background: 'rgba(255, 255, 255, 0.2)',
                    color: 'white',
                    border: '2px solid rgba(255, 255, 255, 0.5)',
                    borderRadius: '0.75rem',
                    fontSize: '1rem',
                    fontWeight: '600',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    backdropFilter: 'blur(10px)',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '0.5rem'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = 'rgba(255, 255, 255, 0.3)';
                    e.currentTarget.style.transform = 'translateY(-2px)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = 'rgba(255, 255, 255, 0.2)';
                    e.currentTarget.style.transform = 'translateY(0)';
                  }}
                >
                  💾 공유 및 저장
                </button>
              )}
            </div>
          </div>

          {/* 다중 분석 탭 네비게이션 - 레이어 겹침 스타일 */}
          {batchResults.length > 1 && (
            <div style={{
              position: 'relative',
              display: 'flex',
              justifyContent: 'center',
              padding: '0 5%',
              overflowX: 'auto',
              WebkitOverflowScrolling: 'touch',
              scrollbarWidth: 'none',
              msOverflowStyle: 'none',
              marginTop: '0'
            }} className="hide-scrollbar">
              <style>{`
                .hide-scrollbar::-webkit-scrollbar {
                  display: none;
                }
              `}</style>

              {/* 종합 총평 탭 - 첫 번째 탭 */}
              <button
                onClick={() => {
                  console.log('📊 종합 총평 탭 클릭');
                  setSelectedUrlIndex(undefined);
                }}
                style={{
                  padding: '1rem 2rem 1.25rem 2rem',
                  background: selectedUrlIndex === undefined
                    ? `linear-gradient(135deg, #667eea 0%, #764ba2 100%)`
                    : 'white',
                  color: selectedUrlIndex === undefined ? 'white' : designSystem.colors.textSecondary,
                  border: 'none',
                  borderBottom: selectedUrlIndex === undefined ? 'none' : `2px solid ${designSystem.colors.border}`,
                  borderRadius: '0 0 1.25rem 1.25rem',
                  fontSize: '0.95rem',
                  fontWeight: selectedUrlIndex === undefined ? '700' : '500',
                  cursor: 'pointer',
                  transition: 'all 0.25s ease',
                  whiteSpace: 'nowrap',
                  boxShadow: selectedUrlIndex === undefined
                    ? '0 10px 25px rgba(0, 0, 0, 0.2)'
                    : '0 3px 10px rgba(0, 0, 0, 0.08)',
                  position: 'relative',
                  zIndex: selectedUrlIndex === undefined ? 100 : 0,
                  flexShrink: 0,
                  marginRight: '0.25rem'
                }}
                onMouseEnter={(e) => {
                  if (selectedUrlIndex !== undefined) {
                    e.currentTarget.style.background = designSystem.colors.bgSecondary;
                    e.currentTarget.style.boxShadow = '0 6px 18px rgba(0, 0, 0, 0.12)';
                    e.currentTarget.style.zIndex = '50';
                    e.currentTarget.style.borderBottomColor = designSystem.colors.primary;
                  }
                }}
                onMouseLeave={(e) => {
                  if (selectedUrlIndex !== undefined) {
                    e.currentTarget.style.background = 'white';
                    e.currentTarget.style.boxShadow = '0 3px 10px rgba(0, 0, 0, 0.08)';
                    e.currentTarget.style.zIndex = '0';
                    e.currentTarget.style.borderBottomColor = designSystem.colors.border;
                  }
                }}
              >
                📊 종합 총평
              </button>

              {batchResults.map((result, index) => {
                const isActive = selectedUrlIndex === index;
                const isFailed = result.failed === true;

                // 공유 모드에서는 실패한 탭을 아예 표시하지 않음
                if (isSharedView && isFailed) {
                  return null;
                }

                // 라벨 우선순위: 1) 불러온 분석의 라벨 (고정), 2) sessionStorage (현재 분석)
                const urlLabel = (() => {
                  try {
                    // 저장된 분석을 불러온 경우 → loadedUrlLabels 사용 (저장 당시 고정)
                    if (loadedUrlLabels && loadedUrlLabels[result.url]) {
                      return loadedUrlLabels[result.url];
                    }
                    // 새 분석인 경우 → sessionStorage 사용 (현재 편집 중)
                    const sessionLabels = sessionStorage.getItem('urlLabels');
                    if (sessionLabels) {
                      const labels = JSON.parse(sessionLabels);
                      return labels[result.url] || new URL(result.url).hostname;
                    }
                    return new URL(result.url).hostname;
                  } catch {
                    return new URL(result.url).hostname;
                  }
                })();

                return (
                  <button
                    key={index}
                    disabled={isFailed}
                    onClick={() => {
                      if (isFailed) return; // 실패한 탭은 클릭 불가

                      console.log('🔄 탭 클릭:', result.url, index);
                      setSelectedUrlIndex(index);
                      setDisplayUrl(result.url);
                      setAnalysisResult({
                        pageSpeedData: result.pageSpeedData || null,
                        llmAnalysis: result.llmAnalysis || null
                      });

                      // 백엔드에서 계산된 customRatings 우선 사용
                      if (result.customRatings) {
                        console.log('✅ 탭 전환 - 백엔드 점수 사용:', result.customRatings);
                        setCustomScores({
                          overall: result.customRatings.overall,
                          mobile: result.customRatings.mobile,
                          desktop: result.customRatings.desktop,
                          grade: getRatingGrade(result.customRatings.overall)
                        });
                        setPerformanceScores({
                          desktop: result.customRatings.desktop,
                          mobile: result.customRatings.mobile
                        });
                      } else if (result.pageSpeedData && hallOfFameBenchmarks.length > 0) {
                        // customRatings가 없으면 프론트엔드에서 재계산 (폴백)
                        console.log('⚠️ 탭 전환 - 백엔드 점수 없음, 재계산 시작');
                        const currentSiteData = {
                          fcp_mobile: result.pageSpeedData.mobile?.firstContentfulPaint,
                          lcp_mobile: result.pageSpeedData.mobile?.largestContentfulPaint,
                          tbt_mobile: result.pageSpeedData.mobile?.totalBlockingTime,
                          total_size_mobile: result.pageSpeedData.mobile?.pageSize?.totalSize,
                          fcp_desktop: result.pageSpeedData.desktop?.firstContentfulPaint,
                          lcp_desktop: result.pageSpeedData.desktop?.largestContentfulPaint,
                          tbt_desktop: result.pageSpeedData.desktop?.totalBlockingTime,
                          total_size_desktop: result.pageSpeedData.desktop?.pageSize?.totalSize
                        };

                        const mobileScore = calculateMobileRating(currentSiteData, hallOfFameBenchmarks);
                        const desktopScore = calculateDesktopRating(currentSiteData, hallOfFameBenchmarks);
                        const overallScore = Math.round((mobileScore + desktopScore) / 2);

                        const scores = {
                          overall: overallScore,
                          mobile: mobileScore,
                          desktop: desktopScore,
                          grade: getRatingGrade(overallScore)
                        };

                        console.log('✅ 탭 전환 - 재계산 완료:', scores);
                        setCustomScores(scores);
                        setPerformanceScores({
                          desktop: desktopScore,
                          mobile: mobileScore
                        });
                      }

                      setTimeout(() => {
                        reportSectionRef.current?.scrollIntoView({ behavior: 'smooth' });
                      }, 100);
                    }}
                    style={{
                      padding: '1rem 2rem 1.25rem 2rem',
                      background: isFailed
                        ? '#E5E7EB' // 실패한 탭 - 회색 배경
                        : isActive
                        ? `linear-gradient(135deg, ${designSystem.colors.primary} 0%, ${designSystem.colors.secondary} 100%)`
                        : 'white',
                      color: isFailed
                        ? '#9CA3AF' // 실패한 탭 - 회색 텍스트
                        : isActive ? 'white' : designSystem.colors.textSecondary,
                      border: 'none',
                      borderBottom: isActive ? 'none' : `2px solid ${designSystem.colors.border}`,
                      borderRadius: '0 0 1.25rem 1.25rem',
                      fontSize: '0.95rem',
                      fontWeight: isActive ? '700' : '500',
                      cursor: isFailed ? 'not-allowed' : 'pointer',
                      transition: 'all 0.25s ease',
                      whiteSpace: 'nowrap',
                      boxShadow: isActive
                        ? '0 10px 25px rgba(0, 0, 0, 0.2)'
                        : '0 3px 10px rgba(0, 0, 0, 0.08)',
                      position: 'relative',
                      zIndex: isActive ? 100 : index,
                      flexShrink: 0,
                      marginLeft: index === 0 ? '0' : '-0.5rem',
                      opacity: isFailed ? 0.6 : 1
                    }}
                    onMouseEnter={(e) => {
                      if (!isActive && !isFailed) {
                        e.currentTarget.style.background = designSystem.colors.bgSecondary;
                        e.currentTarget.style.boxShadow = '0 6px 18px rgba(0, 0, 0, 0.12)';
                        e.currentTarget.style.zIndex = '50';
                        e.currentTarget.style.borderBottomColor = designSystem.colors.primary;
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!isActive && !isFailed) {
                        e.currentTarget.style.background = 'white';
                        e.currentTarget.style.boxShadow = '0 3px 10px rgba(0, 0, 0, 0.08)';
                        e.currentTarget.style.zIndex = String(index);
                        e.currentTarget.style.borderBottomColor = designSystem.colors.border;
                      }
                    }}
                  >
                    {index + 1}. {urlLabel}{isFailed && ' (실패)'}
                  </button>
                );
              })}
            </div>
          )}

          {/* 다중 URL 비교 테이블 - 종합 총평 탭에서만 표시 */}
          {batchResults.length > 1 && selectedUrlIndex === undefined && (
            <div style={{ padding: '2rem 5% 0', background: 'white' }}>
              <MultiUrlComparisonTable
                batchResults={batchResults}
                selectedUrlIndex={selectedUrlIndex}
                urlLabels={loadedUrlLabels}
                onUrlClick={handleUrlClick}
                testCase={testCase}
              />
            </div>
          )}

          {/* 메인 분석 대시보드 - 진정한 풀와이드 */}
          {/* 종합 총평 탭(selectedUrlIndex === undefined)에서는 숨김, 사이트별 탭에서는 바로 표시 */}
          {(batchResults.length <= 1 || selectedUrlIndex !== undefined) && (
            <div style={{
              padding: batchResults.length > 1 ? '2rem 5% 4rem' : '4rem 5%',
              background: 'white'
            }}>
              {/* 섹션 헤더 */}
              <div style={{
                marginBottom: '24px',
                minHeight: '48px',
                display: 'flex',
                flexDirection: 'column' as const,
                justifyContent: 'flex-start'
              }}>
                <div style={{ fontSize: '20px', fontWeight: '700', color: '#212529', lineHeight: 1.3 }}>
                  {(() => {
                    try {
                      const url = displayUrl || (isBatchMode && selectedUrlIndex !== undefined ? batchResults[selectedUrlIndex]?.url : '');
                      if (!url) return '성능분석 리포트';

                      // 탭 라벨과 동일한 로직 사용
                      // 1. 저장된 분석 불러온 경우 → loadedUrlLabels
                      if (loadedUrlLabels && loadedUrlLabels[url]) {
                        return `${loadedUrlLabels[url]} 성능분석 리포트`;
                      }
                      // 2. 새 분석인 경우 → sessionStorage
                      const sessionLabels = sessionStorage.getItem('urlLabels');
                      if (sessionLabels) {
                        const labels = JSON.parse(sessionLabels);
                        if (labels[url]) {
                          return `${labels[url]} 성능분석 리포트`;
                        }
                      }
                      // 3. 라벨 없으면 URL에서 추출
                      const urlObj = new URL(url.startsWith('http') ? url : `https://${url}`);
                      const domain = urlObj.hostname.replace('www.', '');
                      const path = urlObj.pathname === '/' ? '' : urlObj.pathname;
                      const fullPath = domain + path;
                      const displayName = fullPath.length > 40 ? fullPath.substring(0, 37) + '...' : fullPath;
                      return `${displayName} 성능분석 리포트`;
                    } catch {
                      return '성능분석 리포트';
                    }
                  })()}
                </div>
              </div>

              {/* 4개 핵심 지표 카드 그리드 */}
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
                gap: '24px',
                marginBottom: '48px'
              }}>
                {/* 카드 1: Core Web Vitals */}
                <div style={{
                  background: '#FFFFFF',
                  borderRadius: '12px',
                  padding: '32px 48px',
                  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
                  border: '1px solid #e9ecef',
                  display: 'flex',
                  flexDirection: 'column' as const,
                  transition: 'all 0.2s ease-in-out'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.boxShadow = '0 8px 25px rgba(0, 0, 0, 0.12)';
                  e.currentTarget.style.transform = 'translateY(-2px)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.08)';
                  e.currentTarget.style.transform = 'translateY(0)';
                }}
                >
                  <div style={{
                    marginBottom: '20px',
                    minHeight: '48px',
                    display: 'flex',
                    flexDirection: 'column' as const,
                    justifyContent: 'flex-start'
                  }}>
                    <div style={{ fontSize: '16px', fontWeight: '700', color: '#212529', lineHeight: 1.3 }}>
                      핵심 지표 (Core Web Vitals)
                    </div>
                    <div style={{ fontSize: '13px', fontWeight: '400', color: '#868e96', marginTop: '6px' }}>
                      사용자 경험에 영향을 미치는 핵심 성능 지표
                    </div>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column' as const, gap: '16px', flex: 1 }}>
                    <div style={{
                      padding: '16px',
                      background: '#f8fafc',
                      borderRadius: '8px',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      border: '1px solid #e2e8f0'
                    }}>
                      <div>
                        <div style={{ fontSize: '13px', color: '#64748b', marginBottom: '4px' }}>첫 콘텐츠 표시 (FCP)</div>
                        <div style={{ fontSize: '24px', fontWeight: '700', color: designSystem.colors.primary }}>
                          {analysisResult.pageSpeedData.mobile?.firstContentfulPaint
                            ? (Math.round(analysisResult.pageSpeedData.mobile.firstContentfulPaint * 10) / 10) + 's'
                            : '—'}
                        </div>
                      </div>
                      <div style={{
                        padding: '6px 12px',
                        borderRadius: '6px',
                        fontSize: '12px',
                        fontWeight: '600',
                        background: (analysisResult.pageSpeedData.mobile?.firstContentfulPaint || 0) < 3
                          ? 'rgba(0, 184, 148, 0.15)' : 'rgba(253, 203, 110, 0.15)',
                        color: (analysisResult.pageSpeedData.mobile?.firstContentfulPaint || 0) < 3
                          ? '#00B894' : '#D69E2E'
                      }}>
                        {(analysisResult.pageSpeedData.mobile?.firstContentfulPaint || 0) < 3 ? '양호' : '개선 필요'}
                      </div>
                    </div>
                    <div style={{
                      padding: '16px',
                      background: '#f8fafc',
                      borderRadius: '8px',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      border: '1px solid #e2e8f0'
                    }}>
                      <div>
                        <div style={{ fontSize: '13px', color: '#64748b', marginBottom: '4px' }}>최대 콘텐츠 표시 (LCP)</div>
                        <div style={{ fontSize: '24px', fontWeight: '700', color: designSystem.colors.primary }}>
                          {analysisResult.pageSpeedData.mobile?.largestContentfulPaint
                            ? (Math.round(analysisResult.pageSpeedData.mobile.largestContentfulPaint * 10) / 10) + 's'
                            : '—'}
                        </div>
                      </div>
                      <div style={{
                        padding: '6px 12px',
                        borderRadius: '6px',
                        fontSize: '12px',
                        fontWeight: '600',
                        background: (analysisResult.pageSpeedData.mobile?.largestContentfulPaint || 0) < 4
                          ? 'rgba(0, 184, 148, 0.15)' : 'rgba(253, 203, 110, 0.15)',
                        color: (analysisResult.pageSpeedData.mobile?.largestContentfulPaint || 0) < 4
                          ? '#00B894' : '#D69E2E'
                      }}>
                        {(analysisResult.pageSpeedData.mobile?.largestContentfulPaint || 0) < 4 ? '양호' : '개선 필요'}
                      </div>
                    </div>
                  </div>
                  {/* 출처 도움말 */}
                  <div style={{
                    marginTop: '16px',
                    padding: '12px',
                    background: '#f8fafc',
                    borderRadius: '8px',
                    border: '1px dashed #e2e8f0'
                  }}>
                    <div style={{ fontSize: '11px', color: '#94a3b8', lineHeight: 1.5 }}>
                      <strong style={{ color: '#64748b' }}>출처:</strong> Google PageSpeed Insights API
                      <br />
                      <strong style={{ color: '#64748b' }}>기준:</strong> FCP 3초 미만, LCP 4초 미만 시 '양호' (Web.dev 권장 기준)
                    </div>
                  </div>
                </div>

                {/* 카드 2: 전체 성능 점수 */}
                <div style={{
                  background: '#FFFFFF',
                  borderRadius: '12px',
                  padding: '32px 48px',
                  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
                  border: '1px solid #e9ecef',
                  display: 'flex',
                  flexDirection: 'column' as const,
                  transition: 'all 0.2s ease-in-out'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.boxShadow = '0 8px 25px rgba(0, 0, 0, 0.12)';
                  e.currentTarget.style.transform = 'translateY(-2px)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.08)';
                  e.currentTarget.style.transform = 'translateY(0)';
                }}
                >
                  <div style={{
                    marginBottom: '20px',
                    minHeight: '48px',
                    display: 'flex',
                    flexDirection: 'column' as const,
                    justifyContent: 'flex-start'
                  }}>
                    <div style={{ fontSize: '16px', fontWeight: '700', color: '#212529', lineHeight: 1.3 }}>
                      전체 성능 점수
                    </div>
                    <div style={{ fontSize: '13px', fontWeight: '400', color: '#868e96', marginTop: '6px' }}>
                      데스크톱/모바일 통합 평가 점수
                    </div>
                  </div>
                  <div style={{
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column' as const,
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '16px'
                  }}>
                    <div style={{
                      fontSize: '56px',
                      fontWeight: '700',
                      color: customScores ? getRatingColor(customScores.overall) : designSystem.colors.primary,
                      lineHeight: 1
                    }}>
                      {customScores ? customScores.overall : '—'}
                      {customScores && (
                        <span style={{
                          fontSize: '24px',
                          marginLeft: '8px',
                          fontWeight: '600',
                          opacity: 0.8
                        }}>
                          {getRatingGrade(customScores.overall)}
                        </span>
                      )}
                    </div>
                    {customScores && (
                      <div style={{
                        display: 'flex',
                        gap: '16px',
                        width: '100%'
                      }}>
                        <div style={{
                          flex: 1,
                          padding: '12px',
                          background: '#f8fafc',
                          borderRadius: '8px',
                          textAlign: 'center' as const,
                          border: '1px solid #e2e8f0'
                        }}>
                          <div style={{ fontSize: '20px', fontWeight: '700', color: getRatingColor(customScores.desktop) }}>
                            {customScores.desktop}
                          </div>
                          <div style={{ fontSize: '12px', color: '#64748b', marginTop: '4px' }}>데스크톱</div>
                        </div>
                        <div style={{
                          flex: 1,
                          padding: '12px',
                          background: '#f8fafc',
                          borderRadius: '8px',
                          textAlign: 'center' as const,
                          border: '1px solid #e2e8f0'
                        }}>
                          <div style={{ fontSize: '20px', fontWeight: '700', color: getRatingColor(customScores.mobile) }}>
                            {customScores.mobile}
                          </div>
                          <div style={{ fontSize: '12px', color: '#64748b', marginTop: '4px' }}>모바일</div>
                        </div>
                      </div>
                    )}
                  </div>
                  {/* 출처 도움말 */}
                  <div style={{
                    marginTop: '16px',
                    padding: '12px',
                    background: '#f8fafc',
                    borderRadius: '8px',
                    border: '1px dashed #e2e8f0'
                  }}>
                    <div style={{ fontSize: '11px', color: '#94a3b8', lineHeight: 1.5 }}>
                      <strong style={{ color: '#64748b' }}>산정 방식:</strong> 자체 상대평가 시스템
                      <br />
                      <strong style={{ color: '#64748b' }}>기준:</strong> 163개 명예의전당 벤치마크 사이트 대비 상대 순위 기반 점수
                    </div>
                  </div>
                </div>

                {/* 카드 3: 페이지 크기 */}
                <div style={{
                  background: '#FFFFFF',
                  borderRadius: '12px',
                  padding: '32px 48px',
                  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
                  border: '1px solid #e9ecef',
                  display: 'flex',
                  flexDirection: 'column' as const,
                  transition: 'all 0.2s ease-in-out'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.boxShadow = '0 8px 25px rgba(0, 0, 0, 0.12)';
                  e.currentTarget.style.transform = 'translateY(-2px)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.08)';
                  e.currentTarget.style.transform = 'translateY(0)';
                }}
                >
                  <div style={{
                    marginBottom: '20px',
                    minHeight: '48px',
                    display: 'flex',
                    flexDirection: 'column' as const,
                    justifyContent: 'flex-start'
                  }}>
                    <div style={{ fontSize: '16px', fontWeight: '700', color: '#212529', lineHeight: 1.3 }}>
                      페이지 리소스 크기
                    </div>
                    <div style={{ fontSize: '13px', fontWeight: '400', color: '#868e96', marginTop: '6px' }}>
                      전체 페이지 용량 및 리소스 구성
                    </div>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column' as const, gap: '12px', flex: 1 }}>
                    <div style={{
                      padding: '16px',
                      background: '#f8fafc',
                      borderRadius: '8px',
                      border: '1px solid #e2e8f0'
                    }}>
                      <div style={{ fontSize: '13px', color: '#64748b', marginBottom: '8px' }}>데스크톱</div>
                      <div style={{ fontSize: '24px', fontWeight: '700', color: designSystem.colors.secondary }}>
                        {analysisResult.pageSpeedData.desktop?.pageSize?.totalSize
                          ? (parseInt(String(analysisResult.pageSpeedData.desktop.pageSize.totalSize)) / (1024 * 1024)).toFixed(1) + ' MB'
                          : '—'}
                      </div>
                      <div style={{ fontSize: '12px', color: '#94a3b8', marginTop: '4px' }}>
                        리소스 {analysisResult.pageSpeedData.desktop?.pageSize?.resourceCount || 0}개
                      </div>
                    </div>
                    <div style={{
                      padding: '16px',
                      background: '#f8fafc',
                      borderRadius: '8px',
                      border: '1px solid #e2e8f0'
                    }}>
                      <div style={{ fontSize: '13px', color: '#64748b', marginBottom: '8px' }}>모바일</div>
                      <div style={{ fontSize: '24px', fontWeight: '700', color: designSystem.colors.secondary }}>
                        {analysisResult.pageSpeedData.mobile?.pageSize?.totalSize
                          ? (parseInt(String(analysisResult.pageSpeedData.mobile.pageSize.totalSize)) / (1024 * 1024)).toFixed(1) + ' MB'
                          : '—'}
                      </div>
                      <div style={{ fontSize: '12px', color: '#94a3b8', marginTop: '4px' }}>
                        리소스 {analysisResult.pageSpeedData.mobile?.pageSize?.resourceCount || 0}개
                      </div>
                    </div>
                  </div>
                  {/* 출처 도움말 */}
                  <div style={{
                    marginTop: '16px',
                    padding: '12px',
                    background: '#f8fafc',
                    borderRadius: '8px',
                    border: '1px dashed #e2e8f0'
                  }}>
                    <div style={{ fontSize: '11px', color: '#94a3b8', lineHeight: 1.5 }}>
                      <strong style={{ color: '#64748b' }}>출처:</strong> Google PageSpeed Insights API
                      <br />
                      <strong style={{ color: '#64748b' }}>포함 항목:</strong> HTML, CSS, JS, 이미지, 폰트 등 모든 페이지 리소스 합계
                    </div>
                  </div>
                </div>

                {/* 카드 4: 총 차단 시간 */}
                <div style={{
                  background: '#FFFFFF',
                  borderRadius: '12px',
                  padding: '32px 48px',
                  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
                  border: '1px solid #e9ecef',
                  display: 'flex',
                  flexDirection: 'column' as const,
                  transition: 'all 0.2s ease-in-out'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.boxShadow = '0 8px 25px rgba(0, 0, 0, 0.12)';
                  e.currentTarget.style.transform = 'translateY(-2px)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.08)';
                  e.currentTarget.style.transform = 'translateY(0)';
                }}
                >
                  <div style={{
                    marginBottom: '20px',
                    minHeight: '48px',
                    display: 'flex',
                    flexDirection: 'column' as const,
                    justifyContent: 'flex-start'
                  }}>
                    <div style={{ fontSize: '16px', fontWeight: '700', color: '#212529', lineHeight: 1.3 }}>
                      총 차단 시간 (TBT)
                    </div>
                    <div style={{ fontSize: '13px', fontWeight: '400', color: '#868e96', marginTop: '6px' }}>
                      사용자 인터랙션 지연을 유발하는 차단 시간
                    </div>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column' as const, gap: '12px', flex: 1 }}>
                    <div style={{
                      padding: '16px',
                      background: '#f8fafc',
                      borderRadius: '8px',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      border: '1px solid #e2e8f0'
                    }}>
                      <div>
                        <div style={{ fontSize: '13px', color: '#64748b', marginBottom: '4px' }}>데스크톱</div>
                        <div style={{ fontSize: '24px', fontWeight: '700', color: designSystem.colors.accent }}>
                          {analysisResult.pageSpeedData.desktop?.totalBlockingTime !== undefined
                            ? analysisResult.pageSpeedData.desktop.totalBlockingTime >= 1000
                              ? (analysisResult.pageSpeedData.desktop.totalBlockingTime / 1000).toFixed(1) + 's'
                              : Math.round(analysisResult.pageSpeedData.desktop.totalBlockingTime) + 'ms'
                            : '—'}
                        </div>
                      </div>
                      <div style={{
                        padding: '6px 12px',
                        borderRadius: '6px',
                        fontSize: '12px',
                        fontWeight: '600',
                        background: (analysisResult.pageSpeedData.desktop?.totalBlockingTime || 0) < 600
                          ? 'rgba(0, 184, 148, 0.15)' : 'rgba(253, 203, 110, 0.15)',
                        color: (analysisResult.pageSpeedData.desktop?.totalBlockingTime || 0) < 600
                          ? '#00B894' : '#D69E2E'
                      }}>
                        {(analysisResult.pageSpeedData.desktop?.totalBlockingTime || 0) < 600 ? '양호' : '개선 필요'}
                      </div>
                    </div>
                    <div style={{
                      padding: '16px',
                      background: '#f8fafc',
                      borderRadius: '8px',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      border: '1px solid #e2e8f0'
                    }}>
                      <div>
                        <div style={{ fontSize: '13px', color: '#64748b', marginBottom: '4px' }}>모바일</div>
                        <div style={{ fontSize: '24px', fontWeight: '700', color: designSystem.colors.accent }}>
                          {analysisResult.pageSpeedData.mobile?.totalBlockingTime !== undefined
                            ? analysisResult.pageSpeedData.mobile.totalBlockingTime >= 1000
                              ? (analysisResult.pageSpeedData.mobile.totalBlockingTime / 1000).toFixed(1) + 's'
                              : Math.round(analysisResult.pageSpeedData.mobile.totalBlockingTime) + 'ms'
                            : '—'}
                        </div>
                      </div>
                      <div style={{
                        padding: '6px 12px',
                        borderRadius: '6px',
                        fontSize: '12px',
                        fontWeight: '600',
                        background: (analysisResult.pageSpeedData.mobile?.totalBlockingTime || 0) < 600
                          ? 'rgba(0, 184, 148, 0.15)' : 'rgba(253, 203, 110, 0.15)',
                        color: (analysisResult.pageSpeedData.mobile?.totalBlockingTime || 0) < 600
                          ? '#00B894' : '#D69E2E'
                      }}>
                        {(analysisResult.pageSpeedData.mobile?.totalBlockingTime || 0) < 600 ? '양호' : '개선 필요'}
                      </div>
                    </div>
                  </div>
                  {/* 출처 도움말 */}
                  <div style={{
                    marginTop: '16px',
                    padding: '12px',
                    background: '#f8fafc',
                    borderRadius: '8px',
                    border: '1px dashed #e2e8f0'
                  }}>
                    <div style={{ fontSize: '11px', color: '#94a3b8', lineHeight: 1.5 }}>
                      <strong style={{ color: '#64748b' }}>출처:</strong> Google PageSpeed Insights API
                      <br />
                      <strong style={{ color: '#64748b' }}>기준:</strong> 600ms 미만 시 '양호' (Web.dev 권장 기준)
                    </div>
                  </div>
                </div>
              </div>

              {/* 종합 성능 균형 분석 - 오각형 레이더 차트 섹션 */}
              {(() => {
                // 레이더 차트 데이터 계산
                const overallScore = customScores?.overall || 0;
                const fcpValue = analysisResult.pageSpeedData.mobile?.firstContentfulPaint || 0;
                const lcpValue = analysisResult.pageSpeedData.mobile?.largestContentfulPaint || 0;
                const tbtValue = analysisResult.pageSpeedData.mobile?.totalBlockingTime || 0;
                const pageSizeValue = analysisResult.pageSpeedData.mobile?.pageSize?.totalSize || 0;
                const pageSizeMB = pageSizeValue / (1024 * 1024);

                // 메트릭별 점수 계산 (0-100 스케일로 정규화)
                const fcpScore = Math.max(0, Math.min(100, 100 - (fcpValue / 5) * 100)); // 5초 이상이면 0점
                const lcpScore = Math.max(0, Math.min(100, 100 - (lcpValue / 10) * 100)); // 10초 이상이면 0점
                const tbtScore = Math.max(0, Math.min(100, 100 - (tbtValue / 3000) * 100)); // 3000ms 이상이면 0점
                const sizeScore = Math.max(0, Math.min(100, 100 - (pageSizeMB / 50) * 100)); // 50MB 이상이면 0점

                // 레이더 차트 좌표 계산 (중심: 100,100, 반경: 90)
                const center = 100;
                const maxRadius = 80;
                const angles = [
                  -90,    // 상단 (종합점수)
                  -18,    // 우상단 (FCP)
                  54,     // 우하단 (LCP)
                  126,    // 좌하단 (TBT)
                  198     // 좌상단 (페이지 용량)
                ];

                const getPoint = (angle: number, value: number) => {
                  const radius = (value / 100) * maxRadius;
                  const rad = (angle * Math.PI) / 180;
                  return {
                    x: center + radius * Math.cos(rad),
                    y: center + radius * Math.sin(rad)
                  };
                };

                const points = [
                  getPoint(angles[0], overallScore),
                  getPoint(angles[1], fcpScore),
                  getPoint(angles[2], lcpScore),
                  getPoint(angles[3], tbtScore),
                  getPoint(angles[4], sizeScore)
                ];

                const polygonPoints = points.map(p => `${p.x},${p.y}`).join(' ');

                // 외곽선 포인트
                const outerPoints = angles.map(angle => getPoint(angle, 100));
                const outerPolygon = outerPoints.map(p => `${p.x},${p.y}`).join(' ');
                const midPoints = angles.map(angle => getPoint(angle, 66));
                const midPolygon = midPoints.map(p => `${p.x},${p.y}`).join(' ');
                const innerPoints = angles.map(angle => getPoint(angle, 33));
                const innerPolygon = innerPoints.map(p => `${p.x},${p.y}`).join(' ');

                // 색상 결정 함수
                const getMetricColor = (score: number) => {
                  if (score >= 70) return '#00B894'; // 초록
                  if (score >= 40) return '#f59e0b'; // 주황
                  return '#ef4444'; // 빨강
                };

                // 문제 지표 식별
                const issues: { name: string; value: string; color: string }[] = [];
                if (lcpScore < 50) issues.push({ name: 'LCP', value: lcpValue.toFixed(1) + 's', color: '#ef4444' });
                if (sizeScore < 50) issues.push({ name: '페이지 용량', value: pageSizeMB.toFixed(1) + 'MB', color: '#ef4444' });
                if (tbtScore < 50) issues.push({ name: 'TBT', value: tbtValue >= 1000 ? (tbtValue / 1000).toFixed(1) + 's' : Math.round(tbtValue) + 'ms', color: '#f59e0b' });
                if (fcpScore < 50) issues.push({ name: 'FCP', value: fcpValue.toFixed(1) + 's', color: '#f59e0b' });

                return (
                  <section style={{
                    background: 'white',
                    borderRadius: '16px',
                    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.07)',
                    border: '1px solid #d1d5db',
                    padding: '40px 56px',
                    marginBottom: '32px',
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '48px',
                    flexWrap: 'wrap',
                    transition: 'all 0.2s ease-in-out'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.boxShadow = '0 8px 25px rgba(0, 0, 0, 0.12)';
                    e.currentTarget.style.transform = 'translateY(-2px)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.boxShadow = '0 4px 6px rgba(0, 0, 0, 0.07)';
                    e.currentTarget.style.transform = 'translateY(0)';
                  }}
                  >
                    {/* 레이더 차트 */}
                    <div style={{
                      position: 'relative',
                      flex: '1 1 auto',
                      minWidth: '300px',
                      maxWidth: '400px',
                      aspectRatio: '1/1',
                      padding: '24px'
                    }}>
                      <svg viewBox="0 0 200 200" style={{ width: '100%', height: '100%', filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.1))' }}>
                        {/* 중심에서 각 꼭지점으로 향하는 선 */}
                        {outerPoints.map((point, i) => (
                          <line key={`line-${i}`} x1={center} y1={center} x2={point.x} y2={point.y} stroke="#e2e8f0" strokeWidth="1" />
                        ))}
                        {/* 외곽선 다각형들 */}
                        <polygon points={outerPolygon} fill="none" stroke="#e2e8f0" strokeWidth="1" />
                        <polygon points={midPolygon} fill="none" stroke="#f1f5f9" strokeWidth="1" />
                        <polygon points={innerPolygon} fill="none" stroke="#f8fafc" strokeWidth="1" />
                        {/* 데이터 다각형 */}
                        <polygon
                          points={polygonPoints}
                          fill="rgba(79, 70, 229, 0.2)"
                          stroke="#4f46e5"
                          strokeWidth="2"
                        />
                        {/* 데이터 포인트 */}
                        {points.map((point, i) => {
                          const scores = [overallScore, fcpScore, lcpScore, tbtScore, sizeScore];
                          return (
                            <circle
                              key={`point-${i}`}
                              cx={point.x}
                              cy={point.y}
                              r="4"
                              fill={getMetricColor(scores[i])}
                            />
                          );
                        })}
                      </svg>
                      {/* 라벨들 */}
                      <span style={{
                        position: 'absolute',
                        top: '-4px',
                        left: '50%',
                        transform: 'translateX(-50%)',
                        fontSize: '11px',
                        fontWeight: '700',
                        color: '#4f46e5',
                        background: 'white',
                        padding: '0 4px'
                      }}>
                        종합 점수 ({overallScore})
                      </span>
                      <span style={{
                        position: 'absolute',
                        top: '30%',
                        right: '-12px',
                        fontSize: '11px',
                        fontWeight: '700',
                        color: getMetricColor(fcpScore)
                      }}>
                        FCP
                      </span>
                      <span style={{
                        position: 'absolute',
                        bottom: '10%',
                        right: '8px',
                        fontSize: '11px',
                        fontWeight: '700',
                        color: getMetricColor(lcpScore)
                      }}>
                        LCP
                      </span>
                      <span style={{
                        position: 'absolute',
                        bottom: '10%',
                        left: '8px',
                        fontSize: '11px',
                        fontWeight: '700',
                        color: getMetricColor(tbtScore)
                      }}>
                        TBT
                      </span>
                      <span style={{
                        position: 'absolute',
                        top: '30%',
                        left: '-32px',
                        fontSize: '11px',
                        fontWeight: '700',
                        color: getMetricColor(sizeScore)
                      }}>
                        페이지 용량
                      </span>
                    </div>

                    {/* 분석 코멘트 + 명예의전당 비교 */}
                    <div style={{ flex: '1 1 auto', minWidth: '300px', maxWidth: '480px', display: 'flex', flexDirection: 'column', gap: '20px' }}>
                      {/* 종합 분석 */}
                      <div>
                        <h3 style={{
                          fontSize: '18px',
                          fontWeight: '700',
                          color: '#1f2937',
                          margin: '0 0 12px 0'
                        }}>
                          종합 성능 균형 분석
                        </h3>
                        <p style={{
                          fontSize: '14px',
                          color: '#4b5563',
                          lineHeight: '1.6',
                          margin: 0
                        }}>
                          종합 점수는 <span style={{ fontWeight: '700', color: '#4f46e5' }}>{overallScore}점</span>
                          {issues.length > 0 ? (
                            <>
                              이나, {issues.slice(0, 2).map((issue, i) => (
                                <span key={i}>
                                  {i > 0 && '와 '}
                                  <span style={{
                                    color: issue.color,
                                    fontWeight: '700',
                                    background: issue.color === '#ef4444' ? 'rgba(239, 68, 68, 0.1)' : 'rgba(245, 158, 11, 0.1)',
                                    padding: '2px 6px',
                                    borderRadius: '4px'
                                  }}>
                                    {issue.name}({issue.value})
                                  </span>
                                </span>
                              ))}
                              {issues.length > 2 ? ' 등이 ' : '이(가) '}
                              성능 불균형을 초래하고 있습니다.
                            </>
                          ) : (
                            <>으로, 전반적으로 균형 잡힌 성능을 보여주고 있습니다.</>
                          )}
                        </p>
                      </div>

                      {/* 명예의전당 비교 버튼 */}
                      <button
                        onClick={() => setShowHallOfFame(true)}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: '10px',
                          padding: '14px 24px',
                          background: 'linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)',
                          color: 'white',
                          border: 'none',
                          borderRadius: '12px',
                          fontSize: '15px',
                          fontWeight: '600',
                          cursor: 'pointer',
                          transition: 'all 0.2s ease',
                          boxShadow: '0 4px 14px rgba(79, 70, 229, 0.3)'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.transform = 'translateY(-2px)';
                          e.currentTarget.style.boxShadow = '0 6px 20px rgba(79, 70, 229, 0.4)';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.transform = 'translateY(0)';
                          e.currentTarget.style.boxShadow = '0 4px 14px rgba(79, 70, 229, 0.3)';
                        }}
                      >
                        <svg style={{ width: '20px', height: '20px' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                        </svg>
                        업계 상위 사이트와 비교하기
                      </button>

                      {/* 간단한 비교 프리뷰 */}
                      {(() => {
                        // 순위 계산 (점수 기반 추정: 163개 사이트 중)
                        const totalSites = 163;
                        const percentile = Math.max(1, Math.round(100 - overallScore));
                        const estimatedRank = Math.max(1, Math.round((percentile / 100) * totalSites));
                        const beatCount = totalSites - estimatedRank;
                        // 실제 벤치마크 데이터에서 평균 계산 (유효한 점수만 필터링, 문자열→숫자 변환)
                        const validSites = hallOfFameBenchmarks.filter(site => {
                          const score = parseFloat(site.overall_rating);
                          return !isNaN(score) && score > 0;
                        });
                        const industryAvg = validSites.length > 0
                          ? Math.round(validSites.reduce((sum, site) => sum + parseFloat(site.overall_rating), 0) / validSites.length)
                          : 65; // 폴백
                        console.log('📊 업계 평균 계산:', { total: hallOfFameBenchmarks.length, valid: validSites.length, avg: industryAvg });
                        const diffFromAvg = overallScore - industryAvg;

                        return (
                          <div style={{
                            background: '#f8fafc',
                            borderRadius: '12px',
                            padding: '16px',
                            border: '1px solid #e2e8f0',
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '14px'
                          }}>
                            {/* 1. 순위 표시 */}
                            <div style={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center'
                            }}>
                              <span style={{ fontSize: '13px', color: '#64748b' }}>163개 쇼핑몰 중</span>
                              <span style={{
                                fontSize: '18px',
                                fontWeight: '800',
                                color: '#4f46e5'
                              }}>
                                {estimatedRank}위 <span style={{ fontSize: '13px', fontWeight: '500', color: '#94a3b8' }}>(상위 {percentile}%)</span>
                              </span>
                            </div>

                            {/* 2. 미니 프로그레스바 - 순위 위치 시각화 */}
                            <div style={{ position: 'relative' }}>
                              <div style={{
                                height: '6px',
                                background: 'linear-gradient(to right, #10b981 0%, #f59e0b 50%, #ef4444 100%)',
                                borderRadius: '3px',
                                opacity: 0.3
                              }} />
                              <div style={{
                                position: 'absolute',
                                left: `${percentile}%`,
                                top: '3px',
                                transform: 'translate(-50%, -50%)',
                                width: '14px',
                                height: '14px',
                                background: '#4f46e5',
                                borderRadius: '50%',
                                border: '2px solid white',
                                boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                              }} />
                              {/* 좌우 레이블 */}
                              <div style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                marginTop: '4px',
                                fontSize: '10px',
                                color: '#94a3b8'
                              }}>
                                <span>1위</span>
                                <span>163위</span>
                              </div>
                            </div>

                            {/* 3. 업계 평균 대비 */}
                            <div style={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                              padding: '10px 12px',
                              background: 'white',
                              borderRadius: '8px',
                              border: '1px solid #e2e8f0'
                            }}>
                              <span style={{ fontSize: '13px', color: '#64748b' }}>
                                업계 평균({industryAvg}점) 대비
                              </span>
                              <span style={{
                                fontSize: '15px',
                                fontWeight: '700',
                                color: diffFromAvg >= 0 ? '#10b981' : '#ef4444'
                              }}>
                                {diffFromAvg >= 0 ? '+' : ''}{diffFromAvg}점
                              </span>
                            </div>

                            {/* 4. 이기는 사이트 수 */}
                            <div style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '8px',
                              fontSize: '13px',
                              color: '#64748b'
                            }}>
                              <svg style={{ width: '16px', height: '16px', color: '#10b981' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                              </svg>
                              <span>
                                <strong style={{ color: '#10b981', fontWeight: '700' }}>{beatCount}개</strong>의 카페24 주요 사이트보다 빠름
                              </span>
                            </div>
                          </div>
                        );
                      })()}
                    </div>
                  </section>
                );
              })()}

              {/* AI 분석 섹션 - 우수한 점만 갤러리 카드로 표시 */}
              {analysisResult.llmAnalysis && (() => {
                // LLM 분석에서 "우수한 점" 섹션만 추출
                const extractGoodPoints = (analysis: string) => {
                  if (!analysis || typeof analysis !== 'string') return [];

                  // "우수한 점" 섹션 찾기
                  const goodPointsMatch = analysis.match(/###\s*1\.\s*[✅✓☑]?\s*\*?\*?우수한\s*점\*?\*?([\s\S]*?)(?=###\s*2\.|$)/i);
                  if (!goodPointsMatch) return [];

                  const goodPointsText = goodPointsMatch[1];

                  // 불릿 포인트 추출
                  const points: string[] = [];
                  const lines = goodPointsText.split('\n');

                  for (const line of lines) {
                    const trimmed = line.trim();
                    // - 로 시작하는 라인만 추출
                    if (trimmed.startsWith('-')) {
                      const pointText = trimmed.replace(/^-\s*/, '').trim();
                      if (pointText.length > 0) {
                        points.push(pointText);
                      }
                    }
                  }

                  return points;
                };

                const analysisText = typeof analysisResult.llmAnalysis === 'string'
                  ? analysisResult.llmAnalysis
                  : String(analysisResult.llmAnalysis);

                const goodPoints = extractGoodPoints(analysisText);

                // 우수한 점이 없으면 표시하지 않음
                if (goodPoints.length === 0) return null;

                // 포인트별 아이콘/색상 매핑
                // 제외할 항목 필터링 (메타 설명, 구조화 데이터)
                const shouldExclude = (point: string) => {
                  const excludeKeywords = ['메타', '구조화', 'JSON-LD', '스키마', 'Schema'];
                  return excludeKeywords.some(keyword => point.includes(keyword));
                };

                const getPointStyle = (point: string) => {
                  // 더 구체적인 매칭 우선
                  if (point.includes('제목') && point.includes('페이지')) {
                    return { icon: 'search', color: '#3b82f6', bg: '#eff6ff', label: '페이지 제목' };
                  }
                  if (point.includes('Analytics') || point.includes('GA') || point.includes('트래픽')) {
                    return { icon: 'chart', color: '#f59e0b', bg: '#fffbeb', label: '분석 도구' };
                  }
                  if (point.includes('용량') || point.includes('MB') || point.includes('KB') || point.includes('가벼')) {
                    return { icon: 'speed', color: '#10b981', bg: '#ecfdf5', label: '페이지 용량' };
                  }
                  if (point.includes('로딩') || point.includes('속도') || point.includes('빠른')) {
                    return { icon: 'speed', color: '#0ea5e9', bg: '#f0f9ff', label: '로딩 속도' };
                  }
                  if (point.includes('HTTP') || point.includes('요청') || point.includes('리소스')) {
                    return { icon: 'network', color: '#6366f1', bg: '#eef2ff', label: '네트워크 효율' };
                  }
                  if (point.includes('폰트') || point.includes('FOUT') || point.includes('텍스트')) {
                    return { icon: 'font', color: '#ec4899', bg: '#fdf2f8', label: '폰트 최적화' };
                  }
                  if (point.includes('보안') || point.includes('HTTPS') || point.includes('SSL')) {
                    return { icon: 'shield', color: '#22c55e', bg: '#f0fdf4', label: '보안 설정' };
                  }
                  if (point.includes('이미지') || point.includes('압축') || point.includes('최적화')) {
                    return { icon: 'image', color: '#8b5cf6', bg: '#f5f3ff', label: '이미지 최적화' };
                  }
                  if (point.includes('캐시') || point.includes('cache')) {
                    return { icon: 'cache', color: '#14b8a6', bg: '#f0fdfa', label: '캐싱 설정' };
                  }
                  if (point.includes('검색') || point.includes('SEO')) {
                    return { icon: 'search', color: '#3b82f6', bg: '#eff6ff', label: '검색 최적화' };
                  }
                  return { icon: 'check', color: '#10b981', bg: '#ecfdf5', label: '성능 우수' };
                };

                // SVG 아이콘 렌더링
                const renderIcon = (iconType: string, color: string) => {
                  switch (iconType) {
                    case 'search':
                      return <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />;
                    case 'tag':
                      return <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />;
                    case 'chart':
                      return <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />;
                    case 'code':
                      return <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />;
                    case 'speed':
                      return <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />;
                    case 'network':
                      return <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9" />;
                    case 'font':
                      return <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5h12M9 3v2m1.048 9.5A18.022 18.022 0 016.412 9m6.088 9h7M11 21l5-10 5 10M12.751 5C11.783 10.77 8.07 15.61 3 18.129" />;
                    case 'shield':
                      return <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />;
                    case 'image':
                      return <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />;
                    case 'cache':
                      return <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582-4 8-4s8 1.79 8 4m0 5c0 2.21-3.582 4-8 4s-8-1.79-8-4" />;
                    default:
                      return <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />;
                  }
                };

                return (
                  <div style={{ marginBottom: '48px' }}>
                    {/* 섹션 헤더 */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '24px' }}>
                      <span style={{
                        background: '#10b981',
                        color: 'white',
                        fontSize: '12px',
                        fontWeight: '600',
                        padding: '4px 12px',
                        borderRadius: '4px'
                      }}>
                        Excellent
                      </span>
                      <h3 style={{ fontSize: '20px', fontWeight: '700', color: '#1f2937', margin: 0 }}>
                        최적화 성공 요소
                      </h3>
                    </div>

                    {/* 심플 카드 그리드 */}
                    <div style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
                      gap: '24px'
                    }}>
                      {goodPoints.filter(point => !shouldExclude(point)).map((point, index) => {
                        const style = getPointStyle(point);

                        return (
                          <div
                            key={index}
                            style={{
                              background: '#ffffff',
                              borderRadius: '20px',
                              border: `2px solid ${style.color}35`,
                              overflow: 'hidden',
                              transition: 'all 0.25s ease',
                              boxShadow: `0 4px 12px ${style.color}12, 0 1px 3px rgba(0,0,0,0.04)`,
                              display: 'flex',
                              flexDirection: 'column'
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.borderColor = `${style.color}60`;
                              e.currentTarget.style.boxShadow = `0 12px 32px ${style.color}20, 0 4px 8px rgba(0,0,0,0.06)`;
                              e.currentTarget.style.transform = 'translateY(-4px)';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.borderColor = `${style.color}35`;
                              e.currentTarget.style.boxShadow = `0 4px 12px ${style.color}12, 0 1px 3px rgba(0,0,0,0.04)`;
                              e.currentTarget.style.transform = 'translateY(0)';
                            }}
                          >
                            {/* 상단 영역 (70%): 심볼 배경 + 짧은 제목 */}
                            <div style={{
                              flex: 7,
                              padding: '32px 28px 24px 28px',
                              display: 'flex',
                              flexDirection: 'column',
                              justifyContent: 'flex-end',
                              minHeight: '160px',
                              position: 'relative',
                              overflow: 'hidden',
                              background: `linear-gradient(145deg, ${style.color}15 0%, ${style.color}06 100%)`
                            }}>
                              {/* 심볼 - 상단 전체 영역 배경처럼 */}
                              <svg
                                style={{
                                  position: 'absolute',
                                  top: '42%',
                                  left: '50%',
                                  transform: 'translate(-50%, -50%)',
                                  width: '110px',
                                  height: '110px',
                                  color: style.color,
                                  opacity: 0.28
                                }}
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="1"
                                viewBox="0 0 24 24"
                              >
                                {renderIcon(style.icon, style.color)}
                              </svg>

                              {/* 짧은 제목 (좌측 하단) - 아이콘 색상 적용 */}
                              <h4 style={{
                                fontSize: '20px',
                                fontWeight: '700',
                                color: style.color,
                                margin: 0,
                                lineHeight: 1.3,
                                position: 'relative',
                                zIndex: 1,
                                textShadow: '0 1px 2px rgba(255,255,255,0.9)'
                              }}>
                                {style.label}
                              </h4>
                            </div>

                            {/* 하단 영역 (30%): 전체 설명 */}
                            <div style={{
                              flex: 3,
                              padding: '18px 28px 22px 28px',
                              borderTop: `1px solid ${style.color}20`,
                              background: '#f8fafc'
                            }}>
                              <p style={{
                                fontSize: '14px',
                                color: '#475569',
                                margin: 0,
                                lineHeight: 1.7
                              }}>
                                {point}
                              </p>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                    {/* 출처 도움말 */}
                    <div style={{
                      marginTop: '24px',
                      padding: '12px 16px',
                      background: '#f8fafc',
                      borderRadius: '8px',
                      border: '1px dashed #e2e8f0'
                    }}>
                      <div style={{ fontSize: '11px', color: '#94a3b8', lineHeight: 1.5 }}>
                        AI를 통해 HTML 구조, SEO, 접근성, 성능 최적화 요소를 국내 주요 쇼핑몰 기준으로 분석했습니다.
                      </div>
                    </div>
                  </div>
                );
              })()}

              {/* 핵심 성능 개선 기회 - 통합 섹션 (아코디언 + 리소스 상세) */}
              {(() => {
                const desktopOpps = analysisResult.pageSpeedData?.desktop?.opportunities
                  ? getImportantOpportunities(analysisResult.pageSpeedData.desktop.opportunities)
                  : [];
                const mobileOpps = analysisResult.pageSpeedData?.mobile?.opportunities
                  ? getImportantOpportunities(analysisResult.pageSpeedData.mobile.opportunities)
                  : [];

                if (desktopOpps.length === 0 && mobileOpps.length === 0) {
                  return null;
                }

                const currentOpps = opportunitiesTab === 'desktop' ? desktopOpps : mobileOpps;
                const topIssues = currentOpps.slice(0, 2);
                const restIssues = currentOpps.slice(2);

                // 총 절약 가능 시간 계산
                const totalSavings = currentOpps.reduce((sum: number, opp: any) => {
                  return sum + (opp.numericValue || 0);
                }, 0);

                // 아코디언 토글 함수
                const toggleOpportunity = (oppId: string) => {
                  setExpandedOpportunities(prev => ({
                    ...prev,
                    [oppId]: !prev[oppId]
                  }));
                };

                // 리소스 필터링 함수 (optimizer.php 제외)
                const filterResources = (details: any) => {
                  if (!details?.items) return [];
                  const excludePatterns = ['optimizer.php'];
                  return details.items.filter((item: any) => {
                    const url = item.url || item.source || item.node?.snippet || '';
                    return !excludePatterns.some(pattern => url.includes(pattern));
                  });
                };

                // URL 축약 함수 (앞부분 도메인 표시, 뒷부분 생략)
                const truncateUrl = (url: string, maxLength: number = 150) => {
                  if (!url) return 'N/A';
                  const cleanUrl = url.replace(/^https?:\/\//, '');
                  if (cleanUrl.length <= maxLength) return cleanUrl;
                  return cleanUrl.substring(0, maxLength) + '...';
                };

                return (
                  <div style={{
                    background: 'white',
                    borderRadius: '24px',
                    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.07)',
                    border: '1px solid #d1d5db',
                    overflow: 'hidden',
                    marginBottom: '32px',
                    transition: 'all 0.2s ease-in-out'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.boxShadow = '0 8px 25px rgba(0, 0, 0, 0.12)';
                    e.currentTarget.style.transform = 'translateY(-2px)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.boxShadow = '0 4px 6px rgba(0, 0, 0, 0.07)';
                    e.currentTarget.style.transform = 'translateY(0)';
                  }}
                  >
                    {/* 통합 헤더 */}
                    <div style={{
                      padding: '24px 32px',
                      borderBottom: '1px solid #e5e7eb',
                      display: 'flex',
                      flexWrap: 'wrap' as const,
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      gap: '16px',
                      background: '#f3f4f6'
                    }}>
                      <div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '4px' }}>
                          <span style={{
                            background: 'rgba(239, 68, 68, 0.1)',
                            color: '#ef4444',
                            fontSize: '11px',
                            fontWeight: '700',
                            padding: '3px 10px',
                            borderRadius: '9999px',
                            border: '1px solid rgba(239, 68, 68, 0.2)'
                          }}>
                            Action Required
                          </span>
                          <h2 style={{ fontSize: '18px', fontWeight: '700', color: '#1f2937', margin: 0 }}>
                            핵심 성능 개선 기회
                          </h2>
                        </div>
                        <p style={{ fontSize: '13px', color: '#6b7280', margin: 0 }}>
                          아래 항목을 최적화하면 <span style={{ fontWeight: '700', color: '#2563eb' }}>
                            약 {(totalSavings / 1000).toFixed(1)}초
                          </span> 로딩 속도 개선이 가능합니다.
                        </p>
                      </div>
                      {/* 탭 버튼 */}
                      <div style={{
                        background: '#e5e7eb',
                        padding: '4px',
                        borderRadius: '8px',
                        display: 'flex',
                        fontSize: '14px',
                        fontWeight: '600'
                      }}>
                        <button
                          onClick={() => setOpportunitiesTab('desktop')}
                          style={{
                            padding: '8px 20px',
                            borderRadius: '6px',
                            border: 'none',
                            background: opportunitiesTab === 'desktop' ? 'white' : 'transparent',
                            color: opportunitiesTab === 'desktop' ? '#2563eb' : '#6b7280',
                            boxShadow: opportunitiesTab === 'desktop' ? '0 2px 4px rgba(0,0,0,0.1)' : 'none',
                            cursor: 'pointer',
                            transition: 'all 0.2s ease'
                          }}
                        >
                          데스크톱 ({desktopOpps.length})
                        </button>
                        <button
                          onClick={() => setOpportunitiesTab('mobile')}
                          style={{
                            padding: '8px 20px',
                            borderRadius: '6px',
                            border: 'none',
                            background: opportunitiesTab === 'mobile' ? 'white' : 'transparent',
                            color: opportunitiesTab === 'mobile' ? '#2563eb' : '#6b7280',
                            boxShadow: opportunitiesTab === 'mobile' ? '0 2px 4px rgba(0,0,0,0.1)' : 'none',
                            cursor: 'pointer',
                            transition: 'all 0.2s ease'
                          }}
                        >
                          모바일 ({mobileOpps.length})
                        </button>
                      </div>
                    </div>

                    {/* 컨텐츠 영역 */}
                    <div style={{ padding: '24px 32px' }}>
                      {/* 상위 2개: 2분할 카드 스타일 (아코디언) */}
                      {topIssues.length > 0 && (
                        <div style={{ display: 'flex', flexDirection: 'column' as const, gap: '16px', marginBottom: restIssues.length > 0 ? '24px' : 0 }}>
                          {topIssues.map((opportunity: any, index: number) => {
                            const isCritical = index === 0;
                            const borderColor = isCritical ? '#fca5a5' : '#fdba74';
                            const badgeBg = isCritical ? 'rgba(239, 68, 68, 0.1)' : 'rgba(249, 115, 22, 0.1)';
                            const badgeColor = isCritical ? '#ef4444' : '#f97316';
                            const badgeText = isCritical ? 'Critical' : 'Moderate';
                            const oppId = `${opportunitiesTab}-top-${index}`;
                            const isExpanded = expandedOpportunities[oppId] ?? (index === 0); // 첫번째는 기본 펼침
                            const filteredResources = filterResources(opportunity.details);
                            const hasResources = filteredResources.length > 0;

                            return (
                              <div key={index} style={{
                                background: 'white',
                                borderRadius: '12px',
                                boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)',
                                border: `1px solid ${borderColor}`,
                                overflow: 'hidden'
                              }}>
                                {/* 카드 헤더 (클릭 가능) */}
                                <div
                                  onClick={() => hasResources && toggleOpportunity(oppId)}
                                  style={{
                                    display: 'flex',
                                    flexWrap: 'wrap' as const,
                                    cursor: hasResources ? 'pointer' : 'default'
                                  }}
                                >
                                  {/* 좌측: 문제 설명 */}
                                  <div style={{
                                    flex: '1 1 320px',
                                    padding: '20px',
                                    borderRight: '1px solid #e5e7eb'
                                  }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                                      <span style={{
                                        background: badgeBg,
                                        color: badgeColor,
                                        fontSize: '10px',
                                        fontWeight: '700',
                                        padding: '2px 8px',
                                        borderRadius: '4px'
                                      }}>
                                        {badgeText}
                                      </span>
                                      <h4 style={{ fontWeight: '700', color: '#1f2937', margin: 0, fontSize: '14px' }}>
                                        {translateOpportunityTitle(opportunity.title)}
                                      </h4>
                                      {hasResources && (
                                        <svg
                                          style={{
                                            width: '16px',
                                            height: '16px',
                                            color: '#9ca3af',
                                            transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                                            transition: 'transform 0.2s ease'
                                          }}
                                          fill="none"
                                          stroke="currentColor"
                                          viewBox="0 0 24 24"
                                        >
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                        </svg>
                                      )}
                                    </div>
                                    <p style={{ fontSize: '13px', color: '#4b5563', margin: 0, lineHeight: 1.6 }}
                                       dangerouslySetInnerHTML={{ __html: translateDescription(opportunity.description) }} />
                                    <div style={{ marginTop: '12px', fontSize: '12px', color: '#6b7280' }}>
                                      잠재적 절약: <span style={{ color: badgeColor, fontWeight: '700' }}>
                                        {opportunity.numericValue ? `${(opportunity.numericValue / 1000).toFixed(1)}s` : opportunity.displayValue || 'N/A'}
                                      </span>
                                      {hasResources && (
                                        <span style={{ marginLeft: '8px', color: '#9ca3af' }}>
                                          ({filteredResources.length}개 리소스)
                                        </span>
                                      )}
                                    </div>
                                  </div>

                                  {/* 우측: 코드 힌트 + 버튼 */}
                                  <div style={{
                                    flex: '1 1 280px',
                                    padding: '20px',
                                    background: '#f9fafb',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    gap: '12px',
                                    flexWrap: 'wrap' as const
                                  }}>
                                    <div style={{
                                      background: 'white',
                                      border: '1px solid #d1d5db',
                                      borderRadius: '4px',
                                      padding: '6px 10px'
                                    }}>
                                      <code style={{ fontSize: '12px', color: '#4f46e5', fontFamily: 'monospace' }}>
                                        {opportunity.title.includes('JavaScript') ? '<script defer ...>' :
                                         opportunity.title.includes('CSS') ? 'PurgeCSS / Minify' :
                                         opportunity.title.includes('image') || opportunity.title.includes('Image') ? 'WebP / AVIF format' :
                                         opportunity.title.includes('font') || opportunity.title.includes('Font') ? 'font-display: swap' :
                                         'Optimize resource'}
                                      </code>
                                    </div>
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        // 한글 가이드 생성 (실제 분석 데이터 기반)
                                        const title = translateOpportunityTitle(opportunity.title);
                                        const description = translateDescription(opportunity.description);
                                        const savingsTime = opportunity.numericValue ? `${(opportunity.numericValue / 1000).toFixed(1)}초` : null;
                                        const totalWastedBytes = filteredResources.reduce((sum: number, item: any) => sum + (item.wastedBytes || 0), 0);
                                        const savingsSize = totalWastedBytes > 0 ? `${(totalWastedBytes / 1024).toFixed(0)} KB` : null;

                                        // 권장 조치사항 매핑
                                        const actionMap: Record<string, string[]> = {
                                          'JavaScript': ['script 태그에 defer 또는 async 속성 추가', '미사용 코드 분석 후 제거 (Tree shaking)', '필요한 시점에만 로드하도록 동적 import 적용'],
                                          'CSS': ['사용하지 않는 CSS 규칙 제거 (PurgeCSS 권장)', 'CSS 파일 압축 (Minify) 적용', '중요 CSS는 인라인으로, 나머지는 지연 로드'],
                                          'image': ['WebP 또는 AVIF 형식으로 변환', '적절한 이미지 크기로 리사이즈', 'lazy loading 적용 (loading="lazy")'],
                                          'Image': ['WebP 또는 AVIF 형식으로 변환', '적절한 이미지 크기로 리사이즈', 'lazy loading 적용 (loading="lazy")'],
                                          'font': ['font-display: swap 속성 적용', '필요한 글자만 서브셋으로 추출', 'preload로 폰트 미리 로드'],
                                          'Font': ['font-display: swap 속성 적용', '필요한 글자만 서브셋으로 추출', 'preload로 폰트 미리 로드'],
                                          'render-blocking': ['critical CSS 인라인화', 'script에 defer/async 속성 추가', '불필요한 외부 리소스 지연 로드'],
                                          'cache': ['정적 리소스에 Cache-Control 헤더 설정', '파일명에 해시 추가하여 장기 캐싱 적용', 'CDN 활용 권장'],
                                          'server': ['서버 응답 시간 최적화', 'HTTP/2 또는 HTTP/3 적용', 'CDN 활용으로 지연시간 단축']
                                        };

                                        // 제목에서 키워드 매칭하여 권장사항 결정
                                        let actions = ['해당 리소스 최적화 검토', '불필요한 요소 제거', '성능 테스트 후 배포'];
                                        for (const [keyword, tips] of Object.entries(actionMap)) {
                                          if (opportunity.title.includes(keyword) || title.includes(keyword)) {
                                            actions = tips;
                                            break;
                                          }
                                        }

                                        // 리소스 목록 생성 (상위 5개)
                                        const resourceList = filteredResources.slice(0, 5).map((item: any, idx: number) => {
                                          const url = item.url || item.node?.snippet || 'N/A';
                                          const shortUrl = url.replace(/^https?:\/\//, '').substring(0, 60) + (url.length > 60 ? '...' : '');
                                          const waste = item.wastedBytes ? `${(item.wastedBytes / 1024).toFixed(0)} KB 절감 가능` :
                                                        item.wastedMs ? `${(item.wastedMs / 1000).toFixed(1)}초 절감 가능` : '';
                                          return `${idx + 1}. ${shortUrl}${waste ? ` (${waste})` : ''}`;
                                        }).join('\n');

                                        // 가이드 텍스트 조합
                                        const guideText = `━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
${isCritical ? '🔴' : '🟠'} [${badgeText === 'Critical' ? '긴급' : '보통'}] ${title}
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

■ 문제 설명
${description.replace(/<[^>]*>/g, '')}

■ 예상 효과${savingsTime ? `\n- 절감 가능 시간: 약 ${savingsTime}` : ''}${savingsSize ? `\n- 절감 가능 용량: 약 ${savingsSize}` : ''}

■ 개선이 필요한 리소스 (${filteredResources.length}개${filteredResources.length > 5 ? ' 중 상위 5개' : ''})
${resourceList || '- 상세 리소스 정보 없음'}

■ 권장 조치사항
${actions.map(a => `- ${a}`).join('\n')}

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
※ 이 가이드는 Google PageSpeed Insights API 분석 결과를 기반으로 생성되었습니다.
※ 분석 대상: ${displayUrl}
※ 생성 일시: ${new Date().toLocaleString('ko-KR')}
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━`;

                                        navigator.clipboard.writeText(guideText);
                                        alert('작업 가이드가 클립보드에 복사되었습니다.\n\n개발팀에게 전달하여 성능 개선을 요청하세요.');
                                      }}
                                      style={{
                                        fontSize: '12px',
                                        border: '1px solid #d1d5db',
                                        background: 'white',
                                        padding: '8px 14px',
                                        borderRadius: '6px',
                                        cursor: 'pointer',
                                        fontWeight: '500',
                                        color: '#374151'
                                      }}
                                    >
                                      가이드 복사
                                    </button>
                                  </div>
                                </div>

                                {/* 리소스 상세 (아코디언 펼침 시) */}
                                {isExpanded && hasResources && (
                                  <div style={{
                                    borderTop: '1px solid #e5e7eb',
                                    background: '#fafafa'
                                  }}>
                                    {/* 테이블 헤더 */}
                                    <div style={{
                                      display: 'grid',
                                      gridTemplateColumns: '1fr 100px 100px',
                                      padding: '12px 20px',
                                      background: '#f3f4f6',
                                      fontSize: '11px',
                                      fontWeight: '600',
                                      color: '#6b7280',
                                      borderBottom: '1px solid #e5e7eb'
                                    }}>
                                      <span>URL</span>
                                      <span style={{ textAlign: 'right' }}>크기</span>
                                      <span style={{ textAlign: 'right', color: '#ef4444' }}>절감 가능</span>
                                    </div>
                                    {/* 리소스 목록 (최대 5개) */}
                                    {filteredResources.slice(0, 5).map((item: any, itemIndex: number) => (
                                      <div
                                        key={itemIndex}
                                        style={{
                                          display: 'grid',
                                          gridTemplateColumns: '1fr 100px 100px',
                                          padding: '10px 20px',
                                          borderBottom: '1px solid #f3f4f6',
                                          fontSize: '12px',
                                          alignItems: 'center'
                                        }}
                                      >
                                        <a
                                          href={item.url || '#'}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          style={{
                                            color: '#4f46e5',
                                            fontFamily: 'Consolas, Monaco, monospace',
                                            textDecoration: 'none',
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            whiteSpace: 'nowrap' as const
                                          }}
                                          title={item.url || item.node?.snippet}
                                        >
                                          {truncateUrl(item.url || item.node?.snippet || '')}
                                        </a>
                                        <span style={{ textAlign: 'right', color: '#6b7280' }}>
                                          {item.totalBytes ? `${(item.totalBytes / 1024).toFixed(0)} KB` : '-'}
                                        </span>
                                        <span style={{ textAlign: 'right', fontWeight: '600', color: '#ef4444' }}>
                                          {item.wastedBytes ? `${(item.wastedBytes / 1024).toFixed(0)} KB` :
                                           item.wastedMs ? `${(item.wastedMs / 1000).toFixed(1)}s` : '-'}
                                        </span>
                                      </div>
                                    ))}
                                    {/* 더보기 버튼 */}
                                    {filteredResources.length > 5 && (
                                      <div style={{
                                        padding: '12px 20px',
                                        textAlign: 'center',
                                        borderTop: '1px solid #e5e7eb'
                                      }}>
                                        <button
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            openResourcesInNewWindow(
                                              opportunity.details,
                                              oppId,
                                              translateOpportunityTitle(opportunity.title),
                                              opportunity.description
                                            );
                                          }}
                                          style={{
                                            background: '#2563eb',
                                            color: 'white',
                                            border: 'none',
                                            padding: '8px 16px',
                                            borderRadius: '6px',
                                            fontSize: '12px',
                                            fontWeight: '600',
                                            cursor: 'pointer'
                                          }}
                                        >
                                          ... 외 {filteredResources.length - 5}개 항목 새창으로 보기
                                        </button>
                                      </div>
                                    )}
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      )}

                      {/* 나머지 항목: 컴팩트 행 (클릭시 펼침) */}
                      {restIssues.length > 0 && (
                        <div style={{
                          borderTop: topIssues.length > 0 ? '1px solid #e5e7eb' : 'none',
                          paddingTop: topIssues.length > 0 ? '24px' : 0
                        }}>
                          <div style={{ fontSize: '13px', fontWeight: '600', color: '#6b7280', marginBottom: '12px' }}>
                            추가 개선 항목 ({restIssues.length})
                          </div>
                          <div style={{ display: 'flex', flexDirection: 'column' as const, gap: '8px' }}>
                            {restIssues.map((opportunity: any, index: number) => {
                              const actualIndex = index + 2;
                              const dotColor = actualIndex < 4 ? '#eab308' : '#9ca3af';
                              const oppId = `${opportunitiesTab}-rest-${index}`;
                              const isExpanded = expandedOpportunities[oppId] || false;
                              const filteredResources = filterResources(opportunity.details);
                              const hasResources = filteredResources.length > 0;

                              return (
                                <div key={index} style={{
                                  background: '#f9fafb',
                                  borderRadius: '8px',
                                  border: '1px solid #e5e7eb',
                                  overflow: 'hidden'
                                }}>
                                  {/* 행 헤더 */}
                                  <div
                                    onClick={() => hasResources && toggleOpportunity(oppId)}
                                    style={{
                                      padding: '12px 16px',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'space-between',
                                      gap: '12px',
                                      cursor: hasResources ? 'pointer' : 'default'
                                    }}
                                  >
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flex: 1 }}>
                                      <span style={{
                                        width: '8px',
                                        height: '8px',
                                        borderRadius: '50%',
                                        background: dotColor,
                                        flexShrink: 0
                                      }} />
                                      <span style={{ fontSize: '13px', fontWeight: '500', color: '#374151' }}>
                                        {translateOpportunityTitle(opportunity.title)}
                                      </span>
                                      {hasResources && (
                                        <span style={{ fontSize: '11px', color: '#9ca3af' }}>
                                          ({filteredResources.length})
                                        </span>
                                      )}
                                    </div>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                      <span style={{ fontSize: '12px', color: dotColor, fontWeight: '600', flexShrink: 0 }}>
                                        {opportunity.numericValue ? `${(opportunity.numericValue / 1000).toFixed(1)}s` : opportunity.displayValue || '—'}
                                      </span>
                                      {hasResources && (
                                        <svg
                                          style={{
                                            width: '14px',
                                            height: '14px',
                                            color: '#9ca3af',
                                            transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                                            transition: 'transform 0.2s ease'
                                          }}
                                          fill="none"
                                          stroke="currentColor"
                                          viewBox="0 0 24 24"
                                        >
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                        </svg>
                                      )}
                                    </div>
                                  </div>

                                  {/* 리소스 상세 (펼침 시) */}
                                  {isExpanded && hasResources && (
                                    <div style={{
                                      borderTop: '1px solid #e5e7eb',
                                      background: 'white',
                                      padding: '12px 16px'
                                    }}>
                                      {filteredResources.slice(0, 3).map((item: any, itemIndex: number) => (
                                        <div key={itemIndex} style={{
                                          fontSize: '11px',
                                          padding: '6px 0',
                                          borderBottom: itemIndex < Math.min(filteredResources.length, 3) - 1 ? '1px solid #f3f4f6' : 'none',
                                          display: 'flex',
                                          justifyContent: 'space-between',
                                          alignItems: 'center',
                                          gap: '8px'
                                        }}>
                                          <a
                                            href={item.url || '#'}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            style={{
                                              color: '#4f46e5',
                                              fontFamily: 'monospace',
                                              textDecoration: 'none',
                                              overflow: 'hidden',
                                              textOverflow: 'ellipsis',
                                              whiteSpace: 'nowrap' as const,
                                              flex: 1
                                            }}
                                          >
                                            {truncateUrl(item.url || item.node?.snippet || '', 120)}
                                          </a>
                                          <span style={{ color: '#ef4444', fontWeight: '600', flexShrink: 0 }}>
                                            {item.wastedBytes ? `${(item.wastedBytes / 1024).toFixed(0)} KB` :
                                             item.wastedMs ? `${(item.wastedMs / 1000).toFixed(1)}s` : '-'}
                                          </span>
                                        </div>
                                      ))}
                                      {filteredResources.length > 3 && (
                                        <button
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            openResourcesInNewWindow(
                                              opportunity.details,
                                              oppId,
                                              translateOpportunityTitle(opportunity.title),
                                              opportunity.description
                                            );
                                          }}
                                          style={{
                                            marginTop: '8px',
                                            background: '#eff6ff',
                                            color: '#2563eb',
                                            border: 'none',
                                            padding: '6px 12px',
                                            borderRadius: '4px',
                                            fontSize: '11px',
                                            fontWeight: '600',
                                            cursor: 'pointer',
                                            width: '100%'
                                          }}
                                        >
                                          ... 외 {filteredResources.length - 3}개 새창으로 보기
                                        </button>
                                      )}
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}

                      {/* AI 분석 기반 개선 권장사항 */}
                      {analysisResult.llmAnalysis && (() => {
                        // AI 분석에서 "개선이 필요한 영역" 추출 (단순화된 버전)
                        const extractImprovements = (analysis: string) => {
                          if (!analysis || typeof analysis !== 'string') return [];

                          // "개선이 필요한 영역" 또는 "2." 섹션 찾기 (유연한 패턴)
                          const sectionIndex = analysis.indexOf('개선이 필요한 영역');
                          if (sectionIndex === -1) {
                            // 대안: "### 2." 패턴 찾기
                            const altIndex = analysis.search(/###\s*2\./);
                            if (altIndex === -1) return [];
                          }

                          // 섹션 시작 위치부터 끝까지 추출
                          const startIndex = sectionIndex !== -1 ? sectionIndex : analysis.search(/###\s*2\./);
                          const improvementText = analysis.substring(startIndex);

                          const improvements: { title: string; content: string[]; recommendation?: string; howTo?: string }[] = [];

                          // **제목:** 패턴으로 각 항목 분리 - 줄 시작의 **Title:** 패턴
                          const titlePattern = /(?:^|\n)\s*\*\*([^*\n]+)\*\*\s*:?\s*(?=\n|$)/g;
                          let match;
                          const titleMatches: { title: string; index: number }[] = [];

                          while ((match = titlePattern.exec(improvementText)) !== null) {
                            const title = match[1].trim();
                            // 권장사항, 실행방법, 섹션 헤더 등 제외
                            if (!title.includes('권장사항') && !title.includes('실행방법') && !title.includes('우수한') && !title.includes('개선이 필요한 영역')) {
                              titleMatches.push({ title, index: match.index + match[0].length });
                            }
                          }

                          // 각 제목별로 내용 파싱
                          for (let i = 0; i < titleMatches.length; i++) {
                            const { title, index } = titleMatches[i];
                            const nextIndex = i + 1 < titleMatches.length
                              ? improvementText.indexOf('**', index + 1)
                              : improvementText.length;
                            const contentBlock = improvementText.substring(index, nextIndex > index ? nextIndex : undefined);

                            const lines = contentBlock.split('\n').map(l => l.trim()).filter(l => l.length > 0);
                            const content: string[] = [];
                            let recommendation = '';
                            let howTo = '';

                            for (const line of lines) {
                              if (line.includes('권장사항')) {
                                recommendation = line.replace(/^-?\s*\*?\*?권장사항\*?\*?:?\s*/i, '');
                              } else if (line.includes('실행방법')) {
                                howTo = line.replace(/^-?\s*\*?\*?실행방법\*?\*?:?\s*/i, '');
                              } else if (line.startsWith('-') && !line.includes('**')) {
                                content.push(line.replace(/^-\s*/, ''));
                              }
                            }

                            if (content.length > 0 || recommendation || howTo) {
                              improvements.push({ title, content, recommendation, howTo });
                            }
                          }

                          return improvements;
                        };

                        const analysisText = typeof analysisResult.llmAnalysis === 'string'
                          ? analysisResult.llmAnalysis
                          : String(analysisResult.llmAnalysis);

                        const aiImprovements = extractImprovements(analysisText);
                        console.log('AI Improvements extracted:', aiImprovements.length, aiImprovements);

                        if (aiImprovements.length === 0) return null;

                        return (
                          <div style={{
                            borderTop: '1px solid #e5e7eb',
                            paddingTop: '24px',
                            marginTop: '24px'
                          }}>
                            <div style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '8px',
                              marginBottom: '16px'
                            }}>
                              <span style={{
                                background: 'linear-gradient(135deg, #6d28d9 0%, #4c1d95 100%)',
                                color: 'white',
                                fontSize: '11px',
                                fontWeight: '700',
                                padding: '4px 12px',
                                borderRadius: '9999px',
                                boxShadow: '0 2px 6px rgba(124, 58, 237, 0.3)'
                              }}>
                                AI Insights
                              </span>
                              <span style={{ fontSize: '14px', fontWeight: '600', color: '#5b21b6' }}>
                                AI 분석 기반 추가 권장사항
                              </span>
                              <span style={{ fontSize: '12px', color: '#9ca3af', marginLeft: 'auto' }}>
                                {aiImprovements.length}개 항목
                              </span>
                            </div>

                            {/* 카드 그리드 형태 */}
                            <div style={{
                              display: 'grid',
                              gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
                              gap: '16px'
                            }}>
                              {aiImprovements.map((item, index) => (
                                <div
                                  key={index}
                                  style={{
                                    background: 'white',
                                    borderRadius: '16px',
                                    border: '2px solid rgba(124, 58, 237, 0.15)',
                                    overflow: 'hidden',
                                    transition: 'all 0.25s ease',
                                    display: 'flex',
                                    flexDirection: 'column' as const
                                  }}
                                  onMouseEnter={(e) => {
                                    e.currentTarget.style.boxShadow = '0 8px 24px rgba(124, 58, 237, 0.15)';
                                    e.currentTarget.style.transform = 'translateY(-4px)';
                                    e.currentTarget.style.borderColor = 'rgba(124, 58, 237, 0.3)';
                                  }}
                                  onMouseLeave={(e) => {
                                    e.currentTarget.style.boxShadow = 'none';
                                    e.currentTarget.style.transform = 'translateY(0)';
                                    e.currentTarget.style.borderColor = 'rgba(124, 58, 237, 0.15)';
                                  }}
                                >
                                  {/* 카드 헤더 */}
                                  <div style={{
                                    background: 'linear-gradient(135deg, #ede9fe 0%, #ddd6fe 100%)',
                                    padding: '16px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '12px',
                                    borderBottom: '1px solid rgba(124, 58, 237, 0.1)'
                                  }}>
                                    <div style={{
                                      width: '40px',
                                      height: '40px',
                                      borderRadius: '12px',
                                      background: 'white',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      boxShadow: '0 2px 8px rgba(124, 58, 237, 0.15)'
                                    }}>
                                      <svg style={{ width: '20px', height: '20px', color: '#7c3aed' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                                      </svg>
                                    </div>
                                    <div style={{
                                      fontSize: '14px',
                                      fontWeight: '700',
                                      color: '#5b21b6',
                                      flex: 1
                                    }}>
                                      {item.title}
                                    </div>
                                  </div>

                                  {/* 카드 컨텐츠 */}
                                  <div style={{ padding: '16px', flex: 1 }}>
                                    {item.content.length > 0 && (
                                      <p style={{
                                        fontSize: '13px',
                                        color: '#4b5563',
                                        margin: 0,
                                        marginBottom: item.recommendation ? '12px' : 0,
                                        lineHeight: 1.5
                                      }}>
                                        {item.content[0]}
                                      </p>
                                    )}

                                    {item.recommendation && (
                                      <div style={{
                                        background: '#faf5ff',
                                        borderRadius: '8px',
                                        padding: '10px 12px',
                                        borderLeft: '3px solid #a78bfa'
                                      }}>
                                        <span style={{ fontSize: '11px', fontWeight: '700', color: '#7c3aed' }}>
                                          권장: </span>
                                        <span style={{ fontSize: '12px', color: '#6b7280' }}>
                                          {item.recommendation}
                                        </span>
                                      </div>
                                    )}
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        );
                      })()}
                    </div>
                  </div>
                );
              })()}

              {/* 다운로드 섹션 */}
              <div style={{
                background: '#FFFFFF',
                borderRadius: '12px',
                padding: '32px 48px',
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
                border: '1px solid #e9ecef',
                textAlign: 'center' as const
              }}>
                <div style={{
                  marginBottom: '20px',
                  display: 'flex',
                  flexDirection: 'column' as const,
                  alignItems: 'center'
                }}>
                  <div style={{ fontSize: '16px', fontWeight: '700', color: '#212529', lineHeight: 1.3 }}>
                    분석 리포트 다운로드
                  </div>
                  <div style={{ fontSize: '13px', fontWeight: '400', color: '#868e96', marginTop: '6px' }}>
                    상세 분석 결과를 HTML 형식으로 저장하세요
                  </div>
                </div>
                <button
                  onClick={() => {
                    if (!analysisResult.pageSpeedData) return;
                    const content = generateHtmlReport(
                      analysisResult.pageSpeedData,
                      displayUrl,
                      analysisResult.llmAnalysis,
                      hallOfFameBenchmarks,
                      customScores ? { desktop: customScores.desktop, mobile: customScores.mobile, overall: customScores.overall } : undefined
                    );
                    const blob = new Blob([content], { type: 'text/html;charset=utf-8' });
                    const downloadUrl = URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    a.href = downloadUrl;
                    a.download = `${displayUrl.replace(/[^a-zA-Z0-9가-힣]/g, '_')}_분석리포트.html`;
                    document.body.appendChild(a);
                    a.click();
                    document.body.removeChild(a);
                    URL.revokeObjectURL(downloadUrl);
                  }}
                  style={{
                    padding: '12px 32px',
                    borderRadius: '8px',
                    border: 'none',
                    background: 'linear-gradient(135deg, #00D4FF 0%, #00B4D8 100%)',
                    color: 'white',
                    fontSize: '14px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    boxShadow: '0 4px 12px rgba(0, 212, 255, 0.3)',
                    transition: 'all 0.2s ease'
                  }}
                >
                  HTML 리포트 다운로드
                </button>
              </div>

          </div>
          )}
          {/* 메인 분석 대시보드 끝 */}
        </section>
      )}
        </>
      )}
      </div>{/* 메인 컨텐츠 영역 끝 */}

      {/* 전역 스타일 */}
      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>

      {/* ========== 사이트 그룹 관리 모달 ========== */}
      {showGroupManageModal && (
        <>
          {/* 모달 배경 */}
          <div
            onClick={() => setShowGroupManageModal(false)}
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'rgba(0, 0, 0, 0.5)',
              zIndex: 10000,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          />

          {/* 모달 컨텐츠 */}
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              position: 'fixed',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              background: designSystem.colors.bgSecondary,
              borderRadius: '1.5rem',
              boxShadow: designSystem.shadows['2xl'],
              border: `2px solid ${designSystem.colors.primary}`,
              width: '90%',
              maxWidth: '600px',
              zIndex: 10001,
              padding: '2rem'
            }}
          >
            <h2 style={{
              ...designSystem.typography.h2,
              color: designSystem.colors.primary,
              marginBottom: '1.5rem',
              fontSize: '1.5rem'
            }}>
              {editingGroup ? '사이트 그룹 수정' : '새 사이트 그룹 추가'}
            </h2>

            {/* 사이트명 입력 */}
            <div style={{ marginBottom: '1.5rem' }}>
              <label style={{
                display: 'block',
                marginBottom: '0.5rem',
                fontWeight: '600',
                color: designSystem.colors.textPrimary
              }}>
                사이트명 *
              </label>
              <input
                type="text"
                value={groupFormData.site_name}
                onChange={(e) => setGroupFormData({ ...groupFormData, site_name: e.target.value })}
                placeholder="예: 네이버 서비스"
                style={{
                  width: '100%',
                  padding: '0.75rem 1rem',
                  borderRadius: '0.75rem',
                  border: `2px solid ${designSystem.colors.border}`,
                  fontSize: '1rem',
                  outline: 'none',
                  transition: 'all 0.2s ease'
                }}
                onFocus={(e) => {
                  e.currentTarget.style.borderColor = designSystem.colors.primary;
                  e.currentTarget.style.boxShadow = `0 0 0 3px ${designSystem.colors.primary}15`;
                }}
                onBlur={(e) => {
                  e.currentTarget.style.borderColor = designSystem.colors.border;
                  e.currentTarget.style.boxShadow = 'none';
                }}
              />
            </div>

            {/* 설명 입력 */}
            <div style={{ marginBottom: '2rem' }}>
              <label style={{
                display: 'block',
                marginBottom: '0.5rem',
                fontWeight: '600',
                color: designSystem.colors.textPrimary
              }}>
                설명 (선택)
              </label>
              <textarea
                value={groupFormData.description}
                onChange={(e) => setGroupFormData({ ...groupFormData, description: e.target.value })}
                placeholder="이 사이트 그룹에 대한 설명을 입력하세요..."
                rows={3}
                style={{
                  width: '100%',
                  padding: '0.75rem 1rem',
                  borderRadius: '0.75rem',
                  border: `2px solid ${designSystem.colors.border}`,
                  fontSize: '1rem',
                  outline: 'none',
                  resize: 'vertical',
                  transition: 'all 0.2s ease'
                }}
                onFocus={(e) => {
                  e.currentTarget.style.borderColor = designSystem.colors.primary;
                  e.currentTarget.style.boxShadow = `0 0 0 3px ${designSystem.colors.primary}15`;
                }}
                onBlur={(e) => {
                  e.currentTarget.style.borderColor = designSystem.colors.border;
                  e.currentTarget.style.boxShadow = 'none';
                }}
              />
            </div>

            {/* 버튼들 */}
            <div style={{
              display: 'flex',
              gap: '1rem',
              justifyContent: 'flex-end'
            }}>
              <button
                onClick={() => setShowGroupManageModal(false)}
                style={{
                  padding: '0.75rem 1.5rem',
                  background: designSystem.colors.textMuted,
                  color: 'white',
                  border: 'none',
                  borderRadius: '0.75rem',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.opacity = '0.8';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.opacity = '1';
                }}
              >
                취소
              </button>
              <button
                onClick={editingGroup ? updateSiteGroup : createSiteGroup}
                disabled={!groupFormData.site_name.trim()}
                style={{
                  padding: '0.75rem 1.5rem',
                  background: groupFormData.site_name.trim() ? designSystem.colors.primary : designSystem.colors.textMuted,
                  color: 'white',
                  border: 'none',
                  borderRadius: '0.75rem',
                  fontWeight: '600',
                  cursor: groupFormData.site_name.trim() ? 'pointer' : 'not-allowed',
                  transition: 'all 0.2s ease'
                }}
                onMouseEnter={(e) => {
                  if (groupFormData.site_name.trim()) {
                    e.currentTarget.style.background = designSystem.colors.primaryDark;
                  }
                }}
                onMouseLeave={(e) => {
                  if (groupFormData.site_name.trim()) {
                    e.currentTarget.style.background = designSystem.colors.primary;
                  }
                }}
              >
                {editingGroup ? '수정' : '추가'}
              </button>
            </div>
          </div>
        </>
      )}

      {/* ========== URL 관리 모달 ========== */}
      {showUrlManageModal && (
        <>
          {/* 모달 배경 */}
          <div
            onClick={() => {
              setShowUrlManageModal(false);
              setEditingUrl(null);
            }}
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'rgba(0, 0, 0, 0.5)',
              zIndex: 10000,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          />

          {/* 모달 컨텐츠 */}
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              position: 'fixed',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              background: designSystem.colors.bgSecondary,
              borderRadius: '1.5rem',
              boxShadow: designSystem.shadows['2xl'],
              border: `2px solid ${designSystem.colors.primary}`,
              width: '90%',
              maxWidth: '800px',
              maxHeight: '80vh',
              zIndex: 10001,
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden'
            }}
          >
            {/* 헤더 */}
            <div style={{
              padding: '2rem',
              borderBottom: `2px solid ${designSystem.colors.border}`
            }}>
              <h2 style={{
                ...designSystem.typography.h2,
                color: designSystem.colors.primary,
                fontSize: '1.5rem',
                marginBottom: '1rem'
              }}>
                URL 관리
              </h2>

              {/* URL 추가/수정 폼 */}
              <div style={{
                background: designSystem.colors.primaryLight,
                padding: '1.5rem',
                borderRadius: '0.75rem',
                marginTop: '1rem'
              }}>
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: '2fr 1fr auto',
                  gap: '1rem',
                  alignItems: 'end'
                }}>
                  <div>
                    <label style={{
                      display: 'block',
                      marginBottom: '0.5rem',
                      fontWeight: '600',
                      color: designSystem.colors.textPrimary,
                      fontSize: '0.9rem'
                    }}>
                      URL *
                    </label>
                    <input
                      type="text"
                      value={urlFormData.url}
                      onChange={(e) => setUrlFormData({ ...urlFormData, url: e.target.value })}
                      placeholder="https://example.com"
                      style={{
                        width: '100%',
                        padding: '0.75rem 1rem',
                        borderRadius: '0.5rem',
                        border: `2px solid ${designSystem.colors.border}`,
                        fontSize: '0.9rem',
                        outline: 'none'
                      }}
                    />
                  </div>
                  <div>
                    <label style={{
                      display: 'block',
                      marginBottom: '0.5rem',
                      fontWeight: '600',
                      color: designSystem.colors.textPrimary,
                      fontSize: '0.9rem'
                    }}>
                      라벨 (선택)
                    </label>
                    <input
                      type="text"
                      value={urlFormData.label}
                      onChange={(e) => setUrlFormData({ ...urlFormData, label: e.target.value })}
                      placeholder="메인 페이지"
                      style={{
                        width: '100%',
                        padding: '0.75rem 1rem',
                        borderRadius: '0.5rem',
                        border: `2px solid ${designSystem.colors.border}`,
                        fontSize: '0.9rem',
                        outline: 'none'
                      }}
                    />
                  </div>
                  <button
                    onClick={editingUrl ? updateUrl : addUrl}
                    disabled={!urlFormData.url.trim()}
                    style={{
                      padding: '0.75rem 1.5rem',
                      background: urlFormData.url.trim() ? designSystem.colors.primary : designSystem.colors.textMuted,
                      color: 'white',
                      border: 'none',
                      borderRadius: '0.5rem',
                      fontWeight: '600',
                      cursor: urlFormData.url.trim() ? 'pointer' : 'not-allowed',
                      whiteSpace: 'nowrap'
                    }}
                  >
                    {editingUrl ? '수정' : '추가'}
                  </button>
                </div>
                {editingUrl && (
                  <button
                    onClick={() => {
                      setEditingUrl(null);
                      setUrlFormData({ url: '', label: '', display_order: 0 });
                    }}
                    style={{
                      marginTop: '0.5rem',
                      padding: '0.5rem 1rem',
                      background: designSystem.colors.textMuted,
                      color: 'white',
                      border: 'none',
                      borderRadius: '0.5rem',
                      fontSize: '0.85rem',
                      cursor: 'pointer'
                    }}
                  >
                    취소
                  </button>
                )}
              </div>
            </div>

            {/* URL 목록 */}
            <div style={{
              flex: 1,
              overflowY: 'auto',
              padding: '1.5rem 2rem'
            }}>
              {groupUrls.length === 0 ? (
                <div style={{
                  textAlign: 'center',
                  padding: '2rem',
                  color: designSystem.colors.textMuted
                }}>
                  등록된 URL이 없습니다.
                </div>
              ) : (
                groupUrls.map((url: any, index: number) => (
                  <div
                    key={url.id}
                    style={{
                      padding: '1rem',
                      background: editingUrl?.id === url.id ? designSystem.colors.primaryLight : designSystem.colors.bgPrimary,
                      borderRadius: '0.75rem',
                      marginBottom: '0.75rem',
                      border: `2px solid ${editingUrl?.id === url.id ? designSystem.colors.primary : designSystem.colors.border}`,
                      transition: 'all 0.2s ease'
                    }}
                  >
                    <div style={{
                      display: 'flex',
                      alignItems: 'start',
                      gap: '1rem'
                    }}>
                      <div style={{ flex: 1 }}>
                        <div style={{
                          fontSize: '0.9rem',
                          fontWeight: '600',
                          color: designSystem.colors.textPrimary,
                          marginBottom: '0.25rem',
                          wordBreak: 'break-all'
                        }}>
                          {url.label || url.url}
                        </div>
                        {url.label && (
                          <div style={{
                            fontSize: '0.8rem',
                            color: designSystem.colors.textSecondary,
                            wordBreak: 'break-all'
                          }}>
                            {url.url}
                          </div>
                        )}
                      </div>
                      <div style={{
                        display: 'flex',
                        gap: '0.5rem'
                      }}>
                        <button
                          onClick={() => openUrlFormModal(url)}
                          style={{
                            padding: '0.5rem 0.75rem',
                            background: designSystem.colors.warning,
                            color: 'white',
                            border: 'none',
                            borderRadius: '0.5rem',
                            fontSize: '0.75rem',
                            fontWeight: '600',
                            cursor: 'pointer',
                            whiteSpace: 'nowrap'
                          }}
                        >
                          편집
                        </button>
                        <button
                          onClick={() => deleteUrl(url.id)}
                          style={{
                            padding: '0.5rem 0.75rem',
                            background: designSystem.colors.danger,
                            color: 'white',
                            border: 'none',
                            borderRadius: '0.5rem',
                            fontSize: '0.75rem',
                            fontWeight: '600',
                            cursor: 'pointer',
                            whiteSpace: 'nowrap'
                          }}
                        >
                          삭제
                        </button>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>

            {/* 푸터 */}
            <div style={{
              padding: '1.5rem 2rem',
              borderTop: `2px solid ${designSystem.colors.border}`,
              textAlign: 'right'
            }}>
              <button
                onClick={() => {
                  setShowUrlManageModal(false);
                  setEditingUrl(null);
                }}
                style={{
                  padding: '0.75rem 1.5rem',
                  background: designSystem.colors.primary,
                  color: 'white',
                  border: 'none',
                  borderRadius: '0.75rem',
                  fontWeight: '600',
                  cursor: 'pointer'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = designSystem.colors.primaryDark;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = designSystem.colors.primary;
                }}
              >
                완료
              </button>
            </div>
          </div>
        </>
      )}

      {/* 페이지 관리 모달 - Site Pages 시스템 */}
      {showSitePagesModal && (
        <>
          <div
            style={{
              position: 'fixed',
              inset: 0,
              background: 'rgba(0, 0, 0, 0.7)',
              zIndex: 999,
              backdropFilter: 'blur(4px)'
            }}
          />
          <div style={{
            position: 'fixed',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: '90%',
            maxWidth: '800px',
            maxHeight: '85vh',
            background: designSystem.colors.bgSecondary,
            borderRadius: '1rem',
            boxShadow: designSystem.shadows.xl,
            zIndex: 1000,
            display: 'flex',
            flexDirection: 'column',
            border: `2px solid ${designSystem.colors.border}`
          }}>
            {/* 헤더 */}
            <div style={{
              padding: '1.5rem 2rem',
              borderBottom: `2px solid ${designSystem.colors.border}`,
              background: `linear-gradient(135deg, ${designSystem.colors.primary} 0%, ${designSystem.colors.secondary} 100%)`
            }}>
              <h2 style={{
                ...designSystem.typography.h2,
                color: 'white',
                margin: 0,
                fontSize: '1.5rem'
              }}>
                페이지 관리 - {managingSiteName}
              </h2>
              <p style={{
                color: 'rgba(255, 255, 255, 0.9)',
                fontSize: '0.9rem',
                margin: '0.5rem 0 0 0'
              }}>
                {sitePagesData ? '기존 페이지 정보를 수정합니다' : '새 페이지 정보를 등록합니다'}
              </p>
            </div>

            {/* 폼 영역 */}
            <div style={{
              flex: 1,
              overflowY: 'auto',
              padding: '1.5rem 2rem'
            }}>
              {/* 사이트명 (읽기 전용) */}
              <div style={{ marginBottom: '1.5rem' }}>
                <label style={{
                  display: 'block',
                  fontWeight: '600',
                  color: designSystem.colors.textPrimary,
                  marginBottom: '0.5rem',
                  fontSize: '0.9rem'
                }}>
                  사이트명
                </label>
                <input
                  type="text"
                  value={sitePagesForm.site_name}
                  readOnly
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    border: `2px solid ${designSystem.colors.border}`,
                    borderRadius: '0.5rem',
                    fontSize: '0.9rem',
                    background: '#F7FAFC',
                    color: designSystem.colors.textSecondary,
                    cursor: 'not-allowed'
                  }}
                />
              </div>

              {/* 메인 페이지 */}
              <div style={{ marginBottom: '1.5rem' }}>
                <label style={{
                  display: 'block',
                  fontWeight: '600',
                  color: designSystem.colors.textPrimary,
                  marginBottom: '0.5rem',
                  fontSize: '0.9rem'
                }}>
                  메인 페이지 URL
                </label>
                <input
                  type="text"
                  value={sitePagesForm.main_page_url || ''}
                  onChange={(e) => setSitePagesForm({ ...sitePagesForm, main_page_url: e.target.value })}
                  placeholder="https://example.com"
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    border: `2px solid ${designSystem.colors.border}`,
                    borderRadius: '0.5rem',
                    fontSize: '0.9rem',
                    transition: 'border-color 0.2s'
                  }}
                  onFocus={(e) => e.currentTarget.style.borderColor = designSystem.colors.primary}
                  onBlur={(e) => e.currentTarget.style.borderColor = designSystem.colors.border}
                />
              </div>

              {/* 상품 카테고리 */}
              <div style={{ marginBottom: '1.5rem' }}>
                <label style={{
                  display: 'block',
                  fontWeight: '600',
                  color: designSystem.colors.textPrimary,
                  marginBottom: '0.5rem',
                  fontSize: '0.9rem'
                }}>
                  상품 카테고리 페이지 URL
                </label>
                <input
                  type="text"
                  value={sitePagesForm.category_page_url || ''}
                  onChange={(e) => setSitePagesForm({ ...sitePagesForm, category_page_url: e.target.value })}
                  placeholder="https://example.com/category"
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    border: `2px solid ${designSystem.colors.border}`,
                    borderRadius: '0.5rem',
                    fontSize: '0.9rem',
                    transition: 'border-color 0.2s'
                  }}
                  onFocus={(e) => e.currentTarget.style.borderColor = designSystem.colors.primary}
                  onBlur={(e) => e.currentTarget.style.borderColor = designSystem.colors.border}
                />
              </div>

              {/* 상품 상세 */}
              <div style={{ marginBottom: '1.5rem' }}>
                <label style={{
                  display: 'block',
                  fontWeight: '600',
                  color: designSystem.colors.textPrimary,
                  marginBottom: '0.5rem',
                  fontSize: '0.9rem'
                }}>
                  상품 상세 페이지 URL
                </label>
                <input
                  type="text"
                  value={sitePagesForm.product_detail_url || ''}
                  onChange={(e) => setSitePagesForm({ ...sitePagesForm, product_detail_url: e.target.value })}
                  placeholder="https://example.com/product/123"
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    border: `2px solid ${designSystem.colors.border}`,
                    borderRadius: '0.5rem',
                    fontSize: '0.9rem',
                    transition: 'border-color 0.2s'
                  }}
                  onFocus={(e) => e.currentTarget.style.borderColor = designSystem.colors.primary}
                  onBlur={(e) => e.currentTarget.style.borderColor = designSystem.colors.border}
                />
              </div>

              {/* 로그인/회원가입 */}
              <div style={{ marginBottom: '1.5rem' }}>
                <label style={{
                  display: 'block',
                  fontWeight: '600',
                  color: designSystem.colors.textPrimary,
                  marginBottom: '0.5rem',
                  fontSize: '0.9rem'
                }}>
                  로그인/회원가입 페이지 URL
                </label>
                <input
                  type="text"
                  value={sitePagesForm.login_signup_url || ''}
                  onChange={(e) => setSitePagesForm({ ...sitePagesForm, login_signup_url: e.target.value })}
                  placeholder="https://example.com/login"
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    border: `2px solid ${designSystem.colors.border}`,
                    borderRadius: '0.5rem',
                    fontSize: '0.9rem',
                    transition: 'border-color 0.2s'
                  }}
                  onFocus={(e) => e.currentTarget.style.borderColor = designSystem.colors.primary}
                  onBlur={(e) => e.currentTarget.style.borderColor = designSystem.colors.border}
                />
              </div>

              {/* 장바구니 */}
              <div style={{ marginBottom: '1.5rem' }}>
                <label style={{
                  display: 'block',
                  fontWeight: '600',
                  color: designSystem.colors.textPrimary,
                  marginBottom: '0.5rem',
                  fontSize: '0.9rem'
                }}>
                  장바구니 페이지 URL
                </label>
                <input
                  type="text"
                  value={sitePagesForm.cart_url || ''}
                  onChange={(e) => setSitePagesForm({ ...sitePagesForm, cart_url: e.target.value })}
                  placeholder="https://example.com/cart"
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    border: `2px solid ${designSystem.colors.border}`,
                    borderRadius: '0.5rem',
                    fontSize: '0.9rem',
                    transition: 'border-color 0.2s'
                  }}
                  onFocus={(e) => e.currentTarget.style.borderColor = designSystem.colors.primary}
                  onBlur={(e) => e.currentTarget.style.borderColor = designSystem.colors.border}
                />
              </div>

              {/* 기타 페이지 1-5 */}
              <div style={{
                marginTop: '2rem',
                padding: '1rem',
                background: designSystem.colors.bgPrimary,
                borderRadius: '0.75rem',
                border: `1px dashed ${designSystem.colors.border}`
              }}>
                <h3 style={{
                  ...designSystem.typography.h3,
                  fontSize: '1rem',
                  color: designSystem.colors.textSecondary,
                  marginTop: 0,
                  marginBottom: '1rem'
                }}>
                  기타 페이지 (선택사항)
                </h3>

                {[1, 2, 3, 4, 5].map((num) => (
                  <div key={num} style={{ marginBottom: num < 5 ? '1rem' : 0 }}>
                    <label style={{
                      display: 'block',
                      fontWeight: '600',
                      color: designSystem.colors.textSecondary,
                      marginBottom: '0.5rem',
                      fontSize: '0.85rem'
                    }}>
                      기타 페이지 {num}
                    </label>
                    <input
                      type="text"
                      value={sitePagesForm[`other_page_${num}_url` as keyof typeof sitePagesForm] || ''}
                      onChange={(e) => setSitePagesForm({
                        ...sitePagesForm,
                        [`other_page_${num}_url`]: e.target.value
                      })}
                      placeholder={`https://example.com/other${num}`}
                      style={{
                        width: '100%',
                        padding: '0.75rem',
                        border: `1px solid ${designSystem.colors.border}`,
                        borderRadius: '0.5rem',
                        fontSize: '0.85rem',
                        background: designSystem.colors.bgPrimary,
                        transition: 'border-color 0.2s'
                      }}
                      onFocus={(e) => e.currentTarget.style.borderColor = designSystem.colors.primary}
                      onBlur={(e) => e.currentTarget.style.borderColor = designSystem.colors.border}
                    />
                  </div>
                ))}
              </div>
            </div>

            {/* 푸터 */}
            <div style={{
              padding: '1.5rem 2rem',
              borderTop: `2px solid ${designSystem.colors.border}`,
              display: 'flex',
              gap: '1rem',
              justifyContent: 'flex-end'
            }}>
              <button
                onClick={() => {
                  setShowSitePagesModal(false);
                  setSitePagesData(null);
                }}
                style={{
                  padding: '0.75rem 1.5rem',
                  background: '#F7FAFC',
                  color: designSystem.colors.textSecondary,
                  border: `2px solid ${designSystem.colors.border}`,
                  borderRadius: '0.75rem',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = designSystem.colors.border;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = '#F7FAFC';
                }}
              >
                취소
              </button>
              <button
                onClick={saveSitePages}
                style={{
                  padding: '0.75rem 1.5rem',
                  background: designSystem.colors.primary,
                  color: 'white',
                  border: 'none',
                  borderRadius: '0.75rem',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = designSystem.colors.primaryDark;
                  e.currentTarget.style.transform = 'translateY(-1px)';
                  e.currentTarget.style.boxShadow = designSystem.shadows.md;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = designSystem.colors.primary;
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = 'none';
                }}
              >
                저장
              </button>
            </div>
          </div>
        </>
      )}

      {/* 명예의전당 모달 - AppV2 전용 새 디자인 */}
      <HallOfFameModalAppV2
        showHallOfFame={showHallOfFame}
        setShowHallOfFame={setShowHallOfFame}
        currentSiteUrl={displayUrl}
        currentSiteData={analysisResult.pageSpeedData}
        performanceScores={performanceScores}
      />

      {/* 저장된 분석 모달 - V2 디자인 시스템 */}
      {user && (
        <SavedAnalysesModalV2
          isOpen={showSavedAnalysesModal}
          onClose={() => setShowSavedAnalysesModal(false)}
          onLoadAnalysis={handleLoadAnalysis}
        />
      )}
    </div>
  );
};

export default AppV2;